package org.multiplatform.kotfolio.pages.kmm

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.AppearanceAwareImage
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.components.OurValueItem
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Constants
import org.multiplatform.kotfolio.util.Resources
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul
import org.multiplatform.kotfolio.util.Kotlin

@Page("/kmm/plugin")
@Composable
fun KMPPluginPage() {
    BlogLayout(
        PageProperties(
            "KMP Plugin",
            "Expect and actual declarations allow you to access platform-specific APls from Kotlin Multiplatform modules. You can provide platform-agnostic APls in the common code."
        ),
        BlogProperties("KMP", "Expect - Actual")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin provides a language mechanism called expected/actual to access platform-specific APIs while developing common logic, this structure makes multiplatform development easier and cleaner. It can be applied to functions, properties, objects, and classes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "With expected and actual declarations, we can successfully separate the platform-specific implementations from the application-shared code. This allows seamless integrations between the shared and platform modules in the KMP project.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "'expect'/'actual' classes (including interfaces, objects, annotations, enums, and 'actual' typealiases) are in Beta.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "expected — It is a declaration specifying a common functionality or API that should be implemented on all target platforms but does not provide an implementation. It signals to the compiler that the platform-specific module will provide the actual implementation. The compiler will try to match each actual declaration it finds with a corresponding expected declaration. In a KMP project, the common code will be declared in the shared module commonMain. How to Use: You declare a function, interface, class, or property with the expect keyword in your shared code, but you don’t provide an implementation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "expect declarations: An expect declaration specifies a common API or functionality that is expected to be available on all target platforms but does not provide the implementation. It acts as a placeholder, signaling to the compiler that the actual implementation will be provided separately for each platform. Expect declarations can be used for classes, functions, interfaces, and annotations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "actual — It will have the same constructor and package structure as the expected declaration, but it is located in each platform module of the project, like androidMain or iosMain. It contains an implementation that conforms to the expected declaration. On each specific platform (Android & iOS), you provide a concrete implementation of what was declared with expect.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "actual declarations: Corresponding to each expect declaration, an actual declaration provides the platform-specific implementation. There must be one actual declaration for each platform being targeted in the project, ensuring that the common code can operate seamlessly across all platforms. The actual implementation must conform to the API defined by the expect declaration, but the underlying implementation can leverage platform-specific libraries or APIs.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This mechanism can be applied when we need to open browsers, cameras, and platform-specific APIs.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "By applying the expected/actual mechanism, the developer can abstract away platform-specific code and keep the shared codebase platform-agnostic.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "shared module",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Android and iOS live in the same repository, while the core business logic is shared using Kotlin. Common logics are written once and reused across both platforms.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For the iOS side, the shared module is compiled into an XCFramework, which we integrate into Xcode and call directly from Swift. This allows both platforms to keep native Ul and performance while sharing important logic.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "useContents {}",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This function allows us to access the contents of a Swift struct in Kotlin.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "ComposeUIViewController",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ComposeUIViewController component is directly interoperable with UIKit, so we can use it like any other native UIViewController.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To use Compose Multiplatform inside a SwiftUI application, create a Kotlin function MainViewController() that returns UIViewController from UIKit and contains Compose Multiplatform code:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    fun MainViewController(): UIViewController = ComposeUIViewController {
                        Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                            Text("This is Compose code", fontSize = 20.sp)
                        }
                    }
                """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ComposeUIViewController() is a Compose Multiplatform library function that accepts a composable function as the content argument. The function passed in this way can call other composable functions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "UIKit",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ComposeUIViewController component is directly interoperable with UIKit, so we can use it like any other native UIViewController.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "updateUIViewController",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "updateUIViewController, the Multiplatform Compose API lacks³ a mechanism to communicate these changes to the Composable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To update the UI dynamically, use a State or MutableState in the Compose function. Pass updated data from Swift to the shared module, or use a Kotlin Flow to emit changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/apolacsek/kmp-compose-mapkit
            SpanText(
                text = "UIKitView",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "By using UIKitView, you can embed complex platform-specific widgets like maps, web views, media players, and camera feeds within your shared user interface. In the other direction, via ComposeUIViewController, you can embed Compose Multiplatform screens in SwiftUI applications, helping you gradually adopt Compose Multiplatform in iOS applications.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "UIKitView that enables interoperability between UIKit and Compose. It works the same way as AndroidView when you want to transform an Android view (legacy view system) into a Compose component.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The UIKitView composable requires two essential parameters: a factory lambda that creates the native view, and an update lambda that handles view updates.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The factory block runs once when the view is created, making it ideal for initial setup like setting the text color.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The update block is called whenever the composable is recomposed, allowing us to update the view’s properties based on new data — in this case, setting the label’s text.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This pattern — creating a view in factory and updating it in update — is fundamental to working with UIKitView",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    UIKitView(modifier = Modifier.sizeIn(maxHeight = 56.dp), factory = { })
                    """.trimIndent()
            )

            SpanText(
                text = "NativeViews for Android XML",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ComposeView, a Jetpack Compose component that allows us to integrate Composable components within an Android View in XML format.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "By default, ComposeView can stay active in the background after onDestroyView(), leading to memory leaks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                        //Manually set 
                        ViewCompositionStrategy.DisposeOnViewTreeLifecycleDestroyed
                        
                        //Now
                        //collectAsStateWithLifecycle() to pause data flows when the UI isn't visible.
                        collectAsStateWithLifecycle()
                    """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "setViewCompositionStrategy, which is fundamental for interoperability between Compose and XML, as they handle different rendering strategies.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewCompositionStrategy determines when to dispose of the composition. The default value, ViewCompositionStrategy.Default, disposes of the composition when the ComposeView is detached from the window, except when it's part of a container like RecyclerView. This default behavior is ideal for applications that use only Compose.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    <LinearLayout xmlns:android="http://schemas.android.com/apk/res/android"
                        android:orientation="vertical"
                        android:layout_width="match_parent"
                        android:layout_height="match_parent">

                      <TextView
                          android:id="@+id/text"
                          android:layout_width="wrap_content"
                          android:layout_height="wrap_content" />

                      <androidx.compose.ui.platform.ComposeView
                          android:id="@+id/compose_view"
                          android:layout_width="match_parent"
                          android:layout_height="match_parent" />
                    </LinearLayout>

                    class ExampleFragmentXml : Fragment() {
                        override fun onCreateView(
                            inflater: LayoutInflater,
                            container: ViewGroup?,
                            savedInstanceState: Bundle?
                        ): View {
                            val view = inflater.inflate(R.layout.fragment_example, container, false)
                            val composeView = view.findViewById<ComposeView>(R.id.compose_view)
                            composeView.apply {
                                // Dispose of the Composition when the view's LifecycleOwner is destroyed
                                setViewCompositionStrategy(ViewCompositionStrategy.DisposeOnViewTreeLifecycleDestroyed)
                                setContent {
                                    // In Compose world
                                    MyMultiplatformComposable()
                                }
                            }
                            return view
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "low-level shared module",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "shared and composeApp modules both compiled into separate frameworks (.xcframework) for iOS were operating in different memory areas.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "While I couldn’t find explicit documentation on this behavior, when I passed the delegate object and logged its hash code, I observed different values at different layers of our application.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The delegate instance we were initializing in shared was not the same instance our composeApp module was seeing. So the Log interface was moved from the shared module to the composeApp module. The iosApp now provides its delegate implementation directly to composeApp. While this means our UI, ViewModel logic is now more coupled to the delegation logic. So be mindful of singleton objects when passing delegates across multiple KMP framework boundaries on iOS.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    // A function in our `shared` module to receive the delegate
                    fun initCrashlyticsDelegate(delegate: FirebaseCrashlyticsDelegate) {
                        crashlyticsDelegate = delegate
                        // When called from iosApp, this prints one hash code
                        NSLog("Delegate hash in 'shared': ${delegate.hashCode()}") 
                    }

                    // Later, when accessing this delegate from a ViewModel in `composeApp`...
                    // This would print a DIFFERENT hash code, implying it's not the same instance.
                    NSLog("Delegate hash in 'composeApp': ${crashlyticsDelegate.hashCode()}")
                    """.trimIndent()
            )

            SpanText(
                text = "CValue",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "CValue is a generic wrapper in Kotlin Multiplatform, designed to handle C structures passed by value in native code. Although it provides a convenient way to work with platform-specific types such as CGSize, CGRect, and CGPoint, directly accessing these values can be limiting. For example, when retrieving a CGRect from a CValue, you may end up with an unexpected Int value instead of the structure you need for layout and rendering.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What Is expect/actual in KMP?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin uses the expect/actual keyword pair to handle platform-specific implementations. In principal it all seems like declaring abstraction using expect keyword and later on have the implementation each platform using actual key word.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SimpleGrid(
                numColumns = numColumns(
                    base = 1,
                    md = 2
                ),
                modifier = Modifier.margin(top = 20.px)
            ) {
                OurValueItem(
                    title = "expected - (Contract - Declarations - What you need)",
                    description = "In the common module, you use expect to define a function, property, or class without writing its implementation. expect is what KMM expects about it (abstraction) and actual is what you actually do for it (implementation). shared module, define functions or classes that you expect to have platform-specific implementations. Every expect has a corresponding actual for each target platform."
                )
                OurValueItem(
                    title = "actual - (Fulfillment - Implementations)",
                    description = "Actual is the response to your expect announcement. On each platform, you use actual to say, Here’s how we’re going to handle what you were expecting.  Within the androidMain and iosMain modules, provide concrete implementations that utilize the native capabilities of Android (Java/Kotlin) and iOS (Objective-C/Swift)."
                )
                OurValueItem(
                    title = "Platform-Specific Arguments",
                    description = "With expect classes, you can provide platform-specific arguments to the member functions, allowing for more flexibility in handling platform differences."
                )
                OurValueItem(
                    title = "Modularity",
                    description = "expect classes promote a more modular architecture, keeping related functionality together rather than scattering individual expect functions across files."
                )
                OurValueItem(
                    title = "common source set",
                    description = "The common source set contains code shared across all platforms, while platform-specific source sets contain code unique to a single platform."
                )
                OurValueItem(
                    title = "Android context",
                    description = "LocalContext.current.applicationContext\nandroidContext()"
                )
            }

            KotlinCode(
                code = """
                    kotlin {
                        compilerOptions {
                            // Common compiler options applied to all Kotlin source sets
                            freeCompilerArgs.add("-Xexpect-actual-classes")
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "How does Kotlin Multiplatform actually work under the hood?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "KMP compiles the shared code into different binaries for each platform.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For Android: it compiles to JVM bytecode.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For iOS: it compiles to native code (via Kotlin/Native).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "So, each platform understands the shared code natively.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Different approaches to using expected and actual declarations",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Expect/Actual functions",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Expect/Actual interfaces: If the factory function becomes too large, consider using a common Platform interface and implement it differently on different platforms.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Expect/Actual interface extend",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Expect/Actual interface flow",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Expect/Actual properties",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Expect/Actual objects and classes",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Expect/Actual using type alias",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                //https://github.com/wshtaits/GetLike.git ()
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Expect/Actual using internal fun",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Expect/Actual val",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Expect/Actual open class",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Expect/Actual koin annotations",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                //https://doveletter.skydoves.me/preview/articles/kotlin-shared-internals-keep#conclusion
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "shared internal Expect/Actual: Visible within the same module AND to explicitly designated dependent modules.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Expect/Actual compose (Platform Specific UI - Google/Apple Login Button, ImagePicker, Refresh View)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }


            KotlinCode(
                code = """
                    //UnsupportedOperationException
                    
                    actual suspend fun loginWithGoogle(): Result<String> =
                    Result.failure(
                        exception = UnsupportedOperationException("Google login is not supported on iOS."),
                    )
                    
                    actual suspend fun loginWithApple(): Result<String> =
                        Result.failure(
                            exception = UnsupportedOperationException("Apple login is not supported on Android."),
                        )
                    """.trimIndent()
            )

            //https://github.com/Team-Bigong/oguri-kotlin-multiplatform.git
            KotlinCode(
                code = """
                    //Set platform specific icon for google login & Apple login
                    
                    //commonMain
                    @Composable
                    fun LoginActionButton(
                        iconResource: DrawableResource,
                        iconSize: Dp = 18.dp,
                        titleText: String,
                        backgroundColor: Color,
                        contentColor: Color,
                        iconTintColor: Color? = null,
                        borderColor: Color = backgroundColor,
                        onClick: () -> Unit,
                    ) {
                        Row(
                            modifier =
                                Modifier
                                    .fillMaxWidth()
                                    .border(
                                        width = 1.dp,
                                        color = borderColor,
                                        shape = RoundedCornerShape(size = 10.dp),
                                    ).background(color = backgroundColor, shape = RoundedCornerShape(size = 10.dp))
                                    .noRippleClickable(onClick = onClick)
                                    .padding(horizontal = 30.dp, vertical = 16.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.Center,
                        ) {
                            Image(
                                painter = painterResource(resource = iconResource),
                                contentDescription = null,
                                colorFilter = iconTintColor?.let { tintColor -> ColorFilter.tint(tintColor) },
                                modifier = Modifier.size(size = iconSize),
                            )
                            Spacer(modifier = Modifier.size(size = 12.dp))
                            Text(
                                text = titleText,
                                style = OguriTheme.typography.cardSubtitle,
                                color = contentColor,
                                textAlign = TextAlign.Center,
                                modifier = Modifier.weight(1f),
                            )
                            Spacer(modifier = Modifier.size(size = 18.dp))
                        }
                    }

                    @Composable
                    expect fun LoginScreen(
                        isLoading: Boolean,
                        onGoogleLoginClick: () -> Unit,
                        onKakaoLoginClick: () -> Unit,
                        onAppleLoginClick: () -> Unit,
                        onGuestBrowseClick: () -> Unit,
                    )
                    
                    //iosMain
                    @Composable
                    actual fun LoginScreen(
                        isLoading: Boolean,
                        onGoogleLoginClick: () -> Unit,
                        onKakaoLoginClick: () -> Unit,
                        onAppleLoginClick: () -> Unit,
                        onGuestBrowseClick: () -> Unit,
                    ) {
                        val logoResource =
                            if (isSystemInDarkTheme()) {
                                Res.drawable.ic_app_text_dark
                            } else {
                                Res.drawable.ic_app_text
                            }

                        Box(
                            modifier =
                                Modifier
                                    .fillMaxSize()
                                    .background(color = Neutral5),
                        ) {
                            Column(
                                modifier =
                                    Modifier
                                        .fillMaxSize()
                                        .statusBarsPadding()
                                        .padding(horizontal = 24.dp),
                                horizontalAlignment = Alignment.CenterHorizontally,
                                verticalArrangement = Arrangement.Center,
                            ) {
                                Image(
                                    painter = painterResource(resource = Res.drawable.img_oguri_walking),
                                    contentDescription = null,
                                    modifier = Modifier.size(size = 180.dp),
                                )
                                Image(
                                    painter = painterResource(resource = logoResource),
                                    contentDescription = null,
                                    modifier = Modifier.size(width = 110.dp, height = 56.dp),
                                )
                                Spacer(modifier = Modifier.height(height = 32.dp))

                                LoginActionButton(
                                    iconResource = Res.drawable.ic_apple_login,
                                    iconSize = LOGIN_BUTTON_ICON_SIZE,
                                    titleText = stringResource(Res.string.login_apple_with_account),
                                    backgroundColor = Neutral100,
                                    contentColor = Neutral0,
                                    iconTintColor = Neutral0,
                                    onClick = onAppleLoginClick,
                                )
                                Spacer(modifier = Modifier.height(height = 18.dp))
                                LoginActionButton(
                                    iconResource = Res.drawable.ic_kakao_login,
                                    iconSize = LOGIN_BUTTON_ICON_SIZE,
                                    titleText = stringResource(Res.string.login_kakao_with_account),
                                    backgroundColor = KakaoYellow,
                                    contentColor = Neutral100,
                                    onClick = onKakaoLoginClick,
                                )

                                Spacer(modifier = Modifier.height(height = 14.dp))
                                Text(
                                    text = stringResource(Res.string.login_guest_browse),
                                    style = OguriTheme.typography.bodySmall,
                                    color = Neutral50,
                                    modifier = Modifier.noRippleClickable(onClick = onGuestBrowseClick),
                                )
                            }

                            if (isLoading) {
                                CenteredLoadingIndicator()
                            }
                        }
                    }

                    private val LOGIN_BUTTON_ICON_SIZE = 18.dp

                    """.trimIndent()
            )

            //https://github.com/com3run/lifeplanner.git
            KotlinCode(
                code = $$"""
                    // File picker for restore
                    val filePicker = rememberFilePicker { result ->
                        when (result) {
                            is FilePickerResult.Success -> { }
                            is FilePickerResult.Error -> { }
                            FilePickerResult.Cancelled -> { }
                        }
                    }
                    
                    //commonMain
                    import androidx.compose.runtime.Composable

                    /**
                     * Platform-specific file picker result
                     */
                    sealed class FilePickerResult {
                        data class Success(val content: String, val fileName: String) : FilePickerResult()
                        data class Error(val message: String) : FilePickerResult()
                        data object Cancelled : FilePickerResult()
                    }

                    /**
                     * Platform-specific file picker interface
                     */
                    interface FilePicker {
                        /**
                         * Launch the file picker for JSON files
                         */
                        fun launchFilePicker()
                    }

                    /**
                     * Creates a file picker and returns a launcher function.
                     * Call the returned function to open the file picker.
                     * Results will be delivered via the onResult callback.
                     */
                    @Composable
                    expect fun rememberFilePicker(
                        onResult: (FilePickerResult) -> Unit
                    ): FilePicker
                    
                    //iosMain
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.remember
                    import kotlinx.cinterop.ExperimentalForeignApi
                    import platform.Foundation.NSData
                    import platform.Foundation.NSString
                    import platform.Foundation.NSURL
                    import platform.Foundation.NSUTF8StringEncoding
                    import platform.Foundation.create
                    import platform.Foundation.lastPathComponent
                    import platform.UIKit.UIApplication
                    import platform.UIKit.UIDocumentPickerDelegateProtocol
                    import platform.UIKit.UIDocumentPickerViewController
                    import platform.UniformTypeIdentifiers.UTTypeJSON
                    import platform.UniformTypeIdentifiers.UTTypePlainText
                    import platform.darwin.NSObject

                    @OptIn(ExperimentalForeignApi::class)
                    @Composable
                    actual fun rememberFilePicker(
                        onResult: (FilePickerResult) -> Unit
                    ): FilePicker {
                        return remember {
                            IOSFilePicker(onResult)
                        }
                    }

                    @OptIn(ExperimentalForeignApi::class)
                    private class IOSFilePicker(
                        private val onResult: (FilePickerResult) -> Unit
                    ) : FilePicker {

                        private var delegate: DocumentPickerDelegate? = null

                        override fun launchFilePicker() {
                            delegate = DocumentPickerDelegate(onResult)

                            val documentPicker = UIDocumentPickerViewController(
                                forOpeningContentTypes = listOf(UTTypeJSON, UTTypePlainText)
                            )
                            documentPicker.delegate = delegate
                            documentPicker.allowsMultipleSelection = false

                            val rootViewController = UIApplication.sharedApplication.keyWindow?.rootViewController
                            rootViewController?.presentViewController(
                                documentPicker,
                                animated = true,
                                completion = null
                            )
                        }
                    }

                    @OptIn(ExperimentalForeignApi::class)
                    private class DocumentPickerDelegate(
                        private val onResult: (FilePickerResult) -> Unit
                    ) : NSObject(), UIDocumentPickerDelegateProtocol {

                        override fun documentPicker(
                            controller: UIDocumentPickerViewController,
                            didPickDocumentsAtURLs: List<*>
                        ) {
                            val url = didPickDocumentsAtURLs.firstOrNull() as? NSURL
                            if (url == null) {
                                onResult(FilePickerResult.Cancelled)
                                return
                            }

                            try {
                                // Start accessing security-scoped resource
                                val accessing = url.startAccessingSecurityScopedResource()

                                val content = NSString.create(contentsOfURL = url, encoding = NSUTF8StringEncoding, error = null)
                                val fileName = url.lastPathComponent ?: "backup.json"

                                // Stop accessing security-scoped resource
                                if (accessing) {
                                    url.stopAccessingSecurityScopedResource()
                                }

                                if (content != null) {
                                    onResult(FilePickerResult.Success(content.toString(), fileName))
                                } else {
                                    onResult(FilePickerResult.Error("Failed to read file content"))
                                }
                            } catch (e: Exception) {
                                onResult(FilePickerResult.Error(e.message ?: "Unknown error"))
                            }
                        }

                        override fun documentPickerWasCancelled(controller: UIDocumentPickerViewController) {
                            onResult(FilePickerResult.Cancelled)
                        }
                    }
                    
                    //androidMain
                    import android.content.Context
                    import android.net.Uri
                    import android.provider.OpenableColumns
                    import androidx.activity.compose.rememberLauncherForActivityResult
                    import androidx.activity.result.contract.ActivityResultContracts
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.remember
                    import androidx.compose.ui.platform.LocalContext
                    import co.touchlab.kermit.Logger

                    @Composable
                    actual fun rememberFilePicker(
                        onResult: (FilePickerResult) -> Unit
                    ): FilePicker {
                        val context = LocalContext.current

                        val launcher = rememberLauncherForActivityResult(
                            contract = ActivityResultContracts.OpenDocument()
                        ) { uri: Uri? ->
                            if (uri == null) {
                                onResult(FilePickerResult.Cancelled)
                                return@rememberLauncherForActivityResult
                            }

                            try {
                                val content = readFileContent(context, uri)
                                val fileName = getFileName(context, uri) ?: "backup.json"

                                if (content != null) {
                                    onResult(FilePickerResult.Success(content, fileName))
                                } else {
                                    onResult(FilePickerResult.Error("Failed to read file content"))
                                }
                            } catch (e: Exception) {
                                onResult(FilePickerResult.Error(e.message ?: "Unknown error"))
                            }
                        }

                        return remember(launcher) {
                            object : FilePicker {
                                override fun launchFilePicker() {
                                    launcher.launch(arrayOf("application/json", "text/plain", "*/*"))
                                }
                            }
                        }
                    }

                    private fun readFileContent(context: Context, uri: Uri): String? {
                        return try {
                            context.contentResolver.openInputStream(uri)?.use { inputStream ->
                                inputStream.bufferedReader().readText()
                            }
                        } catch (e: Exception) {
                            Logger.e("FilePicker") { "Failed to read file content: ${e.message}" }
                            null
                        }
                    }

                    private fun getFileName(context: Context, uri: Uri): String? {
                        return try {
                            context.contentResolver.query(uri, null, null, null, null)?.use { cursor ->
                                if (cursor.moveToFirst()) {
                                    val nameIndex = cursor.getColumnIndex(OpenableColumns.DISPLAY_NAME)
                                    if (nameIndex >= 0) {
                                        cursor.getString(nameIndex)
                                    } else null
                                } else null
                            }
                        } catch (e: Exception) {
                            null
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/thementalgoose/kmp-flashback.git
            KotlinCode(
                code = """
                    internal fun firebaseModule() = module {
                        singleOf<FirebaseCrashlyticsService>(::FirebaseCrashlyticsServiceImpl)
                    }
                    
                    //commonMain
                    interface FirebaseCrashlyticsService {
                        fun setCrashlyticsCollectionEnabled(enabled: Boolean)
                        fun setCustomKey(key: String, value: String)
                        fun setCustomKey(key: String, value: Boolean)
                        fun setUserId(userId: String)
                        fun logInfo(msg: String)
                        fun logError(msg: String)
                        fun recordException(error: Exception)
                    }
                    
                    internal expect class FirebaseCrashlyticsServiceImpl(): FirebaseCrashlyticsService {
                        override fun setCrashlyticsCollectionEnabled(enabled: Boolean)
                        override fun setCustomKey(key: String, value: String)
                        override fun setCustomKey(key: String, value: Boolean)
                        override fun setUserId(userId: String)
                        override fun logInfo(msg: String)
                        override fun logError(msg: String)
                        override fun recordException(error: Exception)
                    }
                    
                    //iosMain
                    import cocoapods.FirebaseCrashlytics.FIRCrashlytics
                    import kotlinx.cinterop.ExperimentalForeignApi
                    import platform.Foundation.NSError

                    @OptIn(ExperimentalForeignApi::class)
                    internal actual class FirebaseCrashlyticsServiceImpl actual constructor():
                        FirebaseCrashlyticsService {

                        companion object {
                            private const val TAG = "Crashlytics"
                        }

                        private val instance: FIRCrashlytics
                            get() = FIRCrashlytics.crashlytics()

                        actual override fun setCrashlyticsCollectionEnabled(enabled: Boolean) {
                            instance.setCrashlyticsCollectionEnabled(enabled)
                        }

                        actual override fun setCustomKey(key: String, value: String) {
                            instance.setCustomValue(value, key)
                        }

                        actual override fun setCustomKey(key: String, value: Boolean) {
                            instance.setCustomValue(value, key)
                        }

                        actual override fun setUserId(userId: String) {
                            instance.setUserID(userId)
                        }

                        actual override fun logError(msg: String) {
                            instance.log(msg)
                        }

                        actual override fun logInfo(msg: String) {
                            instance.log(msg)
                        }

                        actual override fun recordException(error: Exception) {
                            instance.recordError(NSError.errorWithDomain(error.message, 0, emptyMap<Any?, Any>()))
                        }
                    }
                    
                    //androidMain
                    import com.google.firebase.crashlytics.FirebaseCrashlytics

                    internal actual class FirebaseCrashlyticsServiceImpl actual constructor():
                        FirebaseCrashlyticsService {

                        companion object {
                            private const val TAG = "Crashlytics"
                        }

                        private val instance: FirebaseCrashlytics
                            get() = FirebaseCrashlytics.getInstance()

                        actual override fun setCrashlyticsCollectionEnabled(enabled: Boolean) {
                            instance.isCrashlyticsCollectionEnabled = enabled
                        }

                        actual override fun setCustomKey(key: String, value: String) {
                            instance.setCustomKey(key, value)
                        }

                        actual override fun setCustomKey(key: String, value: Boolean) {
                            instance.setCustomKey(key, value)
                        }

                        actual override fun setUserId(userId: String) {
                            instance.setUserId(userId)
                        }

                        actual override fun logError(msg: String) {
                            instance.log(msg)
                        }

                        actual override fun logInfo(msg: String) {
                            instance.log(msg)
                        }

                        actual override fun recordException(error: Exception) {
                            instance.recordException(error)
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/dalafiarisamuel/Unsplash-KMP.git (KoinComponent, Koin using Get argument)
            KotlinCode(
                code = $$"""
                    internal sealed class ImageDownloadState {
                        data object Idle : ImageDownloadState()
                        data object Loading : ImageDownloadState()
                        data object Success : ImageDownloadState()
                        data class Failure(val exception: Throwable) : ImageDownloadState()
                    }
                    
                    internal expect class PlatformDownloadImage {
                        suspend fun downloadImage(imageLink: String): ImageDownloadState
                    }
                    
                    //iosMain
                    import kotlinx.cinterop.ExperimentalForeignApi
                    import kotlinx.coroutines.Dispatchers
                    import kotlinx.coroutines.IO
                    import kotlinx.coroutines.suspendCancellableCoroutine
                    import kotlinx.coroutines.withContext
                    import okio.IOException
                    import platform.Foundation.NSData
                    import platform.Foundation.NSError
                    import platform.Foundation.NSURL
                    import platform.Foundation.NSURLErrorDomain
                    import platform.Foundation.NSURLResponse
                    import platform.Foundation.NSURLSession
                    import platform.Foundation.dataTaskWithURL
                    import platform.UIKit.UIImage
                    import platform.UIKit.UIImageWriteToSavedPhotosAlbum
                    import ui.state.ImageDownloadState
                    import kotlin.coroutines.resume
                    import kotlin.coroutines.resumeWithException

                    @OptIn(ExperimentalForeignApi::class)
                    internal actual class PlatformDownloadImage {

                        actual suspend fun downloadImage(imageLink: String): ImageDownloadState {
                            return withContext(Dispatchers.IO) {
                                try {
                                    val nsUrl = NSURL(string = imageLink)

                                    val (data, _) = fetchData(nsUrl)

                                    if (data == null) {
                                        println("No data received")
                                        return@withContext ImageDownloadState.Failure(Exception("No data was received"))
                                    }

                                    val image = UIImage(data = data)

                                    saveImageToPhotos(image)
                                    println("Image saved successfully")
                                    ImageDownloadState.Success
                                } catch (e: Exception) {
                                    ImageDownloadState.Failure(e)
                                }
                            }
                        }

                        private suspend fun fetchData(url: NSURL): Pair<NSData?, NSURLResponse?> =
                            suspendCancellableCoroutine { continuation ->
                                val session = NSURLSession.sharedSession
                                val task =
                                    session.dataTaskWithURL(url) { data, response, error ->
                                        if (error != null) {
                                            continuation.resumeWithException(error.toKotlinException())
                                        } else {
                                            continuation.resume(Pair(data, response))
                                        }
                                    }
                                task.resume()
                            }

                        private suspend fun saveImageToPhotos(image: UIImage) =
                            suspendCancellableCoroutine<Unit> { continuation ->
                                UIImageWriteToSavedPhotosAlbum(image, null, null, null)
                                continuation.resume(Unit)
                            }

                        private fun NSError.toKotlinException(): Exception {
                            return when (domain) {
                                NSURLErrorDomain -> IOException(localizedDescription)
                                else -> Exception(localizedDescription)
                            }
                        }
                    }
                    
                    //androidMain
                    import android.app.DownloadManager
                    import android.content.Context
                    import android.os.Environment
                    import androidx.core.net.toUri
                    import kotlin.time.Duration.Companion.seconds
                    import kotlinx.coroutines.Dispatchers
                    import kotlinx.coroutines.delay
                    import kotlinx.coroutines.withContext
                    import ui.state.ImageDownloadState

                    internal actual class PlatformDownloadImage(private val context: Context) {
                        actual suspend fun downloadImage(imageLink: String): ImageDownloadState {
                            withContext(Dispatchers.Default) { delay(3.seconds) }
                            return withContext(Dispatchers.Main) {
                                try {
                                    val uri = imageLink.toUri()
                                    val fileName = "${uri.pathSegments[0]}.jpeg"

                                    val request =
                                        DownloadManager.Request(uri)
                                            .setNotificationVisibility(
                                                DownloadManager.Request.VISIBILITY_VISIBLE_NOTIFY_COMPLETED
                                            )
                                            .setDestinationInExternalPublicDir(
                                                Environment.DIRECTORY_DOWNLOADS,
                                                fileName,
                                            )

                                    (context.getSystemService(Context.DOWNLOAD_SERVICE) as DownloadManager).enqueue(
                                        request
                                    )
                                    ImageDownloadState.Success
                                } catch (e: Exception) {
                                    ImageDownloadState.Failure(e)
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/AchalVishnoi/SpadeBreak-Live-Front-end-.git
            KotlinCode(
                code = """
                    //koin module 
                    
                    //commonMain
                    enum class ToastLength {
                        SHORT,
                        LONG
                    }
                    
                    expect class ToastManager {
                        fun showToast(message: String,toastLength: ToastLength = ToastLength.SHORT)
                    }
                    
                    expect fun toastModule(): Module
                    
                    fun appModule():List<Module> = listOf(
                        toastModule()
                    )
                    
                    //androidMain
                    class MyApp:Application() {
                        override fun onCreate() {
                            super.onCreate()

                            startKoin {
                                androidContext(this@MyApp)
                                modules(appModule())
                            }
                        }
                    }
                    
                    actual fun toastModule(): Module = module {
                        single { ToastManager(get()) }
                    }
                    
                    actual class ToastManager(private val context: Context) {
                        actual fun showToast(message: String, toastLength: ToastLength) {
                            val duration = when (toastLength) {
                                ToastLength.SHORT -> Toast.LENGTH_SHORT
                                ToastLength.LONG -> Toast.LENGTH_LONG
                            }
                            Toast.makeText(context, message, duration).show()
                        }
                    }
                    
                    //iosMain
                    actual fun toastModule(): Module= module {  }
                    
                    actual class ToastManager {
                        actual fun showToast(
                            message: String,
                            toastLength: ToastLength
                        ) {
                        }
                    }
                    
                    //call
                    val toastManager = getKoin().get<ToastManager>()
                    toastManager.showToast("")
                    """.trimIndent()
            )

            //https://github.com/Ma7moud3ly/quran-multiplatform.git
            KotlinCode(
                code = $$"""
                    //koin annotation 
                    
                    interface Platform {
                        val name: String
                        val id: String
                    }

                    expect fun getPlatform(): Platform

                    val Platform.isWasmJs get() = this.id == "wasmJs"
                    val Platform.isJvm get() = this.id == "jvm"
                    val Platform.isAndroid get() = this.id == "android"
                    val Platform.isIos get() = this.id == "ios"
                    val Platform.isMobile get() = isAndroid || isIos
                    
                    //koin DI annotation
                    @Module
                    @ComponentScan("")
                    class AppModule {
                        @Single
                        fun providePlatform(): Platform = getPlatform()
                    }
                    
                    //init
                    fun main() {
                        startKoin {
                            modules(AppModule().module)
                        }
                    }
                    
                    //androidMain
                    class AndroidPlatform : Platform {
                        override val name: String = "Android ${Build.VERSION.SDK_INT}"
                        override val id: String = "android"
                    }

                    actual fun getPlatform(): Platform = AndroidPlatform()
                    
                    //iosMain
                    class IOSPlatform : Platform {
                        override val name: String =
                            UIDevice.currentDevice.systemName() + " " + UIDevice.currentDevice.systemVersion
                        override val id: String = "ios"
                    }

                    actual fun getPlatform(): Platform = IOSPlatform()
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                     //Get the value
                     expect fun openBrowser(url: String, completionHandler: (Boolean) -> Unit = {})
                     
                     //androidMain
                     actual fun openBrowser(url: String, completionHandler: (Boolean) -> Unit) {
                         try {
                             val intent = Intent(
                                 Intent.ACTION_VIEW, url.toUri()
                             ).apply { addFlags(Intent.FLAG_ACTIVITY_NEW_TASK) }
                             context.startActivity(intent)
                         } catch (e: Exception) {
                             println(e.printStackTrace())
                             completionHandler(false)
                         }
                     }
                     
                     //iosMain
                     actual fun openBrowser(url: String, completionHandler: (Boolean) -> Unit) {
                         NSURL.URLWithString(url)?.let {
                             UIApplication.sharedApplication.openURL(
                                 url = it,
                                 options = emptyMap<Any?, Any>(),
                                 completionHandler = completionHandler
                             )
                         }
                     }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //class
                    
                    class Platform(val name: String)

                    expect fun getPlatform(): Platform
                    
                    //androidMain
                    import android.os.Build

                    actual fun getPlatform(): Platform = Platform(
                       name="Android ${Build.VERSION.SDK_INT}"
                    )
                    
                    //iosMain
                    import platform.UIKit.UIDevice

                    actual fun getPlatform(): Platform = Platform(
                       name= UIDevice.currentDevice.systemName() + " " + UIDevice.currentDevice.systemVersion
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //interface
                    
                    interface Platform {
                        val name: String
                    }

                    expect fun getPlatform(): Platform
                    
                    //androidMain
                    import android.os.Build

                    class AndroidPlatform : Platform {
                        override val name: String = "Android ${Build.VERSION.SDK_INT}"
                    }

                    actual fun getPlatform(): Platform = AndroidPlatform()
                    
                    //iosMain
                    import platform.UIKit.UIDevice

                    class IOSPlatform : Platform {
                        override val name: String =
                            UIDevice.currentDevice.systemName() + " " + UIDevice.currentDevice.systemVersion
                    }

                    actual fun getPlatform(): Platform = IOSPlatform(
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //interface extend
                    
                    import kotlinx.coroutines.CoroutineDispatcher

                    interface DispatcherProvider {
                        val main: CoroutineDispatcher
                        val io: CoroutineDispatcher
                        val unconfined: CoroutineDispatcher
                    }

                    internal expect fun getDispatcherProvider(): DispatcherProvider
                    
                    //iosMain
                    
                    import kotlinx.coroutines.Dispatchers

                    internal actual fun getDispatcherProvider(): DispatcherProvider = IosDispatcherProvider()

                    private class IosDispatcherProvider: DispatcherProvider {
                        override val main = Dispatchers.Main
                        override val io = Dispatchers.Default
                        override val unconfined = Dispatchers.Unconfined
                    }

                    //androidMain
                    import kotlinx.coroutines.Dispatchers

                    internal actual fun getDispatcherProvider(): DispatcherProvider = AndroidDispatcherProvider()

                    private class AndroidDispatcherProvider: DispatcherProvider{
                        override val main = Dispatchers.Main
                        override val io = Dispatchers.IO
                        override val unconfined = Dispatchers.Unconfined
                    }
                    
                    //di
                    private val utilityModule = module {
                        factory { getDispatcherProvider() }
                    }
                    """.trimIndent()
            )

            //https://github.com/seabat/ramen-note
            KotlinCode(
                code = $$"""
                    //File Image Save, Load, Rename and Delete
                    
                    //commonMain
                    interface LocalStorageDataSourceContract {
                        suspend fun save(imageBytes: ByteArray, name: String)
                        suspend fun load(name: String): ByteArray?
                        suspend fun rename(oldName: String, newName: String)

                        suspend fun delete(name: String)
                    }
                    
                    //iosMain
                    interface SwiftLibDependencyFactoryContract {
                        fun provideLocalStorageDataSourceContract(): LocalStorageDataSourceContract
                    }
                    
                    import org.koin.core.module.Module
                    import org.koin.dsl.bind
                    import org.koin.dsl.module

                    fun createSwiftLibDependencyModule(factory: SwiftLibDependencyFactoryContract): Module = module {
                        single { factory.provideLocalStorageDataSourceContract() } bind LocalStorageDataSourceContract::class
                    }
                    
                    //iosApp
                    import ComposeApp
                    import SwiftUI

                    @main
                    struct iOSApp: App {

                        init() {
                            KoinHelperKt.doInitKoin(
                                onKoinStart: {
                                    IosModuleKt.createSwiftLibDependencyModule(
                                        factory: SwiftLibDependencyFactory.shared
                                    )
                                }
                            )
                        }

                        var body: some Scene {
                            WindowGroup {
                                ContentView()
                            }
                        }
                    }
                    
                    import ComposeApp

                    class SwiftLibDependencyFactory: SwiftLibDependencyFactoryContract {

                        static var shared = SwiftLibDependencyFactory()

                        func provideLocalStorageDataSourceContract() -> any LocalStorageDataSourceContract {
                            return IosLocalStorageDataSource()
                        }
                    }
                    
                    import ComposeApp
                    import Foundation

                    class IosLocalStorageDataSource: LocalStorageDataSourceContract {
                        
                        func __load(name: String) async throws -> KotlinByteArray? {
                            do {
                                let documentsPath = FileManager.default.urls(for: .documentDirectory, in: .userDomainMask).first
                                guard let documentsPath = documentsPath else {
                                    return nil
                                }
                                
                                let fileName = "\(name).png"
                                let fileURL = documentsPath.appendingPathComponent(fileName)
                                
                                // Check if file exists
                                guard FileManager.default.fileExists(atPath: fileURL.path) else {
                                    return nil
                                }
                                
                                let data = try Data(contentsOf: fileURL)
                                return KotlinByteArray(size: Int32(data.count)) { index in
                                    KotlinByte(value: Int8(bitPattern: data[Int(index)]))
                                }
                            } catch {
                                return nil
                            }
                        }

                        func __save(imageBytes: KotlinByteArray, name: String) async throws {
                            do {
                                let documentsPath = FileManager.default.urls(for: .documentDirectory, in: .userDomainMask).first
                                guard let documentsPath = documentsPath else {
                                    return
                                }
                                
                                let fileName = "\(name).png"
                                let fileURL = documentsPath.appendingPathComponent(fileName)
                                
                                // Convert KotlinByteArray to Data
                                var data = Data()
                                for i in 0..<Int(imageBytes.size) {
                                    let byte = imageBytes.get(index: Int32(i))
                                    data.append(UInt8(bitPattern: byte))
                                }
                                
                                try data.write(to: fileURL)
                            } catch {
                                // Handle error silently or log it
                            }
                        }

                        func __rename(oldName: String, newName: String) async throws {
                            do {
                                let documentsPath = FileManager.default.urls(for: .documentDirectory, in: .userDomainMask).first
                                guard let documentsPath = documentsPath else {
                                    return
                                }
                                
                                let oldFileName = "\(oldName).png"
                                let newFileName = "\(newName).png"
                                let oldFileURL = documentsPath.appendingPathComponent(oldFileName)
                                let newFileURL = documentsPath.appendingPathComponent(newFileName)
                                
                                if FileManager.default.fileExists(atPath: oldFileURL.path) {
                                    try FileManager.default.moveItem(at: oldFileURL, to: newFileURL)
                                }
                            } catch {
                                // Handle error silently or log it
                            }
                        }

                        func __delete(name: String) async throws {
                            do {
                                let documentsPath = FileManager.default.urls(for: .documentDirectory, in: .userDomainMask).first
                                guard let documentsPath = documentsPath else {
                                    return
                                }
                                
                                let fileName = "\(name).png"
                                let fileURL = documentsPath.appendingPathComponent(fileName)
                                
                                if FileManager.default.fileExists(atPath: fileURL.path) {
                                    try FileManager.default.removeItem(at: fileURL)
                                }
                            } catch {
                                // Handle error silently or log it
                            }
                        }
                    }
                    
                    //androidMain
                    import android.content.Context
                    import kotlinx.coroutines.Dispatchers
                    import kotlinx.coroutines.withContext
                    import java.io.File
                    import java.io.FileOutputStream
                    import java.io.FileInputStream

                    class AndroidLocalStorageDataSource(private val context: Context) : LocalStorageDataSourceContract {
                        
                        override suspend fun save(imageBytes: ByteArray, name: String) {
                            withContext(Dispatchers.IO) {
                                try {
                                    val fileName = "$name.png"
                                    val file = File(context.filesDir, fileName)
                                    FileOutputStream(file).use { fos ->
                                        fos.write(imageBytes)
                                    }
                                } catch (e: Exception) {
                                    // Handle error silently or log it
                                }
                            }
                        }

                        override suspend fun load(name: String): ByteArray? {
                            return withContext(Dispatchers.IO) {
                                try {
                                    val fileName = "$name.png"
                                    val file = File(context.filesDir, fileName)
                                    if (file.exists()) {
                                        FileInputStream(file).use { fis ->
                                            fis.readBytes()
                                        }
                                    } else {
                                        null
                                    }
                                } catch (e: Exception) {
                                    null
                                }
                            }
                        }

                        override suspend fun rename(oldName: String, newName: String) {
                            withContext(Dispatchers.IO) {
                                try {
                                    val oldFileName = "$oldName.png"
                                    val newFileName = "$newName.png"
                                    val oldFile = File(context.filesDir, oldFileName)
                                    val newFile = File(context.filesDir, newFileName)
                                    
                                    if (oldFile.exists()) {
                                        oldFile.renameTo(newFile)
                                    }
                                } catch (e: Exception) {
                                    // Handle error silently or log it
                                }
                            }
                        }

                        override suspend fun delete(name: String) {
                            withContext(Dispatchers.IO) {
                                try {
                                    val fileName = "$name.png"
                                    val file = File(context.filesDir, fileName)
                                    
                                    if (file.exists()) {
                                        file.delete()
                                    }
                                } catch (e: Exception) {
                                    // Handle error silently or log it
                                }
                            }
                        }
                    }
                    
                    actual val dataSourceModule = module {
                        single<LocalStorageDataSourceContract> { AndroidLocalStorageDataSource(context = androidContext()) }
                    }
                    
                    //commonMain
                    fun initKoin(onKoinStart: () -> Module) {
                        startKoin {
                            modules(
                                dataSourceModule,
                                onKoinStart()
                            )
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/rainxchzed/FebruaryMiniChallenges.git
            KotlinCode(
                code = """
                    interface ConnectivityManager {
                        fun connectivityStateFlow(): Flow<ConnectivityState>
                    }
                    
                    //androidMain
                    class AndroidConnectivityManager : ConnectivityManager {
                        override fun connectivityStateFlow(): Flow<ConnectivityState> = combine(
                            observeConnectivity()
                                .onStart { emit(ConnectivityState.Disconnected) },
                            airplaneModeFlow()
                                .onStart { emit(isAirplaneModeOn()) }
                        ) { networkState, isAirplaneOn ->
                            if (isAirplaneOn) {
                                ConnectivityState.Airplane
                            } else {
                                networkState
                            }
                        }

                        fun observeConnectivity(): Flow<ConnectivityState> = callbackFlow {
                            val connectivityManager = ApplicationContextHolder.connectivityManager

                            fun hasInternet(capabilities: NetworkCapabilities?): Boolean {
                                return capabilities?.hasCapability(
                                    NetworkCapabilities.NET_CAPABILITY_INTERNET
                                ) == true &&
                                        capabilities.hasCapability(
                                            NetworkCapabilities.NET_CAPABILITY_VALIDATED
                                        )
                            }

                            val callback = object : android.net.ConnectivityManager.NetworkCallback() {
                                override fun onAvailable(network: Network) {
                                    super.onAvailable(network)
                                    trySend(ConnectivityState.Connected)
                                }

                                override fun onCapabilitiesChanged(
                                    network: Network,
                                    networkCapabilities: NetworkCapabilities
                                ) {
                                    if (hasInternet(networkCapabilities)) {
                                        trySend(ConnectivityState.Connected)
                                    } else {
                                        trySend(ConnectivityState.Disconnected)
                                    }
                                }

                                override fun onLost(network: Network) {
                                    super.onLost(network)

                                    trySend(ConnectivityState.Disconnected)
                                }
                            }

                            connectivityManager.registerDefaultNetworkCallback(callback)

                            awaitClose {
                                connectivityManager.unregisterNetworkCallback(callback)
                            }
                        }

                        fun airplaneModeFlow(): Flow<Boolean> = callbackFlow {
                            val receiver = object : BroadcastReceiver() {
                                override fun onReceive(context: Context, intent: Intent) {
                                    val isOn = intent.getBooleanExtra("state", false)
                                    trySend(isOn)
                                }
                            }

                            ApplicationContextHolder.registerBroadcast(
                                receiver,
                                IntentFilter(Intent.ACTION_AIRPLANE_MODE_CHANGED)
                            )

                            awaitClose {
                                ApplicationContextHolder.unregisterReceiver(receiver)
                            }
                        }

                        private fun isAirplaneModeOn(): Boolean =
                            Settings.Global.getInt(
                                ApplicationContextHolder.contentResolver,
                                Settings.Global.AIRPLANE_MODE_ON,
                                0
                            ) == 1
                    }
                    
                    //iosMain
                    @OptIn(ExperimentalForeignApi::class)
                    class IOSConnectivityManager : ConnectivityManager {
                        override fun connectivityStateFlow(): Flow<ConnectivityState> = callbackFlow {
                            val monitor = nw_path_monitor_create()

                            if (monitor == null) {
                                trySend(ConnectivityState.Disconnected)
                                close()
                                return@callbackFlow
                            }

                            // Create queue with proper type
                            val queueAttr = dispatch_queue_attr_make_with_qos_class(
                                null,
                                QOS_CLASS_BACKGROUND,
                                0
                            )
                            val queue = dispatch_queue_create("com.app.networkmonitor", queueAttr)

                            nw_path_monitor_set_update_handler(monitor) { path ->
                                if (path != null) {
                                    val status = nw_path_get_status(path)
                                    val state = when (status) {
                                        nw_path_status_satisfied -> {
                                            // Double-check it has internet capability
                                            val hasInternet = nw_path_uses_interface_type(path, nw_interface_type_wifi) ||
                                                    nw_path_uses_interface_type(path, nw_interface_type_cellular) ||
                                                    nw_path_uses_interface_type(path, nw_interface_type_wired)

                                            if (hasInternet) ConnectivityState.Connected
                                            else ConnectivityState.Disconnected
                                        }
                                        else -> ConnectivityState.Disconnected
                                    }
                                    trySend(state)
                                }
                            }

                            nw_path_monitor_set_queue(monitor, queue)
                            nw_path_monitor_start(monitor)

                            awaitClose {
                                nw_path_monitor_cancel(monitor)
                            }
                        }
                    }
                    
                    data class StillConnectedState(
                        val connectivityState: ConnectivityState = ConnectivityState.Connected
                    )
                    
                    class StillConnectedViewModel(
                        private val connectivityManager: ConnectivityManager
                    ) : ViewModel() {

                        private var hasLoadedInitialData = false

                        private val _state = MutableStateFlow(StillConnectedState())
                        val state = _state
                            .onStart {
                                if (!hasLoadedInitialData) {
                                    observeConnectivityState()

                                    hasLoadedInitialData = true
                                }
                            }
                            .stateIn(
                                scope = viewModelScope,
                                started = SharingStarted.WhileSubscribed(5_000L),
                                initialValue = StillConnectedState()
                            )

                        private fun observeConnectivityState() {
                            viewModelScope.launch {
                                connectivityManager.connectivityStateFlow().collect { connectivityState ->
                                    _state.update {
                                        it.copy(
                                            connectivityState = connectivityState
                                        )
                                    }
                                }
                            }
                        }
                    }
                    
                    @Composable
                    fun StillConnectedRoot(
                        viewModel: StillConnectedViewModel
                    ) {
                        val state by viewModel.state.collectAsStateWithLifecycle()

                        StillConnectedScreen(
                            state = state,
                        )
                    }

                    @Composable
                    fun StillConnectedScreen(
                        state: StillConnectedState,
                    ) {
                        Box(
                            Modifier
                                .fillMaxSize()
                                .background(StillConnectedColors.background),
                            contentAlignment = Alignment.Center
                        ) {
                            Column(
                                modifier = Modifier.fillMaxSize(),
                                horizontalAlignment = Alignment.CenterHorizontally,
                                verticalArrangement = Arrangement.spacedBy(24.dp, Alignment.CenterVertically)
                            ) {
                                val image = when (state.connectivityState) {
                                    ConnectivityState.Airplane -> Res.drawable.airplane_mode
                                    ConnectivityState.Connected -> Res.drawable.connected
                                    ConnectivityState.Disconnected -> Res.drawable.disconnected
                                }
                                val text = when (state.connectivityState) {
                                    ConnectivityState.Airplane -> "You Turned on Airplane Mode"
                                    ConnectivityState.Connected -> "You’re Connected"
                                    ConnectivityState.Disconnected -> "We Lost the Connection"
                                }

                                Box(
                                    modifier = Modifier
                                        .width(300.dp)
                                        .height(240.dp),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Box(
                                        modifier = Modifier
                                            .size(240.dp)
                                            .clip(CircleShape)
                                            .background(StillConnectedColors.surface)
                                    )

                                    Image(
                                        painter = painterResource(image),
                                        contentDescription = null,
                                        modifier = Modifier
                                            .width(300.dp)
                                            .height(240.dp),
                                        contentScale = ContentScale.Crop
                                    )
                                }

                                Text(
                                    text = text,
                                    fontSize = 26.sp,
                                    fontWeight = FontWeight.Medium,
                                    color = StillConnectedColors.textPrimary,
                                    fontFamily = stackSansFont
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/wshtaits/GetLike.git
            KotlinCode(
                code = """
                    //StateFlow using connection check
                    
                    import kotlinx.coroutines.flow.StateFlow

                    expect class Connection {
                        val isConnectedFlow: StateFlow<Boolean>
                    }
                    
                    //iosMain
                    import kotlinx.coroutines.CoroutineScope
                    import kotlinx.coroutines.flow.MutableStateFlow
                    import kotlinx.coroutines.flow.StateFlow
                    import kotlinx.coroutines.job
                    import kotlinx.coroutines.launch
                    import platform.Network.nw_path_get_status
                    import platform.Network.nw_path_monitor_cancel
                    import platform.Network.nw_path_monitor_create
                    import platform.Network.nw_path_monitor_set_queue
                    import platform.Network.nw_path_monitor_set_update_handler
                    import platform.Network.nw_path_monitor_start
                    import platform.Network.nw_path_status_satisfied
                    import platform.darwin.dispatch_get_main_queue

                    actual class Connection(
                        private val coroutineScope: CoroutineScope,
                    ) {

                        private val _isConnectedFlow = MutableStateFlow(false)
                        actual val isConnectedFlow: StateFlow<Boolean> get() = _isConnectedFlow

                        init {
                            val monitor = nw_path_monitor_create()
                            nw_path_monitor_set_queue(monitor, dispatch_get_main_queue())
                            nw_path_monitor_set_update_handler(monitor) { path ->
                                coroutineScope.launch {
                                    val isConnected = nw_path_get_status(path) == nw_path_status_satisfied
                                    _isConnectedFlow.emit(isConnected)
                                }
                            }
                            nw_path_monitor_start(monitor)
                            coroutineScope.coroutineContext.job.invokeOnCompletion {
                                nw_path_monitor_cancel(monitor)
                            }
                        }
                    }
                    
                    //androidMain
                    import android.content.Context
                    import android.net.ConnectivityManager
                    import android.net.Network
                    import android.net.NetworkCapabilities
                    import android.net.NetworkRequest
                    import kotlinx.coroutines.CoroutineScope
                    import kotlinx.coroutines.flow.MutableStateFlow
                    import kotlinx.coroutines.flow.StateFlow
                    import kotlinx.coroutines.launch

                    actual class Connection(
                        context: Context,
                        private val scope: CoroutineScope,
                    ) {

                        private val _isConnectedFlow by lazy { MutableStateFlow(hasInternet()) }
                        actual val isConnectedFlow: StateFlow<Boolean> by lazy { _isConnectedFlow }

                        private val connectivityManager =
                            context.getSystemService(Context.CONNECTIVITY_SERVICE) as ConnectivityManager

                        init {
                            connectivityManager.registerNetworkCallback(
                                /* request = */ NetworkRequest.Builder()
                                    .addCapability(NetworkCapabilities.NET_CAPABILITY_INTERNET)
                                    .build(),
                                /* networkCallback = */ object : ConnectivityManager.NetworkCallback() {
                                    override fun onAvailable(network: Network) {
                                        // no op: network is available, but internet access is not guaranteed
                                    }

                                    override fun onLost(network: Network) {
                                        emitIsConnected(false)
                                    }

                                    override fun onCapabilitiesChanged(network: Network, capabilities: NetworkCapabilities) {
                                        val hasInternet = hasInternet(capabilities)
                                        emitIsConnected(hasInternet)
                                    }
                                }
                            )
                        }

                        private fun hasInternet(): Boolean {
                            val activeNetwork = connectivityManager.activeNetwork
                            val capabilities = connectivityManager.getNetworkCapabilities(activeNetwork) ?: return false
                            return hasInternet(capabilities)
                        }

                        private fun hasInternet(capabilities: NetworkCapabilities): Boolean =
                            capabilities.hasCapability(NetworkCapabilities.NET_CAPABILITY_INTERNET)
                                    && capabilities.hasCapability(NetworkCapabilities.NET_CAPABILITY_VALIDATED)

                        private fun emitIsConnected(value: Boolean) =
                            scope.launch { _isConnectedFlow.emit(value) }
                    } 
                    
                    //Android DI
                    actual fun connection(coroutineScope: CoroutineScope): Connection =
                        Connection(applicationContext, coroutineScope)
                    
                    //iOS DI
                    actual fun connection(coroutineScope: CoroutineScope): Connection =
                        Connection(coroutineScope)
                   
                    """.trimIndent()
            )

            //https://github.com/Cairo-Squad/EvolveFit.git
            KotlinCode(
                code = $$"""
                    //object
                    //PDF Report Generate with Logo
                    
                    expect object PdfReportManager {
                        fun generateAndShareReport(
                            report: ReportScreenState.ReportUiState,
                            name: String,
                            startDate: String,
                            endDate: String
                        )

                        fun createPDF(
                            platformContext: Any?,
                            report: ReportScreenState.ReportUiState,
                            name: String,
                            startDate: String,
                            endDate: String,
                            logoBytes: ByteArray
                        ): String

                        fun loadLogoBytes(platformContext: Any?, resourceName: String): ByteArray
                    }
                    
                    data class ReportScreenState(
                        val report: ReportUiState = ReportUiState(),
                        val reportSection: SectionStatus = SectionStatus.LOADING,
                        val workoutHistorySection: SectionStatus = SectionStatus.LOADING,
                        val profileSection: SectionStatus = SectionStatus.LOADING,

                        val reportScreenState: SectionStatus = SectionStatus.LOADING,

                        val workoutHistory: List<WorkoutHistoryUiState> = emptyList(),
                        val isDropDownMenuOpen: Boolean = false,
                        val weeks: List<WeekItem> = emptyList(),
                        val selectedWeek: WeekItem = WeekItem(),
                        val startDate: String = "",
                        val endDate: String = "",
                        val profile: ProfileUiState = ProfileUiState(),
                        val isRefreshing: Boolean = false,
                        val screenErrorMessage: StringResource? = null,
                    ) {
                        data class WeekItem(
                            val key: String = "",
                            val label: StringResource = Res.string.this_week
                        )

                        data class ReportUiState(
                            val waterConsumed: Float = 0f,
                            val timeSpent: String = "",
                            val takenCaloriesInKcal: Int = 0,
                            val expectedCalories: Int = 100,
                            val totalWorkouts: String = "",
                            val workoutPerWeek: WorkoutPerWeek = WorkoutPerWeek(),
                            val timeSpentPerWeek: TimeSpentPerWeek = TimeSpentPerWeek(),
                            val mostTrainedMuscles: TrainedMuscle = TrainedMuscle(),
                        )

                        data class WorkoutPerWeek(
                            val workoutsCount: List<Int> = emptyList(),
                            val day: List<String> = emptyList()
                        )

                        data class TimeSpentPerWeek(
                            val timeInSeconds: List<Long> = emptyList(),
                            val day: List<String> = emptyList()
                        )

                        data class TrainedMuscle(
                            val muscle: List<String> = emptyList(),
                            val percentage: List<Float> = emptyList()
                        )

                        data class WorkoutHistoryUiState(
                            val imageUrl: String = "",
                            val name: String = "",
                            val date: String = "",
                            val exercisesCount: Int = 0,
                            val duration: String = "",
                            val level: String = ""
                        )

                        data class ProfileUiState(
                            val name: String = "",
                        )

                        enum class SectionStatus {
                            LOADING,
                            SUCCESS,
                            ERROR
                        }

                        sealed class UiError {
                            data class ScreenError(val message: StringResource) : UiError()
                        }
                    }
                    
                    //androidMain
                    import android.content.Context
                    import android.content.Intent
                    import android.graphics.BitmapFactory
                    import android.graphics.Paint
                    import android.graphics.pdf.PdfDocument
                    import android.net.Uri
                    import android.os.Build
                    import androidx.annotation.RequiresApi
                    import androidx.appcompat.content.res.AppCompatResources
                    import androidx.core.content.FileProvider
                    import androidx.core.graphics.drawable.toBitmap
                    import androidx.core.graphics.scale
                    import com.cairosquad.evolvefit.EvolveFitApp
                    import com.cairosquad.evolvefit.viewmodel.report.ReportScreenState
                    import com.cairosquad.evolvefit.viewmodel.utils.formatDateDayMonth
                    import java.io.ByteArrayOutputStream
                    import java.io.File
                    import java.io.FileOutputStream
                    import java.util.Locale

                    @RequiresApi(Build.VERSION_CODES.O)
                    actual object PdfReportManager {

                        actual fun generateAndShareReport(
                            report: ReportScreenState.ReportUiState,
                            name: String,
                            startDate: String,
                            endDate: String
                        ) {
                            val start = formatDateDayMonth(startDate)
                            val end = formatDateDayMonth(endDate)
                            val context = EvolveFitApp.appContext
                            val logoBytes = loadLogoBytes(context, "logo")
                            val pdfPath = createPDF(context, report, name, start, end, logoBytes)
                            val file = File(pdfPath)

                            val uri: Uri = FileProvider.getUriForFile(
                                context,
                                "${context.packageName}.provider",
                                file
                            )

                            val shareIntent = Intent(Intent.ACTION_SEND).apply {
                                type = "application/pdf"
                                putExtra(Intent.EXTRA_STREAM, uri)
                                addFlags(Intent.FLAG_GRANT_READ_URI_PERMISSION)
                            }
                            val chooser = Intent.createChooser(shareIntent, "Share Report").apply {
                                addFlags(Intent.FLAG_ACTIVITY_NEW_TASK)
                            }
                            context.startActivity(chooser)

                        }

                        actual fun createPDF(
                            platformContext: Any?,
                            report: ReportScreenState.ReportUiState,
                            name: String,
                            startDate: String,
                            endDate: String,
                            logoBytes: ByteArray
                        ): String {

                            val context = platformContext as? Context
                                ?: throw IllegalArgumentException("Android createPDF requires an Android Context as platformContext")

                            val logoBitmap = if (logoBytes.isNotEmpty()) BitmapFactory.decodeByteArray(logoBytes, 0, logoBytes.size) else null

                            val document = PdfDocument()
                            val pageInfo = PdfDocument.PageInfo.Builder(595, 842, 1).create() // A4 points
                            var page = document.startPage(pageInfo)
                            var canvas = page.canvas

                            val paint = Paint().apply { textSize = 14f }
                            val boldPaint = Paint().apply { textSize = 16f; isFakeBoldText = true }

                            var y = 40f

                            canvas.drawText("Name: $name", 40f, y, boldPaint); y += 22f
                            canvas.drawText("Start date: $startDate", 40f, y, paint); y += 20f
                            canvas.drawText("End date: $endDate", 40f, y, paint)

                            logoBitmap?.let {
                                val scaledLogo = it.scale(60, 60, true)
                                canvas.drawBitmap(scaledLogo, (pageInfo.pageWidth - 100).toFloat(), 20f, null)
                            }

                            y += 30f
                            canvas.drawLine(40f, y, (pageInfo.pageWidth - 45).toFloat(), y, paint)
                            y += 50f

                            canvas.drawText("Weekly report for the duration of $startDate to $endDate.", 40f, y, paint); y += 28f

                            canvas.drawText("Total time spent training: ${report.timeSpent}", 40f, y, paint); y += 20f
                            canvas.drawText("Total workouts made: ${report.totalWorkouts}", 40f, y, paint); y += 20f
                            canvas.drawText("Total calories taken: ${report.takenCaloriesInKcal} kcal", 40f, y, paint); y += 20f
                            canvas.drawText("Expected calories: ${report.expectedCalories} kcal", 40f, y, paint); y += 20f
                            canvas.drawText("Total water drank: ${report.waterConsumed} liters", 40f, y, paint); y += 30f

                            canvas.drawText("Workouts per week:", 40f, y, boldPaint); y += 24f
                            val workoutsCounts = report.workoutPerWeek.workoutsCount
                            val workoutDays =  report.workoutPerWeek.day

                            val timeSecondsList = report.timeSpentPerWeek.timeInSeconds
                            val timeDays = report.timeSpentPerWeek.day

                            val itemCount = maxOf(
                                workoutsCounts.size,
                                workoutDays.size,
                                timeSecondsList.size,
                                timeDays.size
                            )

                            for (i in 0 until itemCount) {
                                val dayName = workoutDays.getOrNull(i)?.toString() ?: timeDays.getOrNull(i)?.toString() ?: "Day ${i + 1}"
                                val count = workoutsCounts.getOrNull(i) ?: 0
                                val seconds = timeSecondsList.getOrNull(i) ?: 0L
                                val minutes = seconds / 60
                                val line = "- ${dayName.capitalize(Locale.ROOT)}: Workouts = $count, Time spent = $minutes minutes"
                                canvas.drawText(line, 60f, y, paint)
                                y += 18f

                                // page break
                                if (y > pageInfo.pageHeight - 80) {
                                    document.finishPage(page)
                                    page = document.startPage(pageInfo)
                                    canvas = page.canvas
                                    y = 40f
                                }
                            }
                            y += 20f

                            canvas.drawText("Most trained muscles:", 40f, y, boldPaint); y += 26f
                            val muscles = report.mostTrainedMuscles.muscle
                            val percents = report.mostTrainedMuscles.percentage
                            val muscleCount = maxOf(muscles.size, percents.size)
                            for (i in 0 until muscleCount) {
                                val muscleName = muscles.getOrNull(i)?.toString() ?: "Muscle ${i + 1}"
                                val pct = (percents.getOrNull(i) ?: 0f) * 100f
                                val pctInt = pct.toInt()
                                canvas.drawText("- ${muscleName.capitalize(Locale.ROOT)}: $pctInt%", 60f, y, paint)
                                y += 18f
                                if (y > pageInfo.pageHeight - 80) {
                                    document.finishPage(page)
                                    page = document.startPage(pageInfo)
                                    canvas = page.canvas
                                    y = 40f
                                }
                            }

                            document.finishPage(page)

                            val file = File(context.cacheDir, "WeeklyReport.pdf")
                            FileOutputStream(file).use { out -> document.writeTo(out) }
                            document.close()

                            return file.absolutePath
                        }

                        actual fun loadLogoBytes(platformContext: Any?, resourceName: String): ByteArray {
                            val context = platformContext as? Context
                                ?: throw IllegalArgumentException("Android: platformContext (Context) required")

                            val resId = context.resources.getIdentifier(resourceName, "drawable", context.packageName)
                            if (resId == 0) throw IllegalArgumentException("Drawable '$resourceName' not found")

                            val drawable = AppCompatResources.getDrawable(context, resId)
                                ?: throw IllegalStateException("Unable to load drawable $resourceName")

                            val width = if (drawable.intrinsicWidth > 0) drawable.intrinsicWidth else 256
                            val height = if (drawable.intrinsicHeight > 0) drawable.intrinsicHeight else 256
                            val bitmap = drawable.toBitmap(width, height, android.graphics.Bitmap.Config.ARGB_8888)

                            val baos = ByteArrayOutputStream()
                            bitmap.compress(android.graphics.Bitmap.CompressFormat.PNG, 100, baos)
                            return baos.toByteArray()
                        }
                    }
                    
                    //iosMain
                    import com.cairosquad.evolvefit.viewmodel.report.ReportScreenState
                    import com.cairosquad.evolvefit.viewmodel.utils.formateDateDayMonth
                    import kotlinx.cinterop.BetaInteropApi
                    import kotlinx.cinterop.ExperimentalForeignApi
                    import kotlinx.cinterop.addressOf
                    import kotlinx.cinterop.usePinned
                    import platform.CoreGraphics.CGPointMake
                    import platform.CoreGraphics.CGRectMake
                    import platform.Foundation.NSData
                    import platform.Foundation.NSString
                    import platform.Foundation.NSTemporaryDirectory
                    import platform.Foundation.NSURL
                    import platform.Foundation.create
                    import platform.Foundation.getBytes
                    import platform.UIKit.UIActivityViewController
                    import platform.UIKit.UIApplication
                    import platform.UIKit.UIGraphicsBeginPDFContextToFile
                    import platform.UIKit.UIGraphicsBeginPDFPageWithInfo
                    import platform.UIKit.UIGraphicsEndPDFContext
                    import platform.UIKit.UIImage
                    import platform.UIKit.UIImagePNGRepresentation
                    import platform.UIKit.UIViewController
                    import platform.UIKit.drawAtPoint
                    import kotlin.math.max

                    actual object PdfReportManager {
                        @OptIn(ExperimentalForeignApi::class, BetaInteropApi::class)
                        actual fun createPDF(
                            platformContext: Any?,
                            report: ReportScreenState.ReportUiState,
                            name: String,
                            startDate: String,
                            endDate: String,
                            logoBytes: ByteArray
                        ): String {
                            val fileName = "WeeklyReport.pdf"
                            val tmpDir = NSTemporaryDirectory() ?: "/tmp/"
                            val filePath = tmpDir + fileName

                            val pageWidth = 595.0
                            val pageHeight = 842.0
                            val pageRect = CGRectMake(0.0, 0.0, pageWidth, pageHeight)

                            val logoImage: UIImage? = if (logoBytes.isNotEmpty()) {
                                logoBytes.usePinned { pinned ->
                                    val nsData = NSData.create(bytes = pinned.addressOf(0), length = logoBytes.size.toULong())
                                    nsData?.let { NSData -> UIImage.imageWithData(NSData) }
                                }
                            } else {
                                null
                            }

                            UIGraphicsBeginPDFContextToFile(filePath, pageRect, null)
                            UIGraphicsBeginPDFPageWithInfo(pageRect, null)

                            var y = 40.0

                            (("Name: $name") as NSString).drawAtPoint(CGPointMake(40.0, y), withAttributes = null)
                            y += 22.0
                            (("Start date: $startDate") as NSString).drawAtPoint(CGPointMake(40.0, y), withAttributes = null)
                            y += 20.0
                            (("End date: $endDate") as NSString).drawAtPoint(CGPointMake(40.0, y), withAttributes = null)

                            logoImage?.let {
                                val logoW = 60.0
                                val logoH = 60.0
                                it.drawInRect(CGRectMake(pageWidth - 95.0, 30.0, logoW, logoH))
                            }

                            y += 30.0

                            ("——————————————————————————————————————————" as NSString).drawAtPoint(CGPointMake(40.0, y), withAttributes = null)
                            y += 50.0

                            ("Weekly report for the duration of $startDate to $endDate." as NSString)
                                .drawAtPoint(CGPointMake(40.0, y), withAttributes = null)
                            y += 28.0

                            ("Total time spent training: ${report.timeSpent}" as NSString)
                                .drawAtPoint(CGPointMake(40.0, y), withAttributes = null)
                            y += 20.0
                            ("Total workouts made: ${report.totalWorkouts}" as NSString)
                                .drawAtPoint(CGPointMake(40.0, y), withAttributes = null)
                            y += 20.0
                            ("Total calories taken: ${report.takenCaloriesInKcal} kcal" as NSString)
                                .drawAtPoint(CGPointMake(40.0, y), withAttributes = null)
                            y += 20.0
                            ("Expected calories: ${report.expectedCalories} kcal" as NSString)
                                .drawAtPoint(CGPointMake(40.0, y), withAttributes = null)
                            y += 20.0
                            ("Total water drank: ${report.waterConsumed} liters" as NSString)
                                .drawAtPoint(CGPointMake(40.0, y), withAttributes = null)
                            y += 30.0

                            ("Workouts per week:" as NSString).drawAtPoint(CGPointMake(40.0, y), withAttributes = null)
                            y += 24.0

                            val workoutsCounts = report.workoutPerWeek.workoutsCount
                            val workoutDays = report.workoutPerWeek.day
                            val timeSecondsList = report.timeSpentPerWeek.timeInSeconds
                            val timeDays = report.timeSpentPerWeek.day

                            val itemCount = max(
                                workoutsCounts.size,
                                max(workoutDays.size, max(timeSecondsList.size, timeDays.size))
                            )
                            for (i in 0 until itemCount) {
                                val dayName = workoutDays.getOrNull(i)?.toString() ?: timeDays.getOrNull(i)?.toString() ?: "Day ${i + 1}"
                                val count = workoutsCounts.getOrNull(i) ?: 0
                                val seconds = timeSecondsList.getOrNull(i) ?: 0L
                                val minutes = seconds / 60
                                ("- ${dayName}: Workouts = $count, Time spent = $minutes minutes" as NSString)
                                    .drawAtPoint(CGPointMake(60.0, y), withAttributes = null)
                                y += 18.0

                                if (y > pageHeight - 80.0) {
                                    UIGraphicsBeginPDFPageWithInfo(pageRect, null)
                                    y = 40.0
                                }
                            }
                            y += 20.0

                            ("Most trained muscles:" as NSString).drawAtPoint(CGPointMake(40.0, y), withAttributes = null)
                            y += 24.0
                            val muscles = report.mostTrainedMuscles.muscle
                            val percents = report.mostTrainedMuscles.percentage
                            val muscleCount = max(muscles.size, percents.size)
                            for (i in 0 until muscleCount) {
                                val muscleName = muscles.getOrNull(i)?.toString() ?: "Muscle ${i + 1}"
                                val pctInt = ((percents.getOrNull(i) ?: 0f) * 100).toInt()
                                ("- ${muscleName}: ${pctInt}%" as NSString).drawAtPoint(CGPointMake(60.0, y), withAttributes = null)
                                y += 18.0
                                if (y > pageHeight - 80.0) {
                                    UIGraphicsBeginPDFPageWithInfo(pageRect, null)
                                    y = 40.0
                                }
                            }

                            UIGraphicsEndPDFContext()

                            return filePath
                        }

                        actual fun generateAndShareReport(
                            report: ReportScreenState.ReportUiState,
                            name: String,
                            startDate: String,
                            endDate: String
                        ) {
                            val start = formateDateDayMonth(startDate)
                            val end = formateDateDayMonth(endDate)
                            val logoBytes = loadLogoBytes(null, "evolve_logo")
                            val pdfPath = createPDF(null, report, name, start, end, logoBytes)

                            val url = NSURL.fileURLWithPath(pdfPath)

                            val activityVC = UIActivityViewController(
                                activityItems = listOf(url),
                                applicationActivities = null
                            )

                            val rootVC: UIViewController? =
                                UIApplication.sharedApplication.keyWindow?.rootViewController

                            rootVC?.presentViewController(activityVC, animated = true, completion = null)
                        }

                        @OptIn(ExperimentalForeignApi::class)
                        actual fun loadLogoBytes(platformContext: Any?, resourceName: String): ByteArray {
                            val uiImage = UIImage.imageNamed(resourceName)
                                ?: throw IllegalArgumentException("UIImage '$resourceName' not found")

                            val nsData: NSData = UIImagePNGRepresentation(uiImage)
                                ?: throw IllegalStateException("Failed to create PNG data for image '$resourceName'")

                            val length = nsData.length.toInt()
                            val result = ByteArray(length)

                            result.usePinned { pinned ->
                                nsData.getBytes(pinned.addressOf(0), nsData.length)
                            }

                            return result
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/santimattius/kmp-kvs-storage.git
            KotlinCode(
                code = $$"""
                    internal fun createDataStore(producePath: () -> String): DataStore<Preferences> =
                        PreferenceDataStoreFactory.createWithPath(
                            produceFile = { producePath().toPath() }
                        )

                    internal expect fun getDataStore(name: String): DataStore<Preferences>
                    
                    //iosMain
                    internal actual fun getDataStore(name: String): DataStore<Preferences> {
                        return createDataStore(name)
                    }

                    @OptIn(ExperimentalForeignApi::class)
                    private fun createDataStore(name: String): DataStore<Preferences> = createDataStore(
                        producePath = {
                            val documentDirectory: NSURL? = NSFileManager.defaultManager.URLForDirectory(
                                directory = NSDocumentDirectory,
                                inDomain = NSUserDomainMask,
                                appropriateForURL = null,
                                create = false,
                                error = null,
                            )
                            requireNotNull(documentDirectory).path + "/$name"
                        }
                    )
                    
                    //androidMain
                    class AppContextInitializer : Initializer<Unit> {

                        override fun create(context: Context) {
                            injectContext(context = context)
                            Log.d("AppContextInitializer", "AppContextInitializer initialized")
                        }

                        override fun dependencies(): List<Class<out Initializer<*>?>?> {
                            return emptyList()
                        }
                    }
                    
                    import android.annotation.SuppressLint
                    import android.app.Activity
                    import android.app.Application
                    import android.app.Service
                    import android.app.backup.BackupAgent
                    import android.content.Context
                    import android.content.ContextWrapper

                    /**
                     * Checks if the current [Context] instance is prone to memory leaks if stored statically.
                     *
                     * This function evaluates the context type to determine if it's safe to hold a long-lived reference to it.
                     * - [Application] contexts are safe.
                     * - [Activity], [Service], and [BackupAgent] contexts are not safe as they are tied to their respective component lifecycles.
                     * - [ContextWrapper] instances are checked based on their base context. If the base context is the instance itself (which can happen in some scenarios),
                     *   it's considered unsafe. Otherwise, the check is delegated to the base context.
                     * - Other context types are considered unsafe if their `applicationContext` is null, which might indicate a non-standard or problematic context.
                     *
                     * @return `true` if the context can leak memory, `false` otherwise.
                     */
                    fun Context.canLeakMemory(): Boolean = when (this) {
                        is Application -> false
                        is Activity, is Service, is BackupAgent -> true
                        is ContextWrapper -> if (baseContext === this) true else baseContext.canLeakMemory()
                        else -> applicationContext === null
                    }

                    @SuppressLint("StaticFieldLeak")
                    private var applicationContext: Context? = null

                    /**
                     * Injects a [Context] to be used as a global application context.
                     *
                     * This function should be called early in the application lifecycle, typically in `Application.onCreate()`,
                     * with the application context itself.
                     * It performs a check using [canLeakMemory] to ensure that the provided context is not one that
                     * could cause memory leaks if stored statically (e.g., an Activity context).
                     *
                     * @param context The [Context] to be used as the application context.
                     * @throws IllegalArgumentException if the provided context is deemed prone to memory leaks.
                     */
                    internal fun injectContext(context: Context) {
                        require(!context.canLeakMemory()) { "The passed $context would leak memory!" }
                        applicationContext = context
                    }

                    /**
                     * Retrieves the globally injected application [Context].
                     *
                     * This function provides access to the application context that was previously set via [injectContext].
                     * It's a convenient way to get a context from anywhere in the application without needing to pass it around.
                     *
                     * @return The application [Context].
                     * @throws IllegalStateException if [injectContext] has not been called before this function is invoked.
                     */
                    fun getApplicationContext(): Context = applicationContext ?: error("Context not injected!")
                    
                    internal actual fun getDataStore(name: String): DataStore<Preferences> {
                        return createDataStore(
                            context = getApplicationContext(),
                            name = name
                        )
                    }

                    private fun createDataStore(
                        context: Context,
                        name: String
                    ): DataStore<Preferences> = createDataStore(
                        producePath = { context.filesDir.resolve(name).absolutePath }
                    )
                    
                    <manifest xmlns:android="http://schemas.android.com/apk/res/android"
                        xmlns:tools="http://schemas.android.com/tools">

                        <application>
                            <provider
                                android:name="androidx.startup.InitializationProvider"
                                android:authorities="${applicationId}.androidx-startup"
                                android:directBootAware="true"
                                android:exported="false"
                                tools:ignore="UnusedAttribute"
                                tools:node="merge">
                                <meta-data
                                    android:name="com.santimattius.kvs.internal.context.AppContextInitializer"
                                    android:value="androidx.startup" />
                            </provider>
                        </application>

                    </manifest>
                    """.trimIndent()
            )

            //https://github.com/sajibsaj66/Ndulaz.git
            KotlinCode(
                code = """
                    expect val httpEngineModule: Module
                    
                    //androidMain
                    actual val httpEngineModule = module {
                        single { OkHttp.create() }
                    }
                    
                    //iosMain
                    actual val httpEngineModule = module {
                        single { Darwin.create() }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //open class Toast
                    
                    //commonMain
                    expect open class ToastManager() {
                        fun showToast(message: String, toastDurationType: ToastDurationType = ToastDurationType.SHORT)
                    }

                    enum class ToastDurationType {
                        SHORT,
                        LONG
                    }
                    
                    //iosMain
                    import platform.UIKit.*
                    import platform.CoreGraphics.*
                    import kotlinx.cinterop.ExperimentalForeignApi
                    import kotlinx.cinterop.useContents

                    actual open class ToastManager actual constructor() {
                        @OptIn(ExperimentalForeignApi::class)
                        actual fun showToast(message: String, toastDurationType: ToastDurationType) {

                            val duration = when (toastDurationType) {
                                ToastDurationType.SHORT -> 3.0
                                ToastDurationType.LONG -> 5.0
                            }

                            val rootViewController = UIApplication.sharedApplication.keyWindow?.rootViewController
                            val toastLabel = UILabel(
                                frame = CGRectMake(
                                    0.0,
                                    0.0,
                                    UIScreen.mainScreen.bounds.useContents { size.width } - 40,
                                    35.0
                                )
                            )
                            toastLabel.center = CGPointMake(
                                UIScreen.mainScreen.bounds.useContents { size.width } / 2,
                                UIScreen.mainScreen.bounds.useContents { size.height } - 45.0
                            )

                            toastLabel.textAlignment = NSTextAlignmentCenter
                            toastLabel.backgroundColor = UIColor.blackColor.colorWithAlphaComponent(0.6)
                            toastLabel.textColor = UIColor.whiteColor
                            toastLabel.text = message
                            toastLabel.alpha = 1.0
                            toastLabel.layer.cornerRadius = 10.0
                            toastLabel.clipsToBounds = true
                            rootViewController?.view?.addSubview(toastLabel)

                            UIView.animateWithDuration(
                                duration = duration,
                                delay = 0.1,
                                options = UIViewAnimationOptionCurveEaseOut,
                                animations = {
                                    toastLabel.alpha = 0.0
                                },
                                completion = {
                                    if (it)
                                        toastLabel.removeFromSuperview()
                                })
                        }
                    }
                    
                    //androidMain
                    import android.app.Activity
                    import android.widget.Toast

                    actual open class ToastManager actual constructor() {
                        actual fun showToast(message: String, toastDurationType: ToastDurationType) {
                            val context = activityProvider.invoke()
                            val duration = when (toastDurationType) {
                                ToastDurationType.SHORT -> Toast.LENGTH_SHORT
                                ToastDurationType.LONG -> Toast.LENGTH_LONG
                            }
                            Toast.makeText(context, message, duration).show()
                        }
                    }

                    private var activityProvider: () -> Activity? = {
                        null
                    }

                    fun setActivityProvider(provider: () -> Activity?) {
                        activityProvider = provider
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Why Call Swift Functions from Kotlin?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Reuse iOS-specific functionality",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "without reinventing the wheel.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Optimize development",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "by keeping platform-specific code in Swift but accessible in Kotlin.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Keep code clean",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "by centralizing your Swift and Kotlin interoperation in one place.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Kotlin with Objective-C and Swift",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "@ObjCName",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Allows to customize the name that will be used in Swift or Objective-C",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "@HiddenFromObjC",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Allows hiding a Kotlin declaration from Objective-C (and Swift).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "@ShouldRefineInSwift",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It marks a Kotlin declaration as swift_private in the Objective-C API, allowing to replace it with a wrapper written in Swift.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "gradle.properties",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    #MPP
                    kotlin.mpp.enableCInteropCommonization=true
                    kotlin.mpp.androidSourceSetLayoutVersion=2
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //swift

                    import SwiftUI
                    struct Planet: Hashable {
                        var index: Int
                        var image: String
                        var name: String
                        var description: String
                        var longDescription: String
                        var video: String
                    }

                    import Foundation
                    class SpaceDashboardModel {
                        func getData() -> [Planet] {
                            return [
                                Planet(index: 1, image: "ic-mercury", name: "Mercury", description: "Tiny inferno, closest to the Sun with wild temperature swings.", longDescription: "Tiny inferno, closest to the Sun with wild temperature swings. Scorched by day, freezing by night, it has no atmosphere.", video: ""),
                            ]
                        }
                    }

                    //access
                    let model = SpaceDashboardModel()
                    model.getData()

                    """.trimIndent()
            )

            SpanText(
                text = "Kotlin Map convert to Dictionary",
                modifier =  Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = $$"""
                    fun Map<String, String>.toDictionary(): NSDictionary {
                        val dictionary = NSMutableDictionary()
                        forEach { map ->
                            dictionary.setObject(anObject = map.value, forKey = map.key as NSCopyingProtocol)
                        }
                        return dictionary
                    }

                    init {
                           val map = mapOf("1" to "One", "2" to "Two", "3" to "Three").toDictionary()
                           println("Convert Map to Dictionary ->> $map")  // / remove
                   }
                    """.trimIndent()
            )

            SpanText(
                text = "Kotlin map store data class convert NSArray",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //kotlin
                    data class Student(val key: String, val value: String)

                    //swift
                    class Person {
                        let firstName: String
                        let lastName: String
                        init(firstName fn: String, lastName ln: String) {
                            firstName = fn
                            lastName = ln
                        }
                    }
                    //let person = Person(firstName: "Lakhani", lastName: "Kamlesh")

                    //val nsArray = listOf(1, 2, 3) as NSArray

                    fun Map<String, String>.toDictionary(): NSMutableArray {
                        val nsMutableArray = NSMutableArray()
                        forEach {
                            nsMutableArray.addObject(Student(it.value, it.key))
                        }
                        // nsMutableArray.removeObjectAtIndex(1u)

                        return nsMutableArray
                    }
                    """.trimIndent()
            )


            SpanText(
                text = "Flutter channel communication",
                modifier =  Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //android
                     val arguments = call.arguments as List<Map<String, Any>>
                     val userName = arguments.first()["text"] as String

                     //ios
                     guard let arguments = call.arguments as? [[String: Any]],
                     let firstArgument = arguments.first,
                     let userName = firstArgument["text"] as? String else {
                        result(FlutterError(code: "INVALID_ARGUMENT", message: "Invalid arguments", details: nil))
                        return
                     }
                    """.trimIndent()
            )

            SpanText(
                text = "var using",
                modifier =  Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = $$"""
                    //https://github.com/wh173d3v11/KMP-Sample

                    var toggleiOSProductDetails: ((ProductX) -> Unit)? = null

                    //UI OnClick
                    toggleiOSProductDetails?.invoke(product)

                    import UIKit
                    import SwiftUI
                    import ComposeApp

                    struct ComposeView: UIViewControllerRepresentable {
                        func makeUIViewController(context: Context) -> UIViewController {
                            MainViewControllerKt.MainViewController()
                        }

                        func updateUIViewController(_ uiViewController: UIViewController, context: Context) {}
                    }

                    struct ContentView: View {

                        @State private var showBottomSheet = false
                        @State private var product : ProductX? = nil


                        var body: some View {
                            ComposeView()
                                .ignoresSafeArea(.keyboard) // Compose has its own keyboard handler
                                .sheet(isPresented: ) {
                                    // Content of the bottom sheet
                                    BottomSheetView(showBottomSheet: , product: )
                                }
                                .onAppear(perform: setupToggleClosure)

                        }

                        private func setupToggleClosure() {
                            // Set the toggle closure here
                            IOSUtilsKt.toggleiOSProductDetails = { productX in
                                // Ensure UI update on the main thread
                                DispatchQueue.main.async {
                                    self.product = productX
                                    showBottomSheet.toggle()
                                }
                            }
                        }
                    }

                    struct BottomSheetView: View {
                        @Binding var showBottomSheet: Bool
                        @Binding var product : ProductX?

                        var body: some View {
                            VStack(
                                alignment: .center,
                                spacing: 8
                            ) {
                                Text(product?.title ?? "No Title")
                                    .font(.headline)
                                    .multilineTextAlignment(.center)
                                    .lineLimit(3)
                                    .padding(.horizontal)

                                AsyncImage(url: URL(string: product?.image ?? "")) { image in
                                    image
                                        .resizable()
                                        .scaledToFit()
                                        .frame(height: 130)
                                        .padding(8)
                                } placeholder: {
                                    Color.gray // Placeholder
                                        .frame(height: 130)
                                        .padding(8)
                                }
                                .padding(.horizontal)

                                Text(product?.description_ ?? "")
                                    .font(.body)
                                    .multilineTextAlignment(.center)
                                    .lineLimit(4)
                                    .padding(.horizontal)
                                    .foregroundColor(.gray)

                                Text("$(product?.price?.doubleValue ?? 0, specifier: "%.2f")")
                                    .font(.title3)
                                    .bold()
                                    .multilineTextAlignment(.center)
                                    .lineLimit(1)
                                    .padding(.horizontal)
                                    .foregroundColor(.gray)

                                Button(action: {
                                    // Action for Buy Now
                                }) {
                                    Text("Buy Now")
                                        .fontWeight(.bold)
                                        .frame(maxWidth: .infinity)
                                        .padding()
                                        .background(
                                            LinearGradient(gradient: Gradient(colors: [.yellow, .red]), startPoint: .leading, endPoint: .trailing)
                                        )
                                        .foregroundColor(.white)
                                        .cornerRadius(8)
                                }
                                .padding(.horizontal, 8)
                            }
                            .padding()
                            .frame(maxWidth: .infinity, maxHeight: .infinity)
                            .background(Color.white)
                            .edgesIgnoringSafeArea(.all)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Class using",
                modifier =  Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //commonMain

                    expect class Platform {
                        val osName: String
                        val osVersion: String
                        val deviceModel: String
                        val density: Int

                        fun loadSystemInfo()
                    }

                    //iosMain
                    import platform.Foundation.NSLog
                    import platform.UIKit.UIDevice
                    import platform.UIKit.UIScreen

                    actual class Platform() {
                        actual val osName: String
                            get() = "Ios"

                        actual val osVersion: String
                            get() = UIDevice.currentDevice.systemVersion

                        actual val deviceModel: String
                            get() = UIDevice.currentDevice.model

                        actual val density: Int
                            get() = UIScreen.mainScreen.scale.toInt()

                        actual fun loadSystemInfo() { }
                    }

                    //androidMain
                    import android.content.res.Resources
                    import android.os.Build
                    import android.util.Log
                    import kotlin.math.round

                    actual class Platform {

                        actual val osName: String
                            get() = "Android"

                        actual val osVersion: String
                            get() = ""

                        actual val deviceModel: String
                            get() = ""

                        actual val density: Int
                            get() = round(Resources.getSystem().displayMetrics.density).toInt()

                        actual fun loadSystemInfo() { }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Object using",
                modifier =  Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    expect object SharedService {
                        fun generateUniqId():String
                    }

                    //iosMain
                    import platform.Foundation.NSUUID.Companion.UUID

                    actual object SharedService {
                        actual fun generateUniqId():String{
                            return UUID().UUIDString()
                        }
                    }

                    //androidMain
                    import java.util.UUID

                    actual object SharedService {
                        actual fun generateUniqId():String{
                            return UUID.randomUUID().toString()
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/mwan9ilwa/cueview
            SpanText(
                text = "Fun using",
                modifier =  Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //CommonMain
                    interface FirebaseService { //Method }
                    
                    val repositoryModule = module {
                        single<FirebaseService> { createFirebaseService() }
                    }
                    
                    expect fun createFirebaseService(): FirebaseService
                    
                    //iosMain
                    class FirebaseServiceImpl : FirebaseService {
                        //Method
                    }
                    
                    actual fun createFirebaseService(): FirebaseService = FirebaseServiceImpl()
                    
                    //AndroidMain
                    class FirebaseServiceImpl : FirebaseService { 
                        //Method
                    }
                    
                    expect fun createFirebaseService(): FirebaseService
                    """.trimIndent()
            )

            //https://github.com/StringMeUp/KotlinMultiplatformSampleCMP
            SpanText(
                text = "constructor",
                modifier =  Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = $$"""
                    expect class KoinPlatform() {
                        val name: String
                    }

                    val platformModule = module {
                        singleOf(::KoinPlatform)
                    }

                    //iosMain
                    @Suppress("EXPECT_ACTUAL_CLASSIFIERS_ARE_IN_BETA_WARNING")
                    actual class KoinPlatform actual constructor() {
                        actual val name: String =
                            UIDevice.currentDevice.systemName() + " " + UIDevice.currentDevice.systemVersion
                    }

                    //androidMain
                    @Suppress("EXPECT_ACTUAL_CLASSIFIERS_ARE_IN_BETA_WARNING")
                    actual class KoinPlatform actual constructor() {
                        actual val name: String
                            get() = "Android ${Build.VERSION.SDK_INT}"
                    }
                    """.trimIndent()
                )

            SpanText(
                text = "Passing Context",
                modifier =  Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    expect class ContextFactory {
                        fun getContext(): Any
                        fun getApplication(): Any
                        fun getActivity(): Any
                    }

                    //androidMain
                    actual class ContextFactory(private val activity: ComponentActivity) {
                        actual fun getContext(): Any = activity.baseContext
                        actual fun getApplication(): Any = activity.application
                        actual fun getActivity(): Any = activity
                    }

                    //iosMain
                    actual class ContextFactory {
                        // Bundle allows you to lookup resources
                        actual fun getContext(): Any = Bundle
                        // UIApplication allows you to access all app info
                        actual fun getApplication(): Any = UIApplication
                        // RootViewController can be used to identify your current screen
                        actual fun getActivity(): Any = UIApplication.sharedApplication.keyWindow?.rootViewController
                    }

                    //init android
                    class MainActivity : ComponentActivity() {
                        override fun onCreate(savedInstanceState: Bundle?) {
                            super.onCreate(savedInstanceState)
                            setContent {
                              App(
                                ContextFactory(this) // Use local Activity Context "this" to init
                              )
                            }
                        }
                    }

                    //init iOS
                    fun MainViewController(): UIViewController =
                        ComposeUIViewController {
                            App(ContextFactory()) // iOS constructor requires no arguments
                    }

                    //UI
                    @Preview
                    @Composable
                    fun App(
                      platformContext: ContextFactory // Injecting Context from all platforms
                    ) {
                        AppTheme {
                            Surface(
                                modifier = Modifier.fillMaxSize(),
                                color = MaterialTheme.colorScheme.background
                            ) {
                                AnyOtherComposables(
                                  platformContext  // Pass context down to child Composables
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Compose Swift Bridge (CSB)",
                modifier =  Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose Swift Bridge is a tool for Compose Multiplatform mobile application developers. It generates Swift code to delegate Compose function calls to native iOS views. CSB supports both UIKit and SwiftUI view targets.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose Swift Bridge is an experimental tool that helps you define expect Composable functions to be implemented later on the iOS Project using Swift. The tool works by generating Kotlin and Swift code for expected Composable declarations annotated with @ExpectSwiftView, as well as a View factory interface to be implemented by hand in the iOS Project. Also, the tool generates an ObservableObject for each parameter in the Expect Composable function which easily keeps the state parameter updated in the SwiftUI View without any hassle. This is all possible by using SKIE Sub Plugin API and KSP.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //https://github.com/touchlab/compose-swift-bridge/tree/main/sample/multimodule

                    @ExpectSwiftView
                    @Composable
                    expect fun MapView(
                        modifier: Modifier = Modifier,
                        title: String,
                        coordinate: MapCoordinates,
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    annotation class ExpectSwiftView(
                        val factoryName: String = "NativeView", //Used on iOS for the implementing the factory
                        val type: ViewType = ViewType.SwiftUI, //SwiftUI, UIViewController, UIKit.
                        val keepStateCrossNavigation: Boolean = false //Used when your native view (for example your SwiftUI View) has its own state and it should be kept when navigating back.
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "Swift Export",
                modifier =  Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            //https://medium.com/@santimattius/swift-export-natural-interoperability-between-kotlin-and-swift-720799961edc
            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin Multiplatform (KMP) allows code to be shared between different platforms.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "However, interoperability with Swift has presented significant challenges, as Objective-C bridges hinder a truly native experience on iOS.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Swift Export solves this limitation by exporting Kotlin code directly to idiomatic Swift modules and without Objective-C intermediaries.", modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Box(
                modifier = Modifier.fillMaxSize().padding(topBottom = 1.cssRem),
                contentAlignment = Alignment.Center
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Link(
                        path = Kotlin.KotlinNewFeature.NEW_FEATURE_2_2_0_URL,
                        text = "Enable Interoperability Swift Export - Kotlin 2.2.0",
                        modifier = Modifier.margin(right = 4.px)
                    )
                    AppearanceAwareImage(
                        src = Resources.Images.NAVIGATION_ARROW
                    )
                }
            }

            KotlinCode(
                code = """
                    import org.jetbrains.kotlin.gradle.swiftexport.ExperimentalSwiftExportDsl

                    plugins {
                        alias(libs.plugins.kotlinMultiplatform)
                    }

                    kotlin {
                        iosX64()
                        iosArm64()
                        iosSimulatorArm64()

                        @OptIn(ExperimentalSwiftExportDsl::class)
                        swiftExport {
                            // Root module name
                            moduleName = "Shared"

                            // Collapse rule
                            flattenPackage = "com.github.jetbrains.swiftexport"

                            // Export external modules
                            export(projects.moduleA) {
                                // Exported module name
                                moduleName = "ModuleA"
                                // Collapse exported dependency rule
                                flattenPackage = "com.github.jetbrains.modulea"
                            }

                            export(projects.moduleB) {
                                // Exported module name
                                moduleName = "ModuleB"
                                // Collapse exported dependency rule
                                flattenPackage = "com.github.jetbrains.moduleb"
                            }
                        }

                        sourceSets.commonMain.dependencies {
                            api(projects.moduleA)
                            api(projects.moduleB)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Compose Multiplatform inside a Native SwiftUI (UITextField)",
                modifier =  Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //commonMain
                    import androidx.compose.runtime.Composable
                    import androidx.compose.ui.Modifier

                    @Composable
                    expect fun PlatformTextField(
                        value: String,
                        onValueChange: (String) -> Unit,
                        modifier: Modifier
                    )

                    //androidMain
                    @Composable
                    actual fun PlatformTextField(
                        value: String,
                        onValueChange: (String) -> Unit,
                        modifier: Modifier,
                    ) = TextField(value, onValueChange, modifier)

                    //iosMain
                    @Composable
                    actual fun PlatformTextField(
                        value: String,
                        onValueChange: (String) -> Unit,
                        modifier: Modifier,
                    ) = ComposeUITextField(value, onValueChange, modifier)

                    @OptIn(ExperimentalForeignApi::class, BetaInteropApi::class)
                    @Composable
                    fun ComposeUITextField(value: String, onValueChange: (String) -> Unit, modifier: Modifier) {
                        UIKitView(
                            factory = {
                                val textField = object : UITextField(CGRectMake(0.0, 0.0, 0.0, 0.0)) {
                                    @ObjCAction
                                    fun editingChanged() {
                                        onValueChange(text ?: "")
                                    }
                                }
                                textField.addTarget(
                                    target = textField,
                                    action = NSSelectorFromString(textField::editingChanged.name),
                                    forControlEvents = UIControlEventEditingChanged
                                )
                                textField
                            },
                            modifier = modifier,
                            update = { textField ->
                                textField.text = value
                            },
                            onRelease = { textField ->
                                textField.removeTarget(
                                    target = textField,
                                    action = NSSelectorFromString(textField::editingChanged.name),
                                    forControlEvents = UIControlEventEditingChanged
                                )
                            }
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //https://github.com/barissemerci/Translator

                    //commonMain
                    enum class Language(
                        val langCode: String,
                        val langName: String
                    ) {
                        ENGLISH("en", "English"),
                        ARABIC("ar", "Arabic"),
                        AZERBAIJANI("az", "Azerbaijani"),
                        CHINESE("zh", "Chinese"),
                        CZECH("cs", "Czech"),
                        DANISH("da", "Danish"),
                        DUTCH("nl", "Dutch"),
                        FINNISH("fi", "Finnish"),
                        FRENCH("fr", "French"),
                        GERMAN("de", "German"),
                        GREEK("el", "Greek"),
                        HEBREW("he", "Hebrew"),
                        HINDI("hi", "Hindi"),
                        HUNGARIAN("hu", "Hungarian"),
                        INDONESIAN("id", "Indonesian"),
                        IRISH("ga", "Irish"),
                        ITALIAN("it", "Italian"),
                        JAPANESE("ja", "Japanese"),
                        KOREAN("ko", "Korean"),
                        PERSIAN("fa", "Persian"),
                        POLISH("pl", "Polish"),
                        PORTUGUESE("pt", "Portuguese"),
                        RUSSIAN("ru", "Russian"),
                        SLOVAK("sk", "Slovak"),
                        SPANISH("es", "Spanish"),
                        SWEDISH("sv", "Swedish"),
                        TURKISH("tr", "Turkish"),
                        UKRAINIAN("uk", "Ukrainian");

                        companion object {
                            fun byCode(code: String): Language {
                                return entries.find { it.langCode == code }
                                    ?: throw IllegalArgumentException("Invalid or unsupported language code")
                            }
                        }
                    }

                    expect class UiLanguage {
                        val language : Language

                        companion object {
                           fun byCode(langCode: String): UiLanguage
                           val allLanguages : List<UiLanguage>
                        }
                    }

                    //iosMain
                    actual class UiLanguage(
                        actual val language: Language,
                        val imageName: String
                    ) {
                        actual companion object {
                            actual fun byCode(langCode: String): UiLanguage {
                                return allLanguages.find { it.language.langCode == langCode }
                                    ?: throw IllegalArgumentException("Invalid or unsupported language code")
                            }

                            actual val allLanguages: List<UiLanguage>
                                get() = Language.entries.map { language ->
                                    UiLanguage(
                                        language = language,
                                        imageName = language.name.lowercase()
                                    )
                                }
                        }
                    }

                    //androidMain
                    actual class UiLanguage(
                        @DrawableRes val drawableRes: Int,
                        actual val language: Language
                    ) {
                        fun toLocale(): Locale? {
                            return when(language) {
                                Language.ENGLISH -> Locale.ENGLISH
                                Language.CHINESE -> Locale.CHINESE
                                Language.FRENCH -> Locale.FRENCH
                                Language.GERMAN -> Locale.GERMAN
                                Language.ITALIAN -> Locale.ITALIAN
                                Language.JAPANESE -> Locale.JAPANESE
                                Language.KOREAN -> Locale.KOREAN
                                else -> null
                            }
                        }

                        actual companion object {
                            actual fun byCode(langCode: String): UiLanguage {
                                return allLanguages.find { it.language.langCode == langCode }
                                    ?: throw IllegalArgumentException("Invalid or unsupported language code")
                            }

                            actual val allLanguages: List<UiLanguage>
                                get() = Language.entries.map { language ->
                                    UiLanguage(
                                        language = language,
                                        drawableRes = when(language) {
                                            Language.ENGLISH -> R.drawable.english
                                            Language.ARABIC -> R.drawable.arabic
                                            Language.AZERBAIJANI -> R.drawable.azerbaijani
                                            Language.CHINESE -> R.drawable.chinese
                                            Language.CZECH -> R.drawable.czech
                                            Language.DANISH -> R.drawable.danish
                                            Language.DUTCH -> R.drawable.dutch
                                            Language.FINNISH -> R.drawable.finnish
                                            Language.FRENCH -> R.drawable.french
                                            Language.GERMAN -> R.drawable.german
                                            Language.GREEK -> R.drawable.greek
                                            Language.HEBREW -> R.drawable.hebrew
                                            Language.HINDI -> R.drawable.hindi
                                            Language.HUNGARIAN -> R.drawable.hungarian
                                            Language.INDONESIAN -> R.drawable.indonesian
                                            Language.IRISH -> R.drawable.irish
                                            Language.ITALIAN -> R.drawable.italian
                                            Language.JAPANESE -> R.drawable.japanese
                                            Language.KOREAN -> R.drawable.korean
                                            Language.PERSIAN -> R.drawable.persian
                                            Language.POLISH -> R.drawable.polish
                                            Language.PORTUGUESE -> R.drawable.portuguese
                                            Language.RUSSIAN -> R.drawable.russian
                                            Language.SLOVAK -> R.drawable.slovak
                                            Language.SPANISH -> R.drawable.spanish
                                            Language.SWEDISH -> R.drawable.swedish
                                            Language.TURKISH -> R.drawable.turkish
                                            Language.UKRAINIAN -> R.drawable.ukrainian
                                        }
                                    )
                                }.sortedBy { it.language.langName }
                        }
                    }
                    """.trimIndent()
            )


            SpanText(
                text = "Context pass android different way",
                modifier =  Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //Android

                    import android.content.Context
                    import androidx.startup.Initializer

                    internal lateinit var applicationContext: Context
                        private set

                    object KSecureStorageContext

                    @Suppress("unused")
                    class KSecureStorageInitializer: Initializer<KSecureStorageContext> {
                        override fun create(context: Context): KSecureStorageContext {
                            applicationContext = context.applicationContext
                            return KSecureStorageContext
                        }

                        override fun dependencies(): List<Class<out Initializer<*>>> {
                            return emptyList()
                        }
                    }

                    import android.content.Context
                    import android.content.SharedPreferences
                    import android.util.Log
                    import androidx.annotation.VisibleForTesting
                    import androidx.security.crypto.EncryptedSharedPreferences
                    import androidx.security.crypto.MasterKey

                    private const val SHARED_PREFERENCES_FILENAME = "K_SECURE_STORAGE_ENCRYPTED_STORAGE_SHARED_PREF"

                    @Suppress("unused")
                    actual class KSecureStorage : IKSecureStorage {

                        private var sharedPreferences: SharedPreferences? = null

                        actual constructor() : this(applicationContext)

                        @VisibleForTesting(otherwise = VisibleForTesting.PRIVATE)
                        constructor(context: Context) {
                            try {
                                val key = MasterKey.Builder(context)
                                    .setKeyScheme(MasterKey.KeyScheme.AES256_GCM)
                                    .build()
                                sharedPreferences = EncryptedSharedPreferences.create(
                                    context,
                                    SHARED_PREFERENCES_FILENAME,
                                    key,
                                    EncryptedSharedPreferences.PrefKeyEncryptionScheme.AES256_SIV,
                                    EncryptedSharedPreferences.PrefValueEncryptionScheme.AES256_GCM
                                )
                            } catch (ex: Exception) {
                                Log.e(
                                    KSecureStorage::class.simpleName,
                                    "Failed to create encrypted shared preferences! Failing back to standard SharedPreferences",
                                    ex
                                )
                                sharedPreferences = context.getSharedPreferences(
                                    SHARED_PREFERENCES_FILENAME,
                                    Context.MODE_PRIVATE
                                )
                            }
                        }

                        private fun getSharedPreferences(): SharedPreferences {
                            sharedPreferences?.let {
                                return it
                            } ?: run {
                                throw NullPointerException("Could not initialize SharedPreferences")
                            }
                        }

                        override fun setItem(key: String, value: String?) {
                            val editor = getSharedPreferences().edit()
                            editor.putString(key, value)
                            val saved = editor.commit()
                            if (!saved) {
                                throw Exception("An error occurred while saving")
                            }
                        }

                        override fun getItem(key: String): String? {
                            return getSharedPreferences().getString(key, null)
                        }

                        override fun removeItem(key: String) {
                            val editor = getSharedPreferences().edit()
                            editor.remove(key)
                            val saved = editor.commit()
                            if (!saved) {
                                throw Exception("An error occurred while removing")
                            }
                        }

                        override fun clear() {
                            val editor = getSharedPreferences().edit()
                            editor.clear()
                            val saved = editor.commit()
                            if (!saved) {
                                throw Exception("An error occurred while clearing SharedPreferences")
                            }
                        }
                    }

                    <manifest xmlns:android="http://schemas.android.com/apk/res/android"
                        xmlns:tools="http://schemas.android.com/tools">

                        <application>
                            <provider
                                android:name="androidx.startup.InitializationProvider"
                                android:authorities="dollarSignAdd{applicationId}.androidx-startup"
                                android:exported="false"
                                tools:node="merge">

                                <meta-data
                                    android:name=".KSecureStorageInitializer"
                                    android:value="androidx.startup" />
                            </provider>
                        </application>

                    </manifest>
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //common
                    interface IKSecureStorage {
                        /**
                         * Writes data to the disk, using SharedPreferences or KeyChain, depending on the platform.
                         *
                         * @param key A string that will be associated to the value for later retrieval.
                         * @param value The data to store.
                         */
                        fun setItem(key: String, value: String?)

                        /**
                         * Retrieves data from the disk, using SharedPreferences or KeyChain, depending on the platform
                         * and returns it as the specified type.
                         *
                         * @param key String value that is associated to a value.
                         * @return A string value if the key/value pair exists, otherwise null.
                         */
                        fun getItem(key: String): String?

                        /**
                         * Deletes data from the disk, using SharedPreferences or KeyChain, depending on the platform.
                         * @param key String value that is associated to a value.
                         */
                        fun removeItem(key: String)

                        /**
                         * Clears all data from disk, using SharedPreferences or KeyChain, depending on the platform.
                         */
                        fun clear()
                    }

                    @Suppress("EXPECT_ACTUAL_CLASSIFIERS_ARE_IN_BETA_WARNING")
                    expect class KSecureStorage() : IKSecureStorage

                    /**
                     * Retrieves data from the disk, using SharedPreferences or KeyChain, depending on the platform
                     * and returns a boolean if the key/value pair exists.
                     *
                     * @param key String value that is associated to a value.
                     * @return True if the key/value pair exists, otherwise false.
                     */
                    fun KSecureStorage.hasItem(key: String): Boolean {
                        return getItem(key) != null
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Platform context",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    expect class NfcManager(platformContext: PlatformContext) {

                        val flow: SharedFlow<String>

                        @Composable
                        fun RegisterForNFC()
                    }

                    //ui
                    val nfcManager = NfcManager(getPlatformContext())

                    //iosMain
                    import androidx.compose.runtime.Composable

                    actual class PlatformContext

                    @Composable
                    actual fun getPlatformContext(): PlatformContext {
                        return PlatformContext()
                    }

                    //androidMain
                    expect class PlatformContext

                    @Composable
                    expect fun getPlatformContext(): PlatformContext
                    """.trimIndent()
            )

            SpanText(
                text = "Coroutines context",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    import kotlinx.coroutines.CoroutineDispatcher
                    import kotlinx.coroutines.Dispatchers

                    interface PokedexDispatchers {
                        val main: CoroutineDispatcher
                        val default: CoroutineDispatcher
                        val io: CoroutineDispatcher
                        val unconfined: CoroutineDispatcher
                    }

                    internal fun PokedexDispatchers(): PokedexDispatchers = object : PokedexDispatchers {
                        override val main: CoroutineDispatcher = Dispatchers.Main
                        override val default: CoroutineDispatcher = Dispatchers.Default
                        override val io: CoroutineDispatcher = Dispatchers.IO
                        override val unconfined: CoroutineDispatcher = Dispatchers.Unconfined
                    }

                    import org.koin.core.module.dsl.singleOf
                    import org.koin.dsl.module

                    val CoroutineDispatcherModule = module {
                        singleOf(::PokedexDispatchers)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @OptIn(ExperimentalContracts::class)
                    object Coroutines {
                        suspend inline fun <T> main(noinline block: suspend CoroutineScope.() -> T): T {
                            contract {
                                callsInPlace(block, InvocationKind.EXACTLY_ONCE)
                            }
                            return withContext(mainContext, block)
                        }

                        suspend inline fun <T> cpu(noinline block: suspend CoroutineScope.() -> T): T {
                            contract {
                                callsInPlace(block, InvocationKind.EXACTLY_ONCE)
                            }
                            return withContext(cpuContext, block)
                        }

                        suspend inline fun <T> io(noinline block: suspend CoroutineScope.() -> T): T {
                            contract {
                                callsInPlace(block, InvocationKind.EXACTLY_ONCE)
                            }
                            return withContext(ioContext, block)
                        }

                        suspend inline fun <T> wait(noinline block: suspend CoroutineScope.() -> T): T {
                            contract {
                                callsInPlace(block, InvocationKind.EXACTLY_ONCE)
                            }
                            return withContext(waitContext, block)
                        }

                        suspend inline fun <T> timeout(limit: Int, noinline block: suspend CoroutineScope.() -> T): T {
                            contract {
                                callsInPlace(block, InvocationKind.EXACTLY_ONCE)
                            }
                            return withTimeout(limit.toLong(), block)
                        }

                        fun startMain(block: suspend CoroutineScope.() -> Unit): Job = CoroutineScope(mainContext).launch(block = block)
                        fun startCPU(block: suspend CoroutineScope.() -> Unit): Job = CoroutineScope(cpuContext).launch(block = block)
                        fun startIO(block: suspend CoroutineScope.() -> Unit): Job = CoroutineScope(ioContext).launch(block = block)
                        fun startWait(block: suspend CoroutineScope.() -> Unit): Job = CoroutineScope(waitContext).launch(block = block)
                    }

                    inline fun <T> Continuation<T?>.safeResume(crossinline block: () -> Unit) {
                        try {
                            block()
                        }
                        catch (_: Throwable) {
                            this.resume(null)
                        }
                    }

                    expect val mainContext: CoroutineContext
                    expect val cpuContext: CoroutineContext
                    expect val ioContext: CoroutineContext
                    expect val waitContext: CoroutineContext

                    //iosMain
                    import kotlinx.coroutines.Dispatchers
                    import kotlinx.coroutines.NonCancellable
                    import kotlin.coroutines.CoroutineContext

                    actual val mainContext: CoroutineContext get() = Dispatchers.Main
                    actual val cpuContext: CoroutineContext get() = Dispatchers.Default
                    actual val ioContext: CoroutineContext get() = Dispatchers.Default
                    actual val waitContext: CoroutineContext get() = NonCancellable
                    """.trimIndent()
            )

            //https://github.com/siarhei-luskanau/pixabayeye
            KotlinCode(
                code = """
                        //KoinCommon.kt
                        fun allModules(appModule: Module): List<Module> = listOf(
                            appModule
                        )
                        expect val appPlatformModule: Module

                        //Access DispatcherSet
                        val dispatcherSet: DispatcherSet = get()

                        @Module
                        @ComponentScan(value = ["com.multiplatform.core.common"])
                        class CoreCommonModule

                        interface DispatcherSet {
                            fun defaultDispatcher(): CoroutineDispatcher
                            fun ioDispatcher(): CoroutineDispatcher
                            fun mainDispatcher(): CoroutineDispatcher
                            fun <T> runBlocking(context: CoroutineContext, block: suspend CoroutineScope.() -> T): T
                        }

                        //AndroidMain
                        @Single
                        internal class DispatcherSetAndroid : DispatcherSet {
                            override fun defaultDispatcher() = Dispatchers.Default
                            override fun ioDispatcher() = Dispatchers.IO
                            override fun mainDispatcher() = Dispatchers.Main
                            override fun <T> runBlocking(
                                context: CoroutineContext,
                                block: suspend CoroutineScope.() -> T
                            ): T = kotlinx.coroutines.runBlocking(
                                context = context,
                                block = block
                            )
                        }

                        //iosMain
                        @Single
                        internal class DispatcherSetIos : DispatcherSet {
                            override fun defaultDispatcher() = Dispatchers.Default
                            override fun ioDispatcher() = Dispatchers.IO
                            override fun mainDispatcher() = Dispatchers.Main
                            override fun <T> runBlocking(
                                context: CoroutineContext,
                                block: suspend CoroutineScope.() -> T
                            ): T = kotlinx.coroutines.runBlocking(
                                context = context,
                                block = block
                            )
                        }

                        //JsMain
                        @Single
                        internal class DispatcherSetJs : DispatcherSet {
                            override fun defaultDispatcher() = Dispatchers.Default
                            override fun ioDispatcher() = Dispatchers.Default
                            override fun mainDispatcher() = Dispatchers.Main
                            override fun <T> runBlocking(
                                context: CoroutineContext,
                                block: suspend CoroutineScope.() -> T
                            ): T =
                                @OptIn(DelicateCoroutinesApi::class)
                                GlobalScope.promise(
                                    context = context,
                                    block = block
                                ).unsafeCast<T>()
                        }

                        //jvmMain
                        @Single
                        internal class DispatcherSetJvm : DispatcherSet {
                            override fun defaultDispatcher() = Dispatchers.Default
                            override fun ioDispatcher() = Dispatchers.IO
                            override fun mainDispatcher() = Dispatchers.Main
                            override fun <T> runBlocking(
                                context: CoroutineContext,
                                block: suspend CoroutineScope.() -> T
                            ): T = kotlinx.coroutines.runBlocking(
                                context = context,
                                block = block
                            )
                        }
                    """.trimIndent()
            )

            SpanText(
                text = "Screen width",
                modifier =  Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Composable
                    expect fun getScreenWidth(): Dp

                    @Composable
                    expect fun getScreenHeight(): Dp

                    //iosMain
                    @OptIn(ExperimentalComposeUiApi::class)
                    @Composable
                    actual fun getScreenWidth(): Dp = LocalWindowInfo.current
                        .containerSize
                        .width
                        .dp


                    @OptIn(ExperimentalComposeUiApi::class)
                    @Composable
                    actual fun getScreenHeight(): Dp = LocalWindowInfo.current
                        .containerSize
                        .height
                        .dp

                     //androidMain
                     @Composable
                     actual fun getScreenWidth() = LocalConfiguration.current
                        .screenWidthDp
                        .dp

                     @Composable
                     actual fun getScreenHeight() = LocalConfiguration.current
                        .screenHeightDp
                        .dp
                    """.trimIndent()
            )

            SpanText(
                text = "Image load",
                modifier =  Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = $$"""
                    import androidx.compose.runtime.Composable
                    import androidx.compose.ui.Modifier
                    import androidx.compose.ui.layout.ContentScale

                    /**
                        * Loads an image asynchronously from a URL.
                        * Each platform (Android, iOS) has its own implementation.
                        * Shows a loading indicator while the image is loading.
                    */

                    @Composable
                    expect fun AsyncImage(
                        url: String,
                        contentDescription: String?,
                        modifier: Modifier = Modifier,
                        contentScale: ContentScale = ContentScale.Fit
                    )

                    //iosMain
                    import androidx.compose.foundation.Image
                    import androidx.compose.foundation.layout.Box
                    import androidx.compose.foundation.layout.fillMaxSize
                    import androidx.compose.material3.CircularProgressIndicator
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.LaunchedEffect
                    import androidx.compose.runtime.getValue
                    import androidx.compose.runtime.mutableStateOf
                    import androidx.compose.runtime.remember
                    import androidx.compose.runtime.setValue
                    import androidx.compose.ui.Alignment
                    import androidx.compose.ui.Modifier
                    import androidx.compose.ui.graphics.ImageBitmap
                    import androidx.compose.ui.graphics.painter.BitmapPainter
                    import androidx.compose.ui.graphics.toComposeImageBitmap
                    import androidx.compose.ui.layout.ContentScale
                    import io.ktor.client.HttpClient
                    import io.ktor.client.call.body
                    import io.ktor.client.engine.darwin.Darwin
                    import io.ktor.client.request.get
                    import kotlinx.coroutines.Dispatchers
                    import kotlinx.coroutines.withContext
                    import org.jetbrains.skia.Image
                    import kotlin.let

                    @Composable
                    actual fun AsyncImage(
                        url: String,
                        contentDescription: String?,
                        modifier: Modifier,
                        contentScale: ContentScale
                    ) {
                        var imageBitmap by remember { mutableStateOf<ImageBitmap?>(null) }
                        var isLoading by remember { mutableStateOf(true) }

                        LaunchedEffect(url) {
                            isLoading = true
                            imageBitmap = loadImageWithKtor(url)
                            isLoading = false
                        }

                        Box(modifier = modifier, contentAlignment = Alignment.Center) {
                            if (isLoading) {
                                CircularProgressIndicator()
                            }

                            imageBitmap?.let {
                                Image(
                                    painter = BitmapPainter(it),
                                    contentDescription = contentDescription,
                                    modifier = Modifier.fillMaxSize(),
                                    contentScale = contentScale
                                )
                            }
                        }
                    }

                    // Ktor-based image loader
                    suspend fun loadImageWithKtor(url: String): ImageBitmap? = withContext(Dispatchers.Default) {
                        try {
                            val client = HttpClient(Darwin)
                            val bytes: ByteArray = client.get(url).body()
                            val skiaImage = Image.makeFromEncoded(bytes)
                            skiaImage.toComposeImageBitmap()
                        } catch (e: Exception) {
                            println("Error loading image from $url: $e")
                            null
                        }
                    }

                    //androidMain
                    import androidx.compose.foundation.layout.Box
                    import androidx.compose.foundation.layout.fillMaxSize
                    import androidx.compose.material3.CircularProgressIndicator
                    import androidx.compose.runtime.Composable
                    import androidx.compose.ui.Alignment
                    import androidx.compose.ui.Modifier
                    import androidx.compose.ui.layout.ContentScale
                    import coil.compose.SubcomposeAsyncImage
                    import coil.compose.SubcomposeAsyncImageContent

                    @Composable
                    actual fun AsyncImage(
                        url: String,
                        contentDescription: String?,
                        modifier: Modifier,
                        contentScale: ContentScale
                    ) {
                        SubcomposeAsyncImage(
                            model = url,
                            contentDescription = contentDescription,
                            modifier = modifier,
                            contentScale = contentScale,
                            loading = {
                                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                                    CircularProgressIndicator()
                                }
                            },
                            success = {
                                SubcomposeAsyncImageContent()
                            }
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Open URL",
                modifier =  Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                         //interface
                         interface WebPageRouter{
                             fun openUrl(url :String)
                         }
                         expect fun openWebPage() : WebPageRouter
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         //ios
                         import platform.Foundation.NSURL
                         import platform.UIKit.UIApplication

                         class IOSWebPageRouter : WebPageRouter {
                             override fun openUrl(url: String) {
                                UIApplication.sharedApplication().openURL(NSURL(string = url))
                             }
                         }

                         actual fun openWebPage() : WebPageRouter = IOSWebPageRouter()
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                         //android
                         import android.content.Intent
                         import android.net.Uri

                         class AndroidWebPageRouter : WebPageRouter {
                             override fun openUrl(url: String) {
                                 val intent = Intent(Intent.ACTION_VIEW).apply {
                                     addFlags(Intent.FLAG_ACTIVITY_NEW_TASK)
                                     data = Uri.parse(url)
                                 }
                                 KoinManager().getKoinInstance().get<Context>().apply {
                                     startActivity(intent)
                                 }
                             }
                         }

                         actual fun openWebPage() : WebPageRouter = AndroidWebPageRouter()
                    """.trimIndent()
            )

            //https://github.com/RcuDev/StarGazer
            KotlinCode(
                code = """
                    import androidx.compose.runtime.Composable

                    @Composable
                    expect fun WebViewRoute(
                        url: String,
                        notAuthorizedHost: () -> Unit
                    )

                    //iosMain
                    import androidx.compose.foundation.layout.fillMaxSize
                    import androidx.compose.runtime.Composable
                    import androidx.compose.ui.ExperimentalComposeUiApi
                    import androidx.compose.ui.Modifier
                    import androidx.compose.ui.viewinterop.UIKitView
                    import androidx.compose.ui.viewinterop.UIKitInteropInteractionMode
                    import androidx.compose.ui.viewinterop.UIKitInteropProperties
                    import io.ktor.http.Url
                    import kotlinx.cinterop.ExperimentalForeignApi
                    import kotlinx.cinterop.readValue
                    import platform.CoreGraphics.CGRectZero
                    import platform.Foundation.NSURL
                    import platform.Foundation.NSURLRequest
                    import platform.WebKit.*
                    import platform.WebKit.WKWebView
                    import platform.WebKit.WKWebViewConfiguration
                    import platform.WebKit.javaScriptEnabled
                    import platform.darwin.NSObject

                    @OptIn(ExperimentalForeignApi::class, ExperimentalComposeUiApi::class)
                    @Composable
                    actual fun WebViewRoute(
                        url: String,
                        notAuthorizedHost: () -> Unit
                    ) {
                        if (url.isEmpty()) {
                            EmptyWebView()
                            return
                        }

                        UIKitView(
                            factory = {
                                val configuration = WKWebViewConfiguration().apply {
                                    preferences.apply {
                                        javaScriptEnabled = true
                                    }
                                    allowsInlineMediaPlayback = true
                                }
                                WKWebView(frame = CGRectZero.readValue(), configuration = configuration).apply {
                                    this.navigationDelegate = SGWebViewDelegate(Url(url), notAuthorizedHost)
                                    this.loadRequest(NSURLRequest(NSURL(string = url)))
                                }
                            },
                            update = { webView ->
                                if (webView.URL?.absoluteString != url) {
                                    webView.loadRequest(NSURLRequest(NSURL(string = url)))
                                }
                            },
                            properties = UIKitInteropProperties(
                                interactionMode = UIKitInteropInteractionMode.NonCooperative,
                                isNativeAccessibilityEnabled = true,
                            ),
                            modifier = Modifier.fillMaxSize()
                        )
                    }

                    class SGWebViewDelegate(private val url: Url, val notAuthorizedHost: () -> Unit) : NSObject(), WKNavigationDelegateProtocol {
                        override fun webView(
                            webView: WKWebView,
                            decidePolicyForNavigationAction: WKNavigationAction,
                            decisionHandler: (WKNavigationActionPolicy) -> Unit
                        ) {
                            val navigationType = decidePolicyForNavigationAction.navigationType
                            val webUrl = Url(decidePolicyForNavigationAction.request.URL?.absoluteString.orEmpty())
                            when(navigationType){
                                WKNavigationTypeLinkActivated -> {
                                    if (webUrl.host != url.host) {
                                        decisionHandler(WKNavigationActionPolicy.WKNavigationActionPolicyCancel)
                                    } else {
                                        notAuthorizedHost()
                                        decisionHandler(WKNavigationActionPolicy.WKNavigationActionPolicyAllow)
                                    }
                                }
                                else -> decisionHandler(WKNavigationActionPolicy.WKNavigationActionPolicyAllow)
                            }
                        }
                    }

                    //androidMain
                    import android.annotation.SuppressLint
                    import android.view.View
                    import android.view.ViewGroup
                    import android.webkit.WebResourceRequest
                    import android.webkit.WebSettings
                    import android.webkit.WebView
                    import android.webkit.WebViewClient
                    import androidx.compose.foundation.layout.fillMaxSize
                    import androidx.compose.runtime.Composable
                    import androidx.compose.ui.Modifier
                    import androidx.compose.ui.viewinterop.AndroidView
                    import io.ktor.http.*

                    @SuppressLint("SetJavaScriptEnabled")
                    @Composable
                    actual fun WebViewRoute(
                        url: String,
                        notAuthorizedHost: () -> Unit
                    ) {
                        if (url.isEmpty()) {
                            EmptyWebView()
                            return
                        }

                        AndroidView(
                            factory = {
                                WebView(it).apply {
                                    scrollBarStyle = View.SCROLLBARS_OUTSIDE_OVERLAY
                                    layoutParams = ViewGroup.LayoutParams(
                                        ViewGroup.LayoutParams.MATCH_PARENT,
                                        ViewGroup.LayoutParams.MATCH_PARENT
                                    )
                                    settings.apply {
                                        javaScriptEnabled = true
                                        loadWithOverviewMode = true
                                        useWideViewPort = true
                                        layoutAlgorithm = WebSettings.LayoutAlgorithm.NORMAL
                                    }
                                    webViewClient = SGWebViewClient(Url(url), notAuthorizedHost)
                                }
                            },
                            update = {
                                it.loadUrl(url)
                            },
                            modifier = Modifier
                                .fillMaxSize()
                        )
                    }

                    private class SGWebViewClient(val url: Url, val notAuthorizedHost: () -> Unit) : WebViewClient() {
                        override fun shouldOverrideUrlLoading(view: WebView?, request: WebResourceRequest?): Boolean {
                            val webUrl = Url(request?.url.toString())
                            if (webUrl.host != url.host) notAuthorizedHost()
                            return webUrl.host != url.host
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Sensor manager continues get data using channel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                         //sensor continues data get
                         expect class SensorDataManager() {
                             val data: Channel<SensorData>
                             fun initSensors()
                             fun cancelSensorUpdates()
                         }

                         data class SensorData(
                             val roll: Double,
                             val pitch: Double
                         )

                         //iosMain
                         import kotlinx.cinterop.ExperimentalForeignApi
                         import kotlinx.cinterop.useContents
                         import kotlinx.coroutines.channels.Channel
                         import platform.CoreMotion.CMAcceleration
                         import platform.CoreMotion.CMMagneticField
                         import platform.CoreMotion.CMMotionManager
                         import platform.Foundation.NSLog
                         import platform.Foundation.NSOperationQueue.Companion.mainQueue

                         actual class SensorDataManager {
                             private val motionManager = CMMotionManager()

                             init {
                                 initSensors()
                             }

                             actual val data: Channel<SensorData> = Channel(Channel.UNLIMITED)

                             actual fun initSensors() {
                                 if (motionManager.isDeviceMotionAvailable()) {
                                     motionManager.deviceMotionUpdateInterval = 1.0 / 60.0 // Update frequency
                                     motionManager.startDeviceMotionUpdatesToQueue(mainQueue) { motion, error ->
                                         motion?.let { nonNullMotion ->

                                             val attitude = nonNullMotion.attitude

                                             // Convert radians to degrees
                                             val rollDegrees = (attitude.roll * 180) / 2.14
                                             val pitchDegrees = (attitude.pitch * 180) / 2.14

                                             data.trySend(
                                                 SensorData(
                                                     roll = rollDegrees,
                                                     pitch = pitchDegrees
                                                 )
                                             )
                                         } ?: run {
                                             // Handle the case where motion is null if necessary
                                             NSLog("Motion is null")
                                             println("Motion is null")
                                         }
                                     }
                                 }
                             }


                             actual fun cancelSensorUpdates() {
                                 motionManager.stopDeviceMotionUpdates()
                             }
                         }

                         //androidMain
                         import android.content.Context.SENSOR_SERVICE
                         import android.hardware.Sensor
                         import android.hardware.SensorEvent
                         import android.hardware.SensorEventListener
                         import android.hardware.SensorManager
                         import android.util.Log
                         import kotlinx.coroutines.channels.Channel

                         actual class SensorDataManager : SensorEventListener {

                             private val sensorManager by lazy {
                                 AndroidApp.INSTANCE.getSystemService(SENSOR_SERVICE) as SensorManager
                             }

                             init {
                                 initSensors()
                             }

                             actual fun initSensors() {
                                 Log.d("SensorDataManager", "init")
                                 val accelerometer = sensorManager.getDefaultSensor(Sensor.TYPE_GRAVITY)
                                 val magnetometer = sensorManager.getDefaultSensor(Sensor.TYPE_MAGNETIC_FIELD)

                                 sensorManager.registerListener(this, accelerometer, SensorManager.SENSOR_DELAY_UI)
                                 sensorManager.registerListener(this, magnetometer, SensorManager.SENSOR_DELAY_UI)
                             }

                             private var gravity: FloatArray? = null
                             private var geomagnetic: FloatArray? = null

                             actual val data: Channel<SensorData> = Channel(Channel.UNLIMITED)

                             override fun onSensorChanged(event: SensorEvent?) {
                                 if (event?.sensor?.type == Sensor.TYPE_GRAVITY)
                                     gravity = event.values

                                 if (event?.sensor?.type == Sensor.TYPE_MAGNETIC_FIELD)
                                     geomagnetic = event.values

                                 if (gravity != null && geomagnetic != null) {
                                     val r = FloatArray(9)
                                     val i = FloatArray(9)

                                     if (SensorManager.getRotationMatrix(r, i, gravity, geomagnetic)) {
                                         val orientation = FloatArray(3)
                                         SensorManager.getOrientation(r, orientation)

                                         data.trySend(
                                             SensorData(
                                                 roll = orientation[2] * 180 / Math.PI,
                                                 pitch = orientation[1] * 180 / Math.PI,
                                             )
                                         )
                                     }
                                 }
                             }

                             actual fun cancelSensorUpdates() {
                                 Log.d("SensorDataManager", "cancel")
                                 sensorManager.unregisterListener(this)
                             }

                             override fun onAccuracyChanged(sensor: Sensor?, accuracy: Int) {}
                         }

                         //UI
                         val colorsList = listOf(
                             Color(0xFFF4143C),
                             Color(0xFFEA8CA5),
                             Color(0xFF251473),
                             Color(0xFF726CA0),
                             Color(0xFFD0ADB4),
                             Color(0xFF544C94)
                         )

                         @Composable
                         fun App() = AppTheme {
                             val dataManager = remember { SensorDataManager() }

                             Box(modifier = Modifier.fillMaxSize()) {
                                 DisposableEffect(Unit) {
                                     onDispose {
                                         dataManager.cancelSensorUpdates()
                                     }
                                 }

                                 val data by dataManager.data.receiveAsFlow().collectAsState(null)

                                 // Glowing Sun remains stationary
                                 GlowingSun()

                                 data?.let { sensorData ->
                                     DarkSun(
                                         modifier = Modifier
                                             .offset(x = (sensorData.roll).dp, y = (-sensorData.pitch).dp)

                                     )
                                 }
                             }
                         }

                         @Composable
                         fun GlowingSun(modifier: Modifier = Modifier) {
                             Box(
                                 modifier = Modifier
                                     .fillMaxSize()
                                     .background(Color.Black)
                             ) {
                                 Canvas(modifier = modifier.fillMaxSize()) {
                                     val centerX = size.width / 2
                                     val centerY = size.height / 2
                                     val sunRadius = size.minDimension / 8
                                     val gradientRadius = sunRadius * 3


                                     val glowPaint = Paint().asFrameworkPaint().apply {
                                         isAntiAlias = true
                                         shader = RadialGradientShader(
                                             center = Offset(centerX, centerY),
                                             radius = gradientRadius,
                                             colors = listOf(Color(0xFFFFD700), Color.Transparent),
                                             tileMode = TileMode.Clamp
                                         )
                                     }

                                     // Draw the glow around the sun
                                     drawIntoCanvas { canvas ->
                                         canvas.drawCircle(
                                             Offset(centerX, centerY),
                                             gradientRadius,
                                             glowPaint.asComposePaint()
                                         )
                                     }

                                     val sunPaint = Paint().apply {
                                         color = Color.Yellow
                                     }

                                     // Draw the sun's core
                                     drawIntoCanvas {
                                         it.drawCircle(
                                             center = Offset(centerX, centerY),
                                             radius = sunRadius,
                                             sunPaint
                                         )
                                     }
                                 }
                             }
                         }

                         @Composable
                         fun DarkSun(modifier: Modifier = Modifier) {
                             Box(
                                 modifier = Modifier
                                     .fillMaxSize()
                                     .background(Color.Transparent)
                             ) {
                                 Canvas(modifier = modifier.fillMaxSize()) {
                                     val centerX = size.width / 2
                                     val centerY = size.height / 2
                                     val sunRadius = size.minDimension / 8
                                     val gradientRadius = sunRadius * 3


                                     val glowPaint = Paint().asFrameworkPaint().apply {
                                         isAntiAlias = true
                                         shader = RadialGradientShader(
                                             center = Offset(centerX, centerY),
                                             radius = gradientRadius,
                                             colors = listOf(Color.Black, Color.Transparent),
                                             tileMode = TileMode.Clamp
                                         )
                                     }

                                     drawIntoCanvas { canvas ->
                                         canvas.drawCircle(
                                             Offset(centerX, centerY),
                                             gradientRadius,
                                             glowPaint.asComposePaint()
                                         )
                                     }

                                     val sunPaint = Paint().apply {
                                         color = Color.Black
                                     }

                                     drawIntoCanvas {
                                         it.drawCircle(
                                             center = Offset(centerX, centerY),
                                             radius = sunRadius,
                                             sunPaint
                                         )
                                     }
                                 }
                             }
                         }
                    """.trimIndent()
            )

            SpanText(
                text = "SensorManager Coroutines",
                modifier =  Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    expect class SensorManager {
                        fun registerListener(listener: Listener)
                    }

                    interface Listener {
                        fun onUpdate(sensorData: SensorData)
                    }

                    data class SensorData(
                        val roll: Float,
                        val pitch: Float
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //iosMain
                    import kotlinx.coroutines.channels.Channel
                    import platform.CoreMotion.CMMotionManager
                    import platform.Foundation.NSOperationQueue.Companion.currentQueue

                    class SensorDataManager {
                        var motion = CMMotionManager()
                        val data = Channel<SensorData>(Channel.UNLIMITED)

                        fun startGyros() {
                            if (motion.isGyroAvailable()) {
                                motion.apply {
                                    gyroUpdateInterval = 1.0 / 50.0
                                    startGyroUpdates()
                                    startDeviceMotionUpdatesToQueue(currentQueue!!) { motion, error ->
                                        if (motion != null) {
                                            val attitude = motion.attitude
                                            data.trySend(
                                                SensorData(
                                                    roll = attitude.roll.toFloat(),
                                                    pitch = attitude.pitch.toFloat()
                                                )
                                            )
                                        }
                                    }
                                }
                            }
                        }

                        fun stopGyros(){
                            motion.stopGyroUpdates()
                        }
                    }

                    fun MainViewController() = ComposeUIViewController {
                        val sensorManager = SensorManager()
                        val scope = rememberCoroutineScope()

                        DisposableEffect(Unit) {
                            val dataManager = SensorDataManager()
                            dataManager.startGyros()

                            val job = scope.launch {
                                dataManager.data
                                    .receiveAsFlow()
                                    .onEach { sensorManager.listener?.onUpdate(it) }
                                    .collect()
                            }

                            onDispose {
                                job.cancel()
                                dataManager.stopGyros()
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //androidMain
                    actual class SensorManager {

                        var listener: Listener? = null
                        actual fun registerListener(listener: Listener) {
                            this.listener = listener
                        }
                    }

                    import android.content.Context
                    import android.hardware.Sensor
                    import android.hardware.SensorEvent
                    import android.hardware.SensorEventListener
                    import android.hardware.SensorManager
                    import kotlinx.coroutines.channels.Channel
                    import kotlin.math.PI

                    class SensorDataManager(
                        context: Context,
                    ) : SensorEventListener {

                        private val sensorManager by lazy { context.getSystemService(Context.SENSOR_SERVICE) as SensorManager }
                        private var gravity: FloatArray? = null
                        private var geomagnetic: FloatArray? = null
                        val data: Channel<SensorData> = Channel(Channel.UNLIMITED)

                        fun init() {
                            val accelerometer = sensorManager.getDefaultSensor(Sensor.TYPE_GRAVITY)
                            val magnetometer = sensorManager.getDefaultSensor(Sensor.TYPE_MAGNETIC_FIELD)

                            sensorManager.registerListener(this, accelerometer, SensorManager.SENSOR_DELAY_UI)
                            sensorManager.registerListener(this, magnetometer, SensorManager.SENSOR_DELAY_UI)
                        }

                        override fun onSensorChanged(event: SensorEvent?) {
                            if (event?.sensor?.type == Sensor.TYPE_GRAVITY) {
                                gravity = event.values
                            }

                            if (event?.sensor?.type == Sensor.TYPE_MAGNETIC_FIELD) {
                                geomagnetic = event.values
                            }

                            if (gravity != null && geomagnetic != null) {
                                val r = FloatArray(9)
                                val i = FloatArray(9)

                                if (SensorManager.getRotationMatrix(r, i, gravity, geomagnetic)) {
                                    val orientation = FloatArray(3)
                                    SensorManager.getOrientation(r, orientation)
                                    val adjustedPitch = orientation[1] - (PI.toFloat() / 2)

                                    data.trySend(
                                        SensorData(
                                            roll = orientation[2],
                                            pitch = orientation[1]
                                        )
                                    )
                                }
                            }
                        }

                        fun cancel() {
                            sensorManager.unregisterListener(this)
                        }

                        override fun onAccuracyChanged(p0: Sensor?, p1: Int) {}
                    }

                    @Composable
                    fun MainView() {
                        val sensorManager = SensorManager()
                        val context = LocalContext.current
                        val scope = rememberCoroutineScope()

                        DisposableEffect(Unit) {
                            val dataManager = SensorDataManager(context)
                            dataManager.init()

                            val job = scope.launch {
                                dataManager.data
                                    .receiveAsFlow()
                                    .onEach { sensorManager.listener?.onUpdate(it) }
                                    .collect()
                            }

                            onDispose {
                                dataManager.cancel()
                                job.cancel()
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Device information",
                modifier =  Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                         expect class DeviceInfoXState() {
                             val isAndroid: Boolean
                             val androidInfo: AndroidInfo
                             val isIos: Boolean
                             val iosInfo: IosInfo
                         }

                         @Composable
                         expect fun rememberDeviceInfoXState(): DeviceInfoXState

                         //interface
                         interface AndroidInfo {
                             val appName: String
                         }

                         interface IosInfo {
                             val name: String
                         }

                         //UI
                         val deviceInfoXState: DeviceInfoXState = rememberDeviceInfoXState()
                    """.trimIndent()
            )

            SpanText(
                text = "NFC Reader",
                modifier =  Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                         //iosMain
                         import PlatformContext
                         import androidx.compose.runtime.Composable
                         import kotlinx.cinterop.ExperimentalForeignApi
                         import kotlinx.cinterop.addressOf
                         import kotlinx.cinterop.allocArrayOf
                         import kotlinx.cinterop.memScoped
                         import kotlinx.cinterop.usePinned
                         import kotlinx.coroutines.CoroutineScope
                         import kotlinx.coroutines.SupervisorJob
                         import kotlinx.coroutines.flow.MutableSharedFlow
                         import kotlinx.coroutines.flow.SharedFlow
                         import kotlinx.coroutines.launch
                         import platform.CoreNFC.NFCNDEFMessage
                         import platform.CoreNFC.NFCNDEFPayload
                         import platform.CoreNFC.NFCNDEFReaderSession
                         import platform.CoreNFC.NFCNDEFReaderSessionDelegateProtocol
                         import platform.Foundation.NSData
                         import platform.Foundation.NSError
                         import platform.Foundation.create
                         import platform.darwin.NSObject
                         import platform.posix.memcpy

                         actual class NfcManager actual constructor(platformContext: PlatformContext) : NSObject(),
                             NFCNDEFReaderSessionDelegateProtocol {

                             private val scope = CoroutineScope(SupervisorJob())

                             private val _tagData = MutableSharedFlow<String>()

                             @Composable
                             actual fun RegisterForNFC() {
                                 if (NFCNDEFReaderSession.readingAvailable()) {
                                     val session = NFCNDEFReaderSession(this, null, false)
                                      //NATIVE
                                     //session = NFCNDEFReaderSession(delegate: self, queue: DispatchQueue.main, invalidateAfterFirstRead: false)

                                     session.alertMessage = "Hold your iPhone near the item to learn more about it."
                                     session.beginSession()
                                 }
                             }

                             actual val flow: SharedFlow<String>
                                 get() = _tagData

                             override fun readerSession(session: NFCNDEFReaderSession, didDetectNDEFs: List<*>) {
                                 val message = didDetectNDEFs.firstOrNull() as? NFCNDEFMessage
                                 val records = message?.records as List<NFCNDEFPayload>

                                 records.forEach {
                                     val payloadMessage = it.payload.toByteArray().decodeToString().substringAfter("en")
                                     scope.launch {
                                         _tagData.emit(payloadMessage)
                                     }
                                 }
                                 session.invalidateSession()
                             }

                             override fun readerSessionDidBecomeActive(session: NFCNDEFReaderSession) {
                             }

                             override fun readerSession(session: NFCNDEFReaderSession, didInvalidateWithError: NSError) {
                             }
                         }

                         @OptIn(ExperimentalForeignApi::class)
                         fun NSData.toByteArray(): ByteArray {
                             val data = this
                             val d = memScoped { data }
                             return ByteArray(d.length.toInt()).apply {
                                 usePinned {
                                     memcpy(it.addressOf(0), d.bytes, d.length)
                                 }
                             }
                         }

                         @OptIn(ExperimentalForeignApi::class)
                         fun ByteArray.toData(): NSData = memScoped {
                             NSData.create(
                                 bytes = allocArrayOf(this@toData),
                                 length = this@toData.size.toULong()
                             )
                         }

                         actual class PlatformContext

                         @Composable
                         actual fun getPlatformContext(): PlatformContext {
                             return PlatformContext()
                         }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    package nfcRead

                    import PlatformContext
                    import android.app.Activity
                    import android.nfc.NdefMessage
                    import android.nfc.NfcAdapter
                    import android.nfc.Tag
                    import android.nfc.tech.Ndef
                    import android.os.Bundle
                    import androidx.compose.runtime.Composable
                    import androidx.compose.ui.platform.LocalContext
                    import kotlinx.coroutines.CoroutineScope
                    import kotlinx.coroutines.SupervisorJob
                    import kotlinx.coroutines.flow.MutableSharedFlow
                    import kotlinx.coroutines.flow.SharedFlow
                    import kotlinx.coroutines.launch

                    actual class NfcManager actual constructor(private val platformContext: PlatformContext) :
                        NfcAdapter.ReaderCallback {
                        private var mNfcAdapter: NfcAdapter? = null
                        private val _tagData = MutableSharedFlow<String>()

                        val scope = CoroutineScope(SupervisorJob())

                        @Composable
                        actual fun RegisterForNFC() {
                            mNfcAdapter = NfcAdapter.getDefaultAdapter(platformContext.context);

                            if (mNfcAdapter != null) {

                                val options = Bundle()
                                // Work around for some broken Nfc firmware implementations that poll the card too fast
                                options.putInt(NfcAdapter.EXTRA_READER_PRESENCE_CHECK_DELAY, 500)

                                // Enable ReaderMode for all types of card and disable platform sounds
                                // the option NfcAdapter.FLAG_READER_SKIP_NDEF_CHECK is NOT set
                                // to get the data of the tag after reading
                                mNfcAdapter!!.enableReaderMode(
                                    LocalContext.current as Activity,
                                    this,
                                    NfcAdapter.FLAG_READER_NFC_A or
                                            NfcAdapter.FLAG_READER_NFC_B or
                                            NfcAdapter.FLAG_READER_NFC_F or
                                            NfcAdapter.FLAG_READER_NFC_V or
                                            NfcAdapter.FLAG_READER_NFC_BARCODE or
                                            NfcAdapter.FLAG_READER_NO_PLATFORM_SOUNDS,
                                    options
                                )
                            }
                        }

                        override fun onTagDiscovered(tag: Tag?) {
                            val mNdef = Ndef.get(tag)
                            val mNdefMessage: NdefMessage = mNdef.cachedNdefMessage
                            val record = mNdefMessage.records
                            val ndefRecordsCount = record.size

                            if (ndefRecordsCount > 0) {
                                for (i in 0 until ndefRecordsCount) {
                                    val payload = String(record[i].payload, Charsets.UTF_8)
                                    scope.launch {
                                        _tagData.emit(payload)
                                    }
                                }
                            }
                        }

                        actual val flow: SharedFlow<String>
                            get() = _tagData
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import android.content.Context
                    import androidx.compose.runtime.Composable
                    import androidx.compose.ui.platform.LocalContext

                    actual class PlatformContext(val context: Context)

                    @Composable
                    actual fun getPlatformContext() = PlatformContext(LocalContext.current)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    @Preview
                    fun App() {
                        MaterialTheme {
                            var searchTag by remember { mutableStateOf(false) }
                            val scope = rememberCoroutineScope()
                            val nfcManager = NfcManager(getPlatformContext())
                            var tagData by remember { mutableStateOf("") }

                            scope.launch {

                                nfcManager.flow.collectLatest {
                                    tagData = it
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Location",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    import com.ixidev.jtsalat.data.models.LocationInfo

                    expect class GeocodeReverser {
                        suspend fun reverseGeocode(lat: Double, lng: Double): LocationInfo
                    }

                    class AppViewModel(
                        private val locationTracker: LocationTracker,
                        private val geocodeReverser: GeocodeReverser,
                    ) : ViewModel() {
                        init {
                            locationTracker.getLocationsFlow()
                                .map {
                                    geocodeReverser.reverseGeocode(it.latitude, it.longitude)
                                }
                                .distinctUntilChangedBy { it.city }
                                .onEach {
                                    //LOCATION GET HERE
                                }.launchIn(viewModelScope)

                        }

                        override fun onCleared() {
                            super.onCleared()
                            locationTracker.stopTracking()
                        }

                        fun startTracking() {
                            viewModelScope.launch { locationTracker.startTracking()
                            }
                        }
                    }

                    //Access
                    val facotry = rememberLocationTrackerFactory(LocationTrackerAccuracy.LowPower)
                    val locationTracker = remember { facotry.createLocationTracker() }
                    BindLocationTrackerEffect(locationTracker)

                    val viewModel = koinInject<AppViewModel>(
                        parameters = { parametersOf(locationTracker) }
                    )

                    LaunchedEffect(locationTracker) {
                        viewModel.startTracking()
                    }

                    //iosMain
                    import com.ixidev.jtsalat.data.models.LocationInfo
                    import platform.CoreLocation.CLGeocodeCompletionHandler
                    import platform.CoreLocation.CLGeocoder
                    import platform.CoreLocation.CLLocation
                    import platform.CoreLocation.CLPlacemark
                    import platform.Foundation.NSError
                    import kotlin.coroutines.resume
                    import kotlin.coroutines.suspendCoroutine

                    actual class GeocodeReverser {
                        private val geocoder = CLGeocoder()
                        actual suspend fun reverseGeocode(
                            lat: Double,
                            lng: Double
                        ): LocationInfo = suspendCoroutine { continuation ->
                            geocoder.reverseGeocodeLocation(
                                CLLocation(lat, lng),
                                completionHandler = object : CLGeocodeCompletionHandler {
                                    override fun invoke(placemarks: List<*>?, error: NSError?) {
                                        val placemark = placemarks?.get(0) as CLPlacemark
                                        if (error != null) {
                                            println(error)
                                            continuation.resume(
                                                LocationInfo(
                                                    latitude = lat,
                                                    longitude = lng,
                                                    city = "Unknown"
                                                )
                                            )
                                        } else {
                                            continuation.resume(
                                                LocationInfo(
                                                    latitude = lat,
                                                    longitude = lng,
                                                    city = placemark.country
                                                )
                                            )
                                        }
                                    }
                                }
                            )
                        }
                    }

                    //androidMain
                    import android.content.Context
                    import android.location.Geocoder
                    import android.os.Build
                    import com.ixidev.jtsalat.data.models.LocationInfo
                    import java.util.Locale
                    import kotlin.coroutines.resume
                    import kotlin.coroutines.suspendCoroutine

                    actual class GeocodeReverser(context: Context) {
                        private val geocoder = Geocoder(context, Locale.getDefault())
                        actual suspend fun reverseGeocode(
                            lat: Double,
                            lng: Double
                        ): LocationInfo = suspendCoroutine { continuation ->
                            var cityName = "Unknown"
                            var countryName = "Unknown"
                            if (Build.VERSION.SDK_INT >= Build.VERSION_CODES.TIRAMISU) {
                                geocoder.getFromLocation(lat, lng, 1) {
                                    if (it.isNotEmpty()) {
                                        cityName = it[0].locality
                                        countryName = it[0].countryName
                                    }
                                    continuation.resume(
                                        LocationInfo(
                                            latitude = lat,
                                            longitude = lng,
                                            city = countryName,
                                        )
                                    )
                                }
                            } else {
                                try {
                                    val addresses = geocoder.getFromLocation(lat, lng, 1) ?: emptyList()
                                    if (addresses.isNotEmpty()) {
                                        cityName = addresses[0].locality
                                        countryName = addresses[0].countryName
                                    }
                                } catch (e: Exception) {
                                    e.printStackTrace()
                                }
                                continuation.resume(
                                    LocationInfo(
                                        latitude = lat,
                                        longitude = lng,
                                        city = countryName,
                                    )
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import models.Location

                    // Define a common class for location service
                    expect class LocationService() {
                        suspend fun getCurrentLocationOneTime(): Location
                    }

                    //androidMain
                    import android.Manifest
                    import android.content.pm.PackageManager
                    import android.location.Geocoder
                    import androidx.core.content.ContextCompat
                    import com.google.android.gms.location.LocationRequest
                    import com.google.android.gms.location.LocationServices
                    import com.google.android.gms.tasks.CancellationToken
                    import com.google.android.gms.tasks.CancellationTokenSource
                    import com.google.android.gms.tasks.OnTokenCanceledListener
                    import models.Location
                    import org.lighthousegames.logging.logging
                    import java.util.concurrent.atomic.AtomicReference
                    import kotlin.coroutines.resume
                    import kotlin.coroutines.resumeWithException
                    import kotlin.coroutines.suspendCoroutine

                    actual class LocationService actual constructor() {
                        // Define an atomic reference to store the latest location
                        private val latestLocation = AtomicReference<Location?>(null)

                        private val fusedLocationClient by lazy {
                            LocationServices.getFusedLocationProviderClient(
                                applicationContext
                            )
                        }

                        private val geocoder = Geocoder(applicationContext)

                        actual suspend fun getCurrentLocationOneTime(): Location = suspendCoroutine { continuation ->
                            if (ContextCompat.checkSelfPermission(
                                    applicationContext,
                                    Manifest.permission.ACCESS_FINE_LOCATION
                                )
                                == PackageManager.PERMISSION_GRANTED
                            ) {
                                fusedLocationClient.getCurrentLocation(
                                    LocationRequest.PRIORITY_HIGH_ACCURACY,
                                    object : CancellationToken() {
                                        override fun onCanceledRequested(p0: OnTokenCanceledListener): CancellationToken =
                                            CancellationTokenSource().token

                                        override fun isCancellationRequested(): Boolean = false
                                    })
                                    .addOnSuccessListener { androidOsLocation ->
                                        val addresses = geocoder.getFromLocation(
                                            androidOsLocation.latitude,
                                            androidOsLocation.longitude,
                                            1
                                        )
                                        logging("location tag").d {
                                            androidOsLocation.toString() + " " +
                                                    addresses?.get(0)?.subAdminArea + addresses?.get(0)?.adminArea
                                        }
                                        val updatedLocation = Location(
                                            androidOsLocation.latitude,
                                            androidOsLocation.longitude,
                                            addresses?.get(0)?.subAdminArea ?: addresses?.get(0)?.adminArea
                                        )
                                        latestLocation.set(updatedLocation)
                                        continuation.resume(updatedLocation)
                                    }.addOnFailureListener { e ->
                                        continuation.resumeWithException(e)
                                    }
                            } else {
                                continuation.resumeWithException(Throwable("Location permission is denied"))
                            }
                        }
                    }

                    //iosMain
                    import co.touchlab.stately.concurrency.AtomicReference
                    import kotlinx.cinterop.ExperimentalForeignApi
                    import kotlinx.cinterop.useContents
                    import models.Location
                    import org.lighthousegames.logging.logging
                    import platform.CoreLocation.CLGeocoder
                    import platform.CoreLocation.CLHeading
                    import platform.CoreLocation.CLLocation
                    import platform.CoreLocation.CLLocationManager
                    import platform.CoreLocation.CLLocationManagerDelegateProtocol
                    import platform.CoreLocation.CLPlacemark
                    import platform.CoreLocation.kCLLocationAccuracyBest
                    import platform.Foundation.NSError
                    import platform.darwin.NSObject
                    import kotlin.coroutines.resume
                    import kotlin.coroutines.resumeWithException
                    import kotlin.coroutines.suspendCoroutine

                    actual class LocationService actual constructor() {
                        val log = logging("Location Service")
                        // Define a native CLLocationManager object
                        private val oneTimeLocationManager = CLLocationManager()

                        // Define an atomic reference to store the latest location
                        private val latestLocation = AtomicReference<Location?>(null)

                        // Define a custom delegate that extends NSObject and implements CLLocationManagerDelegateProtocol
                        private class LocationDelegate : NSObject(), CLLocationManagerDelegateProtocol {
                            val log = logging("Location Service")

                            // Define a callback to receive location updates
                            var onLocationUpdate: ((Location?) -> Unit)? = null

                            @OptIn(ExperimentalForeignApi::class)
                            override fun locationManager(manager: CLLocationManager, didUpdateLocations: List<*>) {
                                didUpdateLocations.firstOrNull()?.let {
                                    val location = it as CLLocation
                                    location.coordinate.useContents {
                                        CLGeocoder().reverseGeocodeLocation(location) { list, error ->
                                            if (error == null && list?.get(0) is CLPlacemark) {
                                                onLocationUpdate?.invoke(
                                                    Location(
                                                        latitude,
                                                        longitude,
                                                        (list[0] as CLPlacemark).subAdministrativeArea
                                                    )
                                                )
                                            } else {
                                                onLocationUpdate?.invoke(Location(latitude, longitude))
                                            }
                                        }
                                    }
                                }
                            }


                            override fun locationManager(manager: CLLocationManager, didUpdateHeading: CLHeading) {
                            }

                            override fun locationManager(manager: CLLocationManager, didFailWithError: NSError) {
                                onLocationUpdate?.invoke(null)
                            }

                            override fun locationManager(manager: CLLocationManager, didChangeAuthorizationStatus: Int) {
                            }

                            override fun locationManagerDidPauseLocationUpdates(manager: CLLocationManager) {
                                log.i{"locationManagerDidPauseLocationUpdates" }
                            }

                            override fun locationManagerDidResumeLocationUpdates(manager: CLLocationManager) {
                                log.d{"locationManagerDidResumeLocationUpdates" }
                            }
                        }

                        actual suspend fun getCurrentLocationOneTime(): Location = suspendCoroutine { continuation ->
                            oneTimeLocationManager.requestWhenInUseAuthorization()
                            oneTimeLocationManager.desiredAccuracy = kCLLocationAccuracyBest

                            oneTimeLocationManager.startUpdatingLocation()

                            // Define a callback to receive location updates
                            val locationDelegate = LocationDelegate()
                            locationDelegate.onLocationUpdate = { location ->
                                oneTimeLocationManager.stopUpdatingLocation()
                                latestLocation.set(location)

                                location?.run {
                                    continuation.resume(this)
                                } ?: run {
                                    continuation.resumeWithException(Exception("Unable to get current location"))
                                }
                            }
                            oneTimeLocationManager.delegate = locationDelegate
                        }
                    }

                    //Access
                    locationService.getCurrentLocationOneTime()

                    //swift
                    import Foundation
                    import CoreLocation
                    import MapKit
                    class LocationManager: NSObject, ObservableObject, CLLocationManagerDelegate {

                        var locationManager = CLLocationManager()
                        @Published var authorizationStatus: CLAuthorizationStatus = .notDetermined
                        @Published var lastKnownLocation: CLLocation?

                        override init() {
                            super.init()
                            self.locationManager.delegate = self
                            self.authorizationStatus = CLLocationManager.authorizationStatus()
                        }

                        func requestAuthorization() {
                            self.locationManager.requestWhenInUseAuthorization()
                        }

                        func locationManagerDidChangeAuthorization(_ manager: CLLocationManager) {
                            self.authorizationStatus = manager.authorizationStatus
                        }

                        func locationManager(_ manager: CLLocationManager, didUpdateLocations locations: [CLLocation]) {
                            self.lastKnownLocation = locations.last
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Location continues update",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //Info.plist
                    <key>NSLocationAlwaysUsageDescription</key>
	                <string>We need your location to provide better services.</string>
	                <key>NSLocationWhenInUseUsageDescription</key>
	                <string>We need your location to provide better services.</string>

                    //AndroidManifest.xml
                    <uses-permission android:name="android.permission.ACCESS_COARSE_LOCATION" />
                    <uses-permission android:name="android.permission.ACCESS_FINE_LOCATION" />
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    expect class LocationProvider {
                        suspend fun getCurrentLocation(): AppResult<AppLocation, AppError>
                        fun listenToLocationUpdates(): Flow<AppLocation>
                    }

                    import androidx.compose.runtime.Composable
                    import model.PermissionResult

                    expect class LocationRequester {
                        @Composable
                        fun registerLocationRequest(
                            onAllowed: () -> Unit,
                            onDenied: () -> Unit,
                        )

                        fun launchLocationRequest()

                        @Composable
                        fun rememberLocationPermissionResult(): PermissionResult
                    }

                    sealed interface AppResult<out D, out E : AppError> {
                        data class Done<out D>(val data: D) : AppResult<D, Nothing>
                        data class Error<out E : AppError>(val error: E) : AppResult<Nothing, E>
                    }

                    interface AppError
                    enum class LocationError : AppError {
                        UNKNOWN
                    }

                    import kotlinx.coroutines.CoroutineDispatcher
                    import kotlinx.coroutines.Dispatchers
                    import kotlinx.coroutines.IO

                    interface DispatchersProvider {
                        val io: CoroutineDispatcher
                        val main: CoroutineDispatcher
                        val unconfiend: CoroutineDispatcher
                        val default: CoroutineDispatcher
                    }

                    class DefaultDispatcherProvider() : DispatchersProvider {
                        override val io: CoroutineDispatcher get() = Dispatchers.IO
                        override val main: CoroutineDispatcher get() = Dispatchers.Main
                        override val unconfiend: CoroutineDispatcher get() = Dispatchers.Unconfined
                        override val default: CoroutineDispatcher get() = Dispatchers.Default
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    sealed interface LocationState {
                        data object Loading : LocationState
                        data object Error : LocationState
                        data class Done(val location: AppLocation) : LocationState
                    }

                    import androidx.lifecycle.ViewModel
                    import androidx.lifecycle.viewModelScope
                    import kotlinx.coroutines.flow.SharingStarted
                    import kotlinx.coroutines.flow.StateFlow
                    import kotlinx.coroutines.flow.catch
                    import kotlinx.coroutines.flow.map
                    import kotlinx.coroutines.flow.onStart
                    import kotlinx.coroutines.flow.stateIn
                    import location.LocationProvider

                    class LocationViewModel(
                        private val locationProvider: LocationProvider,
                    ) : ViewModel() {

                        val state: StateFlow<LocationState> = locationProvider.listenToLocationUpdates()
                            .map {
                                LocationState.Done(it) as LocationState
                            }.onStart {
                                emit(LocationState.Loading)
                            }
                            .catch {
                                emit(LocationState.Error)
                            }
                            .stateIn(
                                viewModelScope,
                                SharingStarted.WhileSubscribed(5_000),
                                LocationState.Loading
                            )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //iosMain
                    import kotlinx.cinterop.ExperimentalForeignApi
                    import kotlinx.cinterop.useContents
                    import kotlinx.coroutines.channels.awaitClose
                    import kotlinx.coroutines.flow.Flow
                    import kotlinx.coroutines.flow.callbackFlow
                    import kotlinx.coroutines.flow.flowOn
                    import model.AppLocation
                    import model.DispatchersProvider
                    import platform.CoreLocation.CLLocation
                    import platform.CoreLocation.CLLocationManager
                    import platform.CoreLocation.CLLocationManagerDelegateProtocol
                    import platform.CoreLocation.kCLDistanceFilterNone
                    import platform.CoreLocation.kCLLocationAccuracyBest
                    import platform.Foundation.NSError
                    import platform.darwin.NSObject

                    actual class LocationProvider(
                        private val dispatchers: DispatchersProvider,
                    ) {
                        companion object {
                            private val ACCURACY = kCLLocationAccuracyBest
                            private val DISTANCE_FILTER = kCLDistanceFilterNone
                        }

                        private val locationManager: CLLocationManager = CLLocationManager()
                        private val locationDelegate = LocationDelegate()

                        private class LocationDelegate : NSObject(), CLLocationManagerDelegateProtocol {
                            var onLocationUpdate: ((AppLocation?) -> Unit)? = null
                            override fun locationManager(
                                manager: CLLocationManager,
                                didUpdateLocations: List<*>
                            ) {
                                didUpdateLocations.firstOrNull()?.let {
                                    val location = it as CLLocation
                                    onLocationUpdate?.invoke(location.toAppLocation())
                                }
                            }

                            override fun locationManager(
                                manager: CLLocationManager,
                                didFailWithError: NSError
                            ) {
                                onLocationUpdate?.invoke(null)
                            }
                        }


                        actual fun listenToLocationUpdates(): Flow<AppLocation> = callbackFlow {
                            initializeLocationManager()
                            locationDelegate.onLocationUpdate = { location ->
                                if (location != null) {
                                    trySend(location).isSuccess
                                }
                            }

                            locationManager.delegate = locationDelegate
                            locationManager.startUpdatingLocation()
                            awaitClose {
                                locationManager.stopUpdatingLocation()
                                locationManager.delegate = null
                            }
                        }.flowOn(dispatchers.io)


                        actual suspend fun getCurrentLocation(): AppResult<AppLocation, AppError> {
                            return try {
                                val cllLocation = locationManager.location
                                cllLocation?.let {
                                    AppResult.Done(it.toAppLocation())
                                } ?: AppResult.Error(LocationError.UNKNOWN)
                            } catch (e: Exception) {
                                AppResult.Error(LocationError.UNKNOWN)
                            }
                        }

                        private fun initializeLocationManager() {
                            locationManager.requestWhenInUseAuthorization()
                            locationManager.desiredAccuracy = ACCURACY
                            locationManager.distanceFilter = DISTANCE_FILTER
                        }
                    }

                    @OptIn(ExperimentalForeignApi::class)
                    private fun CLLocation.toAppLocation(): AppLocation {
                        return coordinate.useContents {
                            AppLocation(
                                latitude,
                                longitude,
                            )
                        }
                    }

                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.remember
                    import model.PermissionResult
                    import model.RequestCallBack
                    import platform.CoreLocation.CLAuthorizationStatus
                    import platform.CoreLocation.CLLocationManager
                    import platform.CoreLocation.kCLAuthorizationStatusAuthorizedAlways
                    import platform.CoreLocation.kCLAuthorizationStatusAuthorizedWhenInUse
                    import platform.CoreLocation.kCLAuthorizationStatusDenied
                    import platform.CoreLocation.kCLAuthorizationStatusNotDetermined
                    import platform.CoreLocation.kCLAuthorizationStatusRestricted

                    actual class LocationRequester {
                        private val locationManager = CLLocationManager()
                        private lateinit var locationRequest: RequestCallBack

                        @Composable
                        actual fun registerLocationRequest(
                            onAllowed: () -> Unit,
                            onDenied: () -> Unit,
                        ) {
                            if (!::locationRequest.isInitialized) {
                                locationRequest = RequestCallBack(
                                    onNegative = onDenied,
                                    onPositive = onAllowed
                                )
                            }
                        }

                        @Composable
                        actual fun rememberLocationPermissionResult(
                        ): PermissionResult {
                            return remember {
                                locationManager.authorizationStatus().mapToAppPermission()
                            }
                        }

                        actual fun launchLocationRequest() {
                            locationManager.requestAlwaysAuthorization()
                            val permissionResult =
                                locationManager.authorizationStatus().mapToAppPermission()
                            if (permissionResult.isUnknown) {
                                locationManager.requestAlwaysAuthorization()
                            } else if (permissionResult.isGranted) {
                                locationRequest.onPositive.invoke()
                            } else {
                                locationRequest.onNegative.invoke()
                            }
                        }
                    }

                    fun CLAuthorizationStatus.mapToAppPermission(): PermissionResult {
                        return when (this) {
                            kCLAuthorizationStatusAuthorizedAlways,
                            kCLAuthorizationStatusAuthorizedWhenInUse,
                            kCLAuthorizationStatusRestricted
                            -> PermissionResult.GRANTED

                            kCLAuthorizationStatusNotDetermined -> PermissionResult.UNKNOWN
                            kCLAuthorizationStatusDenied -> PermissionResult.DENIED
                            else -> PermissionResult.UNKNOWN
                        }
                    }

                    //androidMain
                    import android.content.Context
                    import android.os.Looper
                    import com.google.android.gms.location.LocationCallback
                    import com.google.android.gms.location.LocationRequest
                    import com.google.android.gms.location.LocationResult
                    import com.google.android.gms.location.LocationServices
                    import com.google.android.gms.location.Priority
                    import kotlinx.coroutines.channels.awaitClose
                    import kotlinx.coroutines.flow.Flow
                    import kotlinx.coroutines.flow.callbackFlow
                    import kotlinx.coroutines.flow.flowOn
                    import kotlinx.coroutines.withContext
                    import kotlin.coroutines.resume
                    import kotlin.coroutines.suspendCoroutine

                    actual class LocationProvider(
                        private val dispatchers: DispatchersProvider,
                        private val context: Context
                    ) {
                        companion object {
                            const val LOCATION_PRIORITY = Priority.PRIORITY_BALANCED_POWER_ACCURACY

                            const val CHECKING_INTERVAl = 500L

                            const val MIN_DISTANCE = 100f
                        }

                        private val client by lazy { LocationServices.getFusedLocationProviderClient(context) }

                        @Suppress("missingPermission")
                        actual suspend fun getCurrentLocation(): AppResult<AppLocation, AppError> {
                            return withContext(dispatchers.io) {
                                suspendCoroutine { cont ->
                                    client.lastLocation.addOnSuccessListener { loc ->
                                        loc?.let {
                                            cont.resume(
                                                AppResult.Done(
                                                    AppLocation(
                                                        lat = loc.latitude,
                                                        lng = loc.longitude
                                                    )
                                                )
                                            )
                                        } ?: run {
                                            cont.resume(AppResult.Error(LocationError.UNKNOWN))
                                        }
                                    }.addOnFailureListener {
                                        cont.resume(AppResult.Error(LocationError.UNKNOWN))
                                    }
                                }
                            }
                        }

                        @Suppress("missingPermission")
                        actual fun listenToLocationUpdates(): Flow<AppLocation> = callbackFlow<AppLocation> {
                            val locationRequest = LocationRequest.Builder(
                                LOCATION_PRIORITY,
                                CHECKING_INTERVAl,
                            ).setMinUpdateDistanceMeters(MIN_DISTANCE).build()
                            val locationCallBack = object : LocationCallback() {
                                override fun onLocationResult(locationResult: LocationResult) {
                                    super.onLocationResult(locationResult)
                                    locationResult.lastLocation?.let { lastLocation ->
                                        val location = AppLocation(
                                            lat = lastLocation.latitude,
                                            lng = lastLocation.longitude
                                        )
                                        trySend(location)
                                    }
                                }
                            }

                            client.requestLocationUpdates(
                                locationRequest,
                                locationCallBack,
                                Looper.getMainLooper()
                            )
                            awaitClose {
                                client.removeLocationUpdates(locationCallBack)
                            }
                        }.flowOn(dispatchers.io)
                    }

                    import androidx.activity.ComponentActivity
                    import androidx.activity.compose.rememberLauncherForActivityResult
                    import androidx.activity.result.ActivityResultLauncher
                    import androidx.activity.result.contract.ActivityResultContracts
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.remember
                    import androidx.compose.ui.platform.LocalContext

                    actual class LocationRequester {
                        private lateinit var getMultipleContent: ActivityResultLauncher<Array<String>>

                        @Composable
                        actual fun registerLocationRequest(
                            onAllowed: () -> Unit,
                            onDenied: () -> Unit,
                        ) {
                            getMultipleContent =
                                rememberLauncherForActivityResult(
                                    contract = ActivityResultContracts.RequestMultiplePermissions(),
                                ) { perms ->
                                    if (perms.values.any { true }) {
                                        onAllowed()
                                    } else {
                                        onDenied()
                                    }
                                }
                        }

                        @Composable
                        actual fun rememberLocationPermissionResult(): PermissionResult {
                            val context = LocalContext.current as ComponentActivity
                            val activity = LocalContext.current as ComponentActivity
                            val showRational = activity.shouldShowLocationPermissionRationale()
                            val allowedLocation = context.hasLocationPermission()
                            val res = if (allowedLocation) {
                                PermissionResult.GRANTED
                            } else if (showRational) {
                                PermissionResult.UNKNOWN
                            } else {
                                PermissionResult.DENIED
                            }
                            return remember { res }
                        }

                        actual fun launchLocationRequest() {
                            getMultipleContent.launch(
                                arrayOf(
                                    android.Manifest.permission.ACCESS_FINE_LOCATION,
                                    android.Manifest.permission.ACCESS_COARSE_LOCATION
                                )
                            )
                        }
                    }

                   import android.content.Context
                   import android.content.pm.PackageManager
                   import androidx.activity.ComponentActivity
                   import androidx.core.content.ContextCompat

                   fun ComponentActivity.shouldShowLocationPermissionRationale(): Boolean = shouldShowRequestPermissionRationale(android.Manifest.permission.ACCESS_FINE_LOCATION)

                   fun Context.hasPermission(permission: String): Boolean = ContextCompat.checkSelfPermission(this, permission) == PackageManager.PERMISSION_GRANTED

                   fun Context.hasLocationPermission(): Boolean = hasPermission(android.Manifest.permission.ACCESS_FINE_LOCATION) || hasPermission(android.Manifest.permission.ACCESS_COARSE_LOCATION)
                    """.trimIndent()
            )

            SpanText(
                text = "File Read / Write",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    // commonMain
                    interface FileHandler {
                        fun readFile(filePath: String): String
                        fun writeFile(filePath: String, content: String)
                    }

                    // androidMain
                    import java.io.File

                    class AndroidFileHandler : FileHandler {
                        override fun readFile(filePath: String): String {
                            return File(filePath).readText()
                        }

                        override fun writeFile(filePath: String, content: String) {
                            File(filePath).writeText(content)
                        }
                    }

                    // iosMain
                    import platform.Foundation.*

                    class IOSFileHandler : FileHandler {
                        override fun readFile(filePath: String): String {
                            val fileManager = NSFileManager.defaultManager()
                            val data = fileManager.contentsAtPath(filePath)
                            return data?.toKString() ?: ""
                        }

                        override fun writeFile(filePath: String, content: String) {
                            val fileManager = NSFileManager.defaultManager()
                            val data = content.encodeToByteArray()
                            fileManager.createFileAtPath(filePath, data, null)
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //https://github.com/Huctor/News

                    interface CacheManager {
                        /**
                         * Caches the given news response.
                         *
                         * @param newsResponse The news response to cache.
                         */
                        fun cacheNews(newsResponse: NewsResponse)

                        /**
                         * Retrieves the cached news response, if available.
                         *
                         * @return The cached news response, or null if no cache is available.
                         */
                        fun getCachedNews(): NewsResponse?
                    }

                    /**
                     * Expect declaration for a function that creates an HttpClient.
                     *
                     * This function is expected to be implemented in platform-specific code.
                     * It provides a way to create an instance of HttpClient for making
                     * HTTP requests in a multiplatform project.
                     */
                    expect fun createHttpClient(): HttpClient

                    //androidMain
                    import DI.androidModule
                    import DI.commonModule
                    import DI.initKoin
                    import android.app.Application
                    import org.koin.android.ext.koin.androidContext

                    /**
                     * MyApplication class that extends the Application class.
                     *
                     * This class serves as the application-wide context and is used to initialize
                     * dependency injection using Koin when the application starts.
                     */
                    class MyApplication: Application() {

                        // Override the onCreate method to initialize Koin
                        override fun onCreate() {
                            super.onCreate()
                            initKoin {
                                androidContext(this@MyApplication)
                                modules(commonModule(), androidModule)
                            }
                        }
                    }

                    import AndroidCacheManager.AndroidCacheManager
                    import CacheManager.CacheManager
                    import io.ktor.client.HttpClient
                    import io.ktor.client.engine.okhttp.OkHttp
                    import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
                    import io.ktor.client.plugins.logging.LogLevel
                    import io.ktor.client.plugins.logging.Logging
                    import io.ktor.serialization.kotlinx.json.json
                    import kotlinx.serialization.json.Json
                    import org.koin.dsl.module

                    /**
                     * Creates and configures an HttpClient instance using the OkHttp engine.
                     *
                     * This function sets up an HttpClient with logging of HTTP requests and responses,
                     * as well as configuring JSON serialization options.
                     *
                     * @return An instance of HttpClient configured with logging and content negotiation features.
                     */
                    actual fun createHttpClient(): HttpClient {
                        return HttpClient(OkHttp.create()) {
                            // Install the Logging feature to log all HTTP requests and responses
                            install(Logging) {
                                level = LogLevel.ALL // Set the logging level to log everything
                            }
                            // Install the ContentNegotiation feature with JSON support
                            install(ContentNegotiation) {
                                json(
                                    json = Json {
                                        ignoreUnknownKeys = true // Ignore unknown keys in the JSON response
                                    }
                                )
                            }
                        }
                    }

                    /**
                     * Android-specific Koin module to provide the AndroidCacheManager implementation.
                     */
                    val androidModule = module {
                        single<CacheManager> { AndroidCacheManager(get()) }
                    }

                    import CacheManager.CacheManager
                    import android.content.Context
                    import kotlinx.serialization.encodeToString
                    import kotlinx.serialization.json.Json
                    import java.io.File

                    /**
                     * AndroidCacheManager is an implementation of CacheManager for Android.
                     *
                     * @param context The application context used to access the cache directory.
                     */
                    class AndroidCacheManager(private val context: Context) : CacheManager {

                        private val cacheFile = File(context.cacheDir, "news_cache.json")

                        /**
                         * Caches the provided news response by writing it to a file in the cache directory.
                         *
                         * @param newsResponse The news response to cache.
                         */
                        override fun cacheNews(newsResponse: NewsResponse) {
                            cacheFile.writeText(Json.encodeToString(newsResponse))
                        }

                        /**
                         * Retrieves the cached news response from the cache directory.
                         *
                         * @return The cached NewsResponse object, or null if no cached data is available.
                         */
                        override fun getCachedNews(): NewsResponse? {
                             return if (cacheFile.exists()) {
                                val cachedText = cacheFile.readText()
                                 Json.decodeFromString(cachedText)
                            } else {
                                 null
                            }
                        }
                    }

                    //iosMain
                    @file:OptIn(kotlinx.cinterop.ExperimentalForeignApi::class)
                    package IosCacheManager

                    import CacheManager.CacheManager
                    import Model.NewsResponse
                    import kotlinx.cinterop.BetaInteropApi
                    import kotlinx.serialization.decodeFromString
                    import kotlinx.serialization.encodeToString
                    import kotlinx.serialization.json.Json
                    import platform.Foundation.NSCachesDirectory
                    import platform.Foundation.NSFileManager
                    import platform.Foundation.NSSearchPathForDirectoriesInDomains
                    import platform.Foundation.NSString
                    import platform.Foundation.NSUTF8StringEncoding
                    import platform.Foundation.NSUserDomainMask
                    import platform.Foundation.create
                    import platform.Foundation.writeToFile

                    /**
                     * IosCacheManager is an implementation of CacheManager for iOS.
                     */
                    class IosCacheManager : CacheManager {

                        private val cacheFileName = "news_cache.json"

                        /**
                         * Caches the provided news response by writing it to a file in the cache directory.
                         *
                         * @param newsResponse The news response to cache.
                         */
                        @OptIn(BetaInteropApi::class)
                        override fun cacheNews(newsResponse: NewsResponse) {
                            val cacheDir = NSSearchPathForDirectoriesInDomains(NSCachesDirectory, NSUserDomainMask, true).firstOrNull() as String
                            val cacheFilePath = "cacheDir/cacheFileName"
                            val jsonString = Json.encodeToString(newsResponse)
                            NSString.create(string = jsonString).writeToFile(cacheFilePath, atomically = true, encoding = NSUTF8StringEncoding, error = null)
                        }

                        /**
                         * Retrieves the cached news response from the cache directory.
                         *
                         * @return The cached NewsResponse object, or null if no cached data is available.
                         */
                        @OptIn(BetaInteropApi::class)
                        override fun getCachedNews(): NewsResponse? {
                            val cacheDir = NSSearchPathForDirectoriesInDomains(NSCachesDirectory, NSUserDomainMask, true).firstOrNull() as String
                            val cacheFilePath = "cacheDir/cacheFileName"
                            val fileManager = NSFileManager.defaultManager()
                            return if (fileManager.fileExistsAtPath(cacheFilePath)) {
                                val jsonString = NSString.create(contentsOfFile = cacheFilePath, encoding = NSUTF8StringEncoding, error = null) as String
                                Json.decodeFromString(jsonString)
                            } else {
                                null
                            }
                        }
                    }

                    import CacheManager.CacheManager
                    import IosCacheManager.IosCacheManager
                    import io.ktor.client.HttpClient
                    import io.ktor.client.engine.darwin.Darwin
                    import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
                    import io.ktor.client.plugins.logging.LogLevel
                    import io.ktor.client.plugins.logging.Logging
                    import io.ktor.serialization.kotlinx.json.json
                    import kotlinx.serialization.json.Json
                    import org.koin.dsl.module

                    /**
                     * Creates and configures an HttpClient instance with necessary features.
                     *
                     * This function sets up an HttpClient with the Darwin engine, enabling logging
                     * of all HTTP requests and responses, as well as configuring JSON serialization
                     * with specific options.
                     *
                     * @return An instance of HttpClient configured with logging and content negotiation features.
                     */
                    actual fun createHttpClient(): HttpClient {
                        return HttpClient(Darwin.create()) {
                            // Install the Logging feature to log HTTP requests and responses
                            install(Logging) {
                                level = LogLevel.ALL
                            }
                            // Install the ContentNegotiation feature for handling JSON content
                            install(ContentNegotiation) {
                                json(
                                    json = Json {
                                        ignoreUnknownKeys = true
                                    }
                                )
                            }
                        }
                    }

                    /**
                     * iOS-specific Koin module to provide the IosCacheManager implementation.
                     */
                    val iosModule = module {
                        single<CacheManager> { IosCacheManager() }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Google Login",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                     commonMain.dependencies {
                        implementation(libs.firebase.gitlive.common)
                        implementation(libs.firebase.gitlive.auth)
                    }

                    import androidx.compose.runtime.compositionLocalOf

                    val LocalResources = compositionLocalOf<Resources> {
                        error("No LocalResources provided")
                    }

                    interface Resources {
                        val drawables: Drawables
                    }

                    @OptIn(ExperimentalResourceApi::class)
                    interface Drawables {
                        val googleIcon: DrawableResource
                    }

                    sealed interface AuthResponse {
                        data class Success(val account: GoogleAccount) : AuthResponse
                        data class Error(val message: String) : AuthResponse
                        data object Cancelled : AuthResponse

                        suspend fun doOnSuccess(block: suspend (GoogleAccount) -> Unit) {
                            when(this) {
                                is Success -> block(account)
                                Cancelled -> Unit
                                is Error -> Unit
                            }
                        }
                    }

                    data class GoogleAccount(
                        val idToken: String,
                        val accessToken: String,
                        val profile: Profile
                    )

                    data class Profile(
                        val name: String,
                        val familyName: String,
                        val givenName: String,
                        val email: String,
                        val picture: String?
                    )

                    @OptIn(ExperimentalResourceApi::class)
                    @Composable
                    internal fun GoogleButtonUI(
                        onClick: () -> Unit,
                        modifier: Modifier = Modifier
                    ) {
                        Button(
                            modifier = modifier,
                            onClick = onClick,
                            colors = ButtonDefaults.buttonColors(
                                backgroundColor = Color.White,
                                contentColor = Color.Black
                            ),
                            shape = RoundedCornerShape(12.dp),
                            border = BorderStroke(1.dp, Color.LightGray),
                            contentPadding = PaddingValues(horizontal = 16.dp, vertical = 16.dp)
                        ) {
                            Image(
                                modifier = Modifier.size(24.dp),
                                painter = painterResource(LocalResources.current.drawables.googleIcon),
                                contentDescription = null
                            )
                            Spacer(modifier = Modifier.width(8.dp))
                            Text("Continue with Google")
                        }
                    }

                    @Preview
                    @Composable
                    private fun GoogleButtonPreview() {
                        GoogleButtonUI(onClick = {})
                    }

                    @Composable
                    internal expect fun GoogleLoginButton(
                        onResponse: (AuthResponse) -> Unit,
                        modifier: Modifier = Modifier
                    )

                    @Composable
                    fun GoogleButton(
                        onResponse: (AuthResponse) -> Unit,
                        modifier: Modifier = Modifier
                    ) {
                        val coroutineScope = rememberCoroutineScope()

                        GoogleLoginButton(
                            modifier = modifier,
                            onResponse = { response ->
                                coroutineScope.launch(Dispatchers.IO) {
                                    response.doOnSuccess { account ->
                                        Firebase.auth.signInWithCredential(
                                            GoogleAuthProvider.credential(account.idToken, account.accessToken)
                                        )
                                    }

                                    withContext(Dispatchers.Main) { onResponse(response) }
                                }
                            }
                        )
                    }
                    
                    //iosMain
                    import androidx.compose.runtime.Composable
                    import androidx.compose.ui.Modifier
                    import androidx.compose.ui.interop.LocalUIViewController
                    import cocoapods.GoogleSignIn.GIDSignIn
                    import cocoapods.GoogleSignIn.GIDSignInResult
                    import kotlinx.cinterop.ExperimentalForeignApi
                    import platform.Foundation.NSError
                    import platform.UIKit.UIViewController

                    @Composable
                    internal actual fun GoogleLoginButton(
                        onResponse: (AuthResponse) -> Unit,
                        modifier: Modifier
                    ) {
                        val uiViewController = LocalUIViewController.current

                        GoogleButtonUI(
                            modifier = modifier,
                            onClick = { googleLogin(uiViewController, onResponse) }
                        )
                    }

                    @OptIn(ExperimentalForeignApi::class)
                    private fun googleLogin(
                        uiViewController: UIViewController,
                        onLoggedIn: (AuthResponse) -> Unit
                    ) {
                        GIDSignIn.sharedInstance.signInWithPresentingViewController(uiViewController) { result, error ->
                            when {
                                result != null -> onLoggedIn(AuthResponse.Success(result.toGoogleAccount))
                                error != null -> onLoggedIn(AuthResponse.Error(error.fullErrorMessage))
                                else -> onLoggedIn(AuthResponse.Cancelled)
                            }
                        }
                    }

                    @OptIn(ExperimentalForeignApi::class)
                    private val GIDSignInResult.toGoogleAccount: GoogleAccount
                        get() = GoogleAccount(
                            idToken = user.idToken?.tokenString.orEmpty(),
                            accessToken = user.accessToken.tokenString,
                            profile = Profile(
                                name = user.profile?.name.orEmpty(),
                                familyName = user.profile?.familyName.orEmpty(),
                                givenName = user.profile?.givenName.orEmpty(),
                                email = user.profile?.email.orEmpty(),
                                picture = user.profile?.imageURLWithDimension(100u)?.absoluteString
                            )
                        )

                    private val NSError.fullErrorMessage: String
                        get() {
                            val underlyingErrors = underlyingErrors.joinToString(", ") { it.toString() }
                            val recoveryOptions = localizedRecoveryOptions?.joinToString(", ") { it.toString() }

                            return listOfNotNull(
                                "code: code",
                                domain?.let { "domain: domain" },
                                "description: localizedDescription",
                                localizedFailureReason?.let { "reason: localizedFailureReason" },
                                localizedRecoverySuggestion?.let { "suggestion: localizedRecoverySuggestion" },
                                "underlyingErrors: underlyingErrors",
                                "recoveryOptions: recoveryOptions".takeIf { recoveryOptions != null },
                            ).joinToString("\n")
                        }
                        
                         //androidMain

                    @Composable
                    internal actual fun GoogleLoginButton(
                        onResponse: (AuthResponse) -> Unit,
                        modifier: Modifier
                    ) {
                        val gso = GoogleSignInOptions.Builder(GoogleSignInOptions.DEFAULT_SIGN_IN)
                            .requestIdToken("PLACE_YOUR_CLIENT_ID_HERE")
                            .requestEmail()
                            .build()

                        val activity = LocalContext.current as Activity
                        val googleSignInClient = GoogleSignIn.getClient(activity, gso)

                        val launcher =
                            rememberLauncherForActivityResult(ActivityResultContracts.StartActivityForResult()) { result ->
                                try {
                                    val task = GoogleSignIn.getSignedInAccountFromIntent(result.data)
                                    val account = task.getResult(ApiException::class.java)

                                    onResponse(AuthResponse.Success(account.googleAccount))
                                } catch (e: ApiException) {
                                    if (result.resultCode == Activity.RESULT_CANCELED) {
                                        AuthResponse.Cancelled
                                    } else {
                                        AuthResponse.Error(e.fullErrorMessage)
                                    }.also(onResponse)

                                    Log.w("TAG", "Google sign in failed", e)
                                }
                            }

                        GoogleButtonUI(
                            modifier = modifier,
                            onClick = { launcher.launch(googleSignInClient.signInIntent) },
                        )
                    }

                    private val GoogleSignInAccount.googleAccount: GoogleAccount
                        get() = GoogleAccount(
                            idToken = idToken.orEmpty(),
                            accessToken = serverAuthCode.orEmpty(),
                            profile = Profile(
                                name = displayName.orEmpty(),
                                familyName = familyName.orEmpty(),
                                givenName = givenName.orEmpty(),
                                email = email.orEmpty(),
                                picture = photoUrl?.toString().orEmpty()
                            ),
                        )

                    private val ApiException.fullErrorMessage: String
                        get() {
                            return listOfNotNull(
                                "code: statusCode",
                                message?.let { "message: message" },
                                "localizedMessage: localizedMessage",
                                "status: status",
                            ).joinToString("\n")
                        }
                    """.trimIndent()
            )

            //https://github.com/pitampoudel/komposeauth.git
            KotlinCode(
                code = """
                    //Google & Apple Login
                    
                    //Result.kt
                    import io.ktor.http.HttpStatusCode
                   
                    sealed class Result<out T> {
                        data class Success<out T>(val data: T) : Result<T>()
                        open class Error(open val message: InfoMessage.Error) : Result<Nothing>() {
                            constructor(msg: String) : this(InfoMessage.Error(msg))

                            data class Http(
                                override val message: InfoMessage.Error,
                                val httpStatusCode: HttpStatusCode
                            ) : Error(message)
                        }
                    }
                    
                    //InfoMessage.kt
                    sealed interface InfoMessage {
                        val text: String

                        data class General(override val text: String) : InfoMessage
                        data class Success(override val text: String) : InfoMessage
                        data class Error(override val text: String) : InfoMessage
                    }

                    fun Throwable?.toInfoMessage(): InfoMessage {
                        return InfoMessage.Error(this?.message.orEmpty())
                    }
                   
                    //Credential.kt
                    @Serializable
                    data class LoginOptions(
                        @SerialName("googleClientId")
                        val googleClientId: String?,
                        @SerialName("publicKeyAuthOptionsJson")
                        val publicKeyAuthOptionsJson: String?
                    )
                    
                    @Serializable
                    sealed class Credential {
                        @Serializable
                        @SerialName("GOOGLE_ID")
                        class GoogleId(
                            @SerialName("idToken")
                            val idToken: String
                        ) : Credential()

                        @Serializable
                        @SerialName("APPLE_ID")
                        class AppleId(
                            @SerialName("idToken")
                            val idToken: String
                        ) : Credential()

                        @Serializable
                        @SerialName("AUTH_CODE")
                        class AuthCode(
                            @SerialName("platform")
                            val platform: Platform,
                            @SerialName("code")
                            val code: String,
                            @SerialName("codeVerifier")
                            val codeVerifier: String,
                            @SerialName("redirectUri")
                            val redirectUri: String
                        ) : Credential()

                        @Serializable
                        @SerialName("USERNAME_PASSWORD")
                        class UsernamePassword(
                            @SerialName("username")
                            val username: String,
                            @SerialName("password")
                            val password: String
                        ) : Credential()

                        @Serializable
                        @SerialName("PUBLIC_KEY")
                        data class PublicKey(
                            @SerialName("authenticationResponseJson")
                            val authenticationResponseJson: String
                        ) : Credential()

                        @Serializable
                        @SerialName("REFRESH_TOKEN")
                        data class RefreshToken(
                            @SerialName("refreshToken")
                            val refreshToken: String
                        ) : Credential()
                    }
                    
                    //KmpCredentialManager.kt
                    interface KmpCredentialManager {
                        suspend fun getCredential(options: LoginOptions): Result<Credential>
                        suspend fun createPassKeyAndRetrieveJson(options: String): Result<JsonObject>
                    }

                    @Composable
                    expect fun rememberKmpCredentialManager(): KmpCredentialManager
                    
                    //KmpCredentialManager.android.kt
                    @Composable
                    actual fun rememberKmpCredentialManager(): KmpCredentialManager {
                        val activity = LocalActivity.current ?: throw Exception("No activity found")
                        val credentialManager = remember {
                            CredentialManager.create(activity)
                        }
                        return remember {
                            object : KmpCredentialManager {
                                override suspend fun getCredential(options: LoginOptions): Result<Credential> {

                                    val googleAuthClientId = options.googleClientId
                                    val publicKeyVerificationOptionsJson = options.publicKeyAuthOptionsJson

                                    val googleIdOption: GetGoogleIdOption? = googleAuthClientId?.let {
                                        GetGoogleIdOption.Builder()
                                            .setFilterByAuthorizedAccounts(false)
                                            .setServerClientId(googleAuthClientId)
                                            .build()
                                    }

                                    val request = GetCredentialRequest.Builder()
                                        .also { req ->
                                            googleIdOption?.let {
                                                req.addCredentialOption(googleIdOption)
                                            }
                                            publicKeyVerificationOptionsJson?.let {
                                                req.addCredentialOption(
                                                    GetPublicKeyCredentialOption(
                                                        requestJson = publicKeyVerificationOptionsJson
                                                    )
                                                )
                                            }
                                        }
                                        .build()

                                    try {
                                        val result = credentialManager.getCredential(
                                            context = activity,
                                            request = request
                                        )
                                        val credential: Result<Credential> =
                                            when (val credential = result.credential) {
                                                is PublicKeyCredential -> {
                                                    Result.Success(
                                                        Credential.PublicKey(
                                                            authenticationResponseJson = credential.authenticationResponseJson
                                                        )
                                                    )
                                                }

                                                is CustomCredential -> {
                                                    if (credential.type == GoogleIdTokenCredential.TYPE_GOOGLE_ID_TOKEN_CREDENTIAL) {
                                                        try {
                                                            val googleIdTokenCredential =
                                                                GoogleIdTokenCredential.createFrom(credential.data)
                                                            Result.Success(
                                                                Credential.GoogleId(
                                                                    idToken = googleIdTokenCredential.idToken
                                                                )
                                                            )
                                                        } catch (e: GoogleIdTokenParsingException) {
                                                            Result.Error(e.message.orEmpty())
                                                        }
                                                    } else {
                                                        Result.Error("Unexpected type of credential")
                                                    }

                                                }

                                                else -> Result.Error("Unknown credential type")
                                            }
                                        credentialManager.clearCredentialState(ClearCredentialStateRequest())
                                        return credential
                                    } catch (e: GetCredentialCancellationException) {
                                        e.printStackTrace()
                                        return Result.Error(e.message.orEmpty())

                                    } catch (e: GetCredentialException) {
                                        e.printStackTrace()
                                        val userMessage = when (e) {
                                            is GetCredentialProviderConfigurationException ->
                                                "Google Play services on this device is missing, out of date, or not compatible to sign in with Google. Please update/install Google Play services and try again."

                                            is NoCredentialException ->
                                                "No Google account available for sign-in on this device. Please add a Google account and try again."

                                            else -> e.message
                                                ?: "Unable to retrieve credentials at the moment. Please try again."
                                        }
                                        return Result.Error(userMessage)
                                    }


                                }

                                override suspend fun createPassKeyAndRetrieveJson(options: String): Result<JsonObject> {

                                    if (Build.VERSION.SDK_INT < Build.VERSION_CODES.P) return Result.Error("Android version not supported")

                                    val response = try {
                                        credentialManager.createCredential(
                                            context = activity,
                                            request = CreatePublicKeyCredentialRequest(options)
                                        )

                                    } catch (e: CreateCredentialException) {
                                        e.printStackTrace()
                                        return Result.Error(e.message.orEmpty())
                                    }

                                    return when (response) {

                                        is CreatePublicKeyCredentialResponse -> {
                                            Result.Success(Json.parseToJsonElement(response.registrationResponseJson).jsonObject)
                                        }

                                        else -> {
                                            Result.Error("Unknown credential")
                                        }
                                    }
                                }

                            }
                        }
                    }
                    
                    //CredentialRetriever.ios.kt
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.remember
                    import pitampoudel.core.domain.Result
                    import pitampoudel.komposeauth.data.Credential
                    import pitampoudel.komposeauth.data.LoginOptions
                    import kotlinx.serialization.json.JsonObject
                    import platform.AuthenticationServices.ASAuthorization
                    import platform.AuthenticationServices.ASAuthorizationAppleIDCredential
                    import platform.AuthenticationServices.ASAuthorizationAppleIDProvider
                    import platform.AuthenticationServices.ASAuthorizationController
                    import platform.AuthenticationServices.ASAuthorizationControllerDelegateProtocol
                    import platform.AuthenticationServices.ASAuthorizationControllerPresentationContextProvidingProtocol
                    import platform.AuthenticationServices.ASAuthorizationScopeEmail
                    import platform.AuthenticationServices.ASAuthorizationScopeFullName
                    import platform.Foundation.NSError
                    import platform.Foundation.base64EncodedStringWithOptions
                    import platform.UIKit.UIApplication
                    import platform.UIKit.UIWindow
                    import platform.darwin.NSObject
                    import kotlin.coroutines.resume
                    import kotlin.coroutines.suspendCoroutine

                    @Composable
                    actual fun rememberKmpCredentialManager(): KmpCredentialManager {
                        val retriever = remember {
                            object : NSObject(), ASAuthorizationControllerDelegateProtocol,
                                ASAuthorizationControllerPresentationContextProvidingProtocol {
                                private var onResult: (Result<Credential>) -> Unit = {}
                                suspend fun getCredential(): Result<Credential> {
                                    return suspendCoroutine { continuation ->
                                        onResult = { result ->
                                            continuation.resume(result)
                                        }
                                        val appleIDProvider = ASAuthorizationAppleIDProvider()
                                        val request = appleIDProvider.createRequest()
                                        request.requestedScopes =
                                            listOf(ASAuthorizationScopeFullName, ASAuthorizationScopeEmail)

                                        val authorizationController = ASAuthorizationController(listOf(request))
                                        authorizationController.delegate = this
                                        authorizationController.presentationContextProvider = this
                                        authorizationController.performRequests()
                                    }
                                }

                                override fun authorizationController(
                                    controller: ASAuthorizationController,
                                    didCompleteWithAuthorization: ASAuthorization
                                ) {
                                    val appleIDCredential =
                                        didCompleteWithAuthorization.credential as? ASAuthorizationAppleIDCredential
                                    if (appleIDCredential != null) {
                                        val idToken =
                                            appleIDCredential.identityToken?.base64EncodedStringWithOptions(0u) ?: ""
                                        onResult(Result.Success(Credential.AppleId(idToken)))
                                    } else {
                                        onResult(Result.Error("Apple ID credential is null"))
                                    }
                                }

                                override fun authorizationController(
                                    controller: ASAuthorizationController,
                                    didCompleteWithError: NSError
                                ) {
                                    onResult(Result.Error(didCompleteWithError.localizedDescription))
                                }

                                override fun presentationAnchorForAuthorizationController(controller: ASAuthorizationController): UIWindow {
                                    return UIApplication.sharedApplication.windows.first() as UIWindow
                                }
                            }
                        }

                        return object : KmpCredentialManager {
                            override suspend fun getCredential(options: LoginOptions): Result<Credential> {
                                return retriever.getCredential()
                            }

                            override suspend fun createPassKeyAndRetrieveJson(options: String): Result<JsonObject> {
                                return Result.Error("Passkeys are not implemented on iOS yet")
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Google & Facebook Login",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            //https://github.com/sjaindl/StartingEleven
            KotlinCode(
                code = $$"""
                    //androidMain
                    import androidx.activity.compose.LocalActivityResultRegistryOwner
                    import androidx.compose.runtime.Composable
                    import com.facebook.CallbackManager
                    import com.facebook.FacebookCallback
                    import com.facebook.FacebookException
                    import com.facebook.login.LoginManager
                    import com.facebook.login.LoginResult
                    import com.sjaindl.s11.auth.model.FacebookAuthResponse
                    import io.github.aakira.napier.Napier

                    private val callbackManager by lazy {
                        CallbackManager.Factory.create()
                    }

                    @Composable
                    actual fun FacebookSignIn(onResponse: (FacebookAuthResponse) -> Unit) {
                        val activityResultRegistryOwner = LocalActivityResultRegistryOwner.current

                        LoginManager.getInstance().registerCallback(
                            callbackManager = callbackManager,
                            callback = object : FacebookCallback<LoginResult> {
                                override fun onSuccess(result: LoginResult) {
                                    onResponse(FacebookAuthResponse.Success(result.accessToken.token))
                                }

                                override fun onCancel() {
                                    Napier.d(message = "Facebook onCancel")
                                    onResponse(FacebookAuthResponse.Cancelled)
                                }

                                override fun onError(error: FacebookException) {
                                    Napier.e(message = "Facebook onError", throwable = error)
                                    onResponse(FacebookAuthResponse.Error(error.localizedMessage ?: error.toString()))
                                }
                            },
                        )

                        if (activityResultRegistryOwner != null) {
                            LoginManager.getInstance().logInWithReadPermissions(
                                activityResultRegistryOwner = activityResultRegistryOwner,
                                callbackManager = callbackManager,
                                permissions = listOf("email", "public_profile"),
                            )
                        } else {
                            onResponse(FacebookAuthResponse.Error("ActivityResultRegistryOwner is null"))
                        }
                    }

                    import com.google.android.libraries.identity.googleid.GoogleIdTokenCredential
                    import com.sjaindl.s11.auth.model.GoogleAccount
                    import com.sjaindl.s11.auth.model.GoogleProfile

                    val GoogleIdTokenCredential.googleAccount: GoogleAccount
                        get() = GoogleAccount(
                            idToken = idToken,
                            accessToken = null,
                            googleProfile = GoogleProfile(
                                name = displayName,
                                familyName = familyName,
                                givenName = givenName,
                                email = id,
                                pictureUrl = profilePictureUri.toString(),
                            ),
                        )

                        import androidx.compose.runtime.Composable
                        import androidx.compose.runtime.LaunchedEffect
                        import androidx.compose.runtime.rememberCoroutineScope
                        import androidx.compose.ui.platform.LocalContext
                        import androidx.credentials.CredentialManager
                        import androidx.credentials.CustomCredential
                        import androidx.credentials.GetCredentialRequest
                        import androidx.credentials.PasswordCredential
                        import androidx.credentials.PublicKeyCredential
                        import androidx.credentials.exceptions.GetCredentialException
                        import com.google.android.libraries.identity.googleid.GetSignInWithGoogleOption
                        import com.google.android.libraries.identity.googleid.GoogleIdTokenCredential
                        import com.sjaindl.s11.BuildConfig
                        import com.sjaindl.s11.auth.model.GoogleAuthResponse
                        import io.github.aakira.napier.Napier
                        import kotlinx.coroutines.launch
                        import org.koin.java.KoinJavaComponent.inject

                        @Composable
                        actual fun GoogleSignIn(onResponse: (GoogleAuthResponse) -> Unit) {
                            val credentialManager: CredentialManager by inject(CredentialManager::class.java)

                            val coroutineScope = rememberCoroutineScope()
                            val context = LocalContext.current

                            val googleIdOption = GetSignInWithGoogleOption.Builder(serverClientId = BuildConfig.googleServerClientId)
                                .build()

                            val request = GetCredentialRequest.Builder()
                                .addCredentialOption(googleIdOption)
                                .build()

                            LaunchedEffect(Unit) {
                                coroutineScope.launch {
                                    try {
                                        val result = credentialManager.getCredential(
                                            context = context,
                                            request = request,
                                        )

                                        when (val credential = result.credential) {
                                            is PublicKeyCredential -> {
                                                // Share responseJson such as a GetCredentialResponse on your server to validate and authenticate
                                                val responseJson = credential.authenticationResponseJson
                                                onResponse(GoogleAuthResponse.Error(responseJson)) //Received PublicKeyCredential:
                                            }

                                            is PasswordCredential -> {
                                                // Send ID and password to your server to validate and authenticate.
                                                val username = credential.id
                                                val password = credential.password
                                                onResponse(GoogleAuthResponse.Error("Received PasswordCredential for instead of CustomCredential"))
                                            }

                                            is CustomCredential -> {
                                                if (credential.type == GoogleIdTokenCredential.TYPE_GOOGLE_ID_TOKEN_CREDENTIAL) {
                                                    val googleIdTokenCredential = GoogleIdTokenCredential.createFrom(data = credential.data)

                                                    onResponse(
                                                        GoogleAuthResponse.Success(account = googleIdTokenCredential.googleAccount),
                                                    )
                                                }
                                            }
                                        }

                                    } catch (exception: GetCredentialException) {
                                        onResponse(GoogleAuthResponse.Error(exception.message ?: exception.toString()))
                                    }
                                }
                            }
                        }

                        //iosMain
                        import androidx.compose.runtime.Composable
                        import androidx.compose.ui.interop.LocalUIViewController
                        import cocoapods.FBSDKLoginKit.FBSDKLoginConfiguration
                        import cocoapods.FBSDKLoginKit.FBSDKLoginManager
                        import cocoapods.FBSDKLoginKit.FBSDKLoginTrackingEnabled
                        import com.sjaindl.s11.auth.model.FacebookAuthResponse
                        import kotlinx.cinterop.ExperimentalForeignApi

                        @OptIn(ExperimentalForeignApi::class)
                        @Composable
                        actual fun FacebookSignIn(onResponse: (FacebookAuthResponse) -> Unit) {
                            val uiViewController = LocalUIViewController.current

                            val loginManager = FBSDKLoginManager()
                            loginManager.logInWithPermissions(listOf("public_profile"), uiViewController) { result, error ->
                                val token = result?.token()
                                when {
                                    token != null -> onResponse(FacebookAuthResponse.Success(token.tokenString()))
                                    error != null -> onResponse(FacebookAuthResponse.Error(error.toString()))
                                    else -> onResponse(FacebookAuthResponse.Cancelled)
                                }
                            }
                        }

                        // TODO: Switch to this implementation as soon as https://github.com/facebook/facebook-ios-sdk/issues/2455 is fixed
                        @OptIn(ExperimentalForeignApi::class)
                        @Composable
                        fun FacebookLimitedSignIn(onResponse: (FacebookAuthResponse) -> Unit) {
                            val uiViewController = LocalUIViewController.current

                            val loginManager = FBSDKLoginManager()
                            val config = FBSDKLoginConfiguration(
                                permissions = listOf("email", "public_profile"),
                                tracking = FBSDKLoginTrackingEnabled,
                            )

                            loginManager.logInFromViewController(uiViewController, config) { result, error ->
                                val token = result?.token()

                                when {
                                    token != null -> onResponse(FacebookAuthResponse.Success(token.tokenString()))
                                    error != null -> onResponse(FacebookAuthResponse.Error(error.toString()))
                                    else -> onResponse(FacebookAuthResponse.Cancelled)
                                }
                            }
                        }

                        import androidx.compose.runtime.Composable
                        import androidx.compose.ui.interop.LocalUIViewController
                        import cocoapods.GoogleSignIn.GIDSignIn
                        import cocoapods.GoogleSignIn.GIDSignInResult
                        import com.sjaindl.s11.auth.model.GoogleAccount
                        import com.sjaindl.s11.auth.model.GoogleAuthResponse
                        import com.sjaindl.s11.auth.model.GoogleProfile
                        import kotlinx.cinterop.ExperimentalForeignApi
                        import platform.Foundation.NSError

                        @OptIn(ExperimentalForeignApi::class)
                        @Composable
                        actual fun GoogleSignIn(onResponse: (GoogleAuthResponse) -> Unit) {
                            val uiViewController = LocalUIViewController.current

                            GIDSignIn.sharedInstance.signInWithPresentingViewController(uiViewController) { result, error ->
                                when {
                                    result != null -> onResponse(GoogleAuthResponse.Success(result.toGoogleAccount))
                                    error != null -> onResponse(GoogleAuthResponse.Error(error.fullErrorMessage))
                                    else -> onResponse(GoogleAuthResponse.Cancelled)
                                }
                            }
                        }

                        @OptIn(ExperimentalForeignApi::class)
                        private val GIDSignInResult.toGoogleAccount: GoogleAccount
                            get() = GoogleAccount(
                                idToken = user.idToken?.tokenString.orEmpty(),
                                accessToken = user.accessToken.tokenString,
                                googleProfile = GoogleProfile(
                                    name = user.profile?.name.orEmpty(),
                                    familyName = user.profile?.familyName.orEmpty(),
                                    givenName = user.profile?.givenName.orEmpty(),
                                    email = user.profile?.email.orEmpty(),
                                    pictureUrl = user.profile?.imageURLWithDimension(100u)?.absoluteString,
                                ),
                            )

                        private val NSError.fullErrorMessage: String
                            get() {
                                val underlyingErrors = underlyingErrors.joinToString(", ") { it.toString() }
                                val recoveryOptions = localizedRecoveryOptions?.joinToString(", ") { it.toString() }

                                return listOfNotNull(
                                    "code: code",
                                    domain?.let { "domain: $domain" },
                                    "description:  $localizedDescription",
                                    localizedFailureReason?.let { "reason:  $localizedFailureReason" },
                                    localizedRecoverySuggestion?.let { "suggestion:  $localizedRecoverySuggestion" },
                                    "underlyingErrors: $underlyingErrors",
                                    "recoveryOptions:  $recoveryOptions".takeIf { recoveryOptions != null },
                                ).joinToString("\n")
                            }

                            sealed interface FacebookAuthResponse {
                                data class Success(val accessToken: String) : FacebookAuthResponse
                                data class Error(val message: String) : FacebookAuthResponse
                                data object Cancelled : FacebookAuthResponse
                            }

                            data class GoogleAccount(
                                val idToken: String,
                                val accessToken: String?,
                                val googleProfile: GoogleProfile,
                            )

                            data class GoogleProfile(
                                val name: String?,
                                val familyName: String?,
                                val givenName: String?,
                                val email: String?,
                                val pictureUrl: String?,
                            )

                            sealed interface GoogleAuthResponse {
                                data class Success(val account: GoogleAccount) : GoogleAuthResponse
                                data class Error(val message: String) : GoogleAuthResponse
                                data object Cancelled : GoogleAuthResponse
                            }


                            import androidx.compose.runtime.Composable
                            import com.sjaindl.s11.auth.model.FacebookAuthResponse

                            @Composable
                            expect fun FacebookSignIn(onResponse: (FacebookAuthResponse) -> Unit)

                            import androidx.compose.runtime.Composable
                            import com.sjaindl.s11.auth.model.GoogleAuthResponse

                            @Composable
                            expect fun GoogleSignIn(
                                onResponse: (GoogleAuthResponse) -> Unit,
                            )

                            import androidx.lifecycle.ViewModel
                            import androidx.lifecycle.viewModelScope
                            import dev.gitlive.firebase.Firebase
                            import dev.gitlive.firebase.FirebaseException
                            import dev.gitlive.firebase.auth.FacebookAuthProvider
                            import dev.gitlive.firebase.auth.FirebaseAuthException
                            import dev.gitlive.firebase.auth.FirebaseAuthInvalidCredentialsException
                            import dev.gitlive.firebase.auth.FirebaseAuthInvalidUserException
                            import dev.gitlive.firebase.auth.GoogleAuthProvider
                            import dev.gitlive.firebase.auth.auth
                            import io.github.aakira.napier.Napier
                            import kotlinx.coroutines.flow.MutableStateFlow
                            import kotlinx.coroutines.flow.asStateFlow
                            import kotlinx.coroutines.launch

                            sealed class AuthenticationState {
                                data object Initial: AuthenticationState()
                                data object Loading: AuthenticationState()
                                data object EmailSignInSuccess: AuthenticationState()
                                data object EmailSignUpSuccess: AuthenticationState()
                                data object GoogleSignInSuccess: AuthenticationState()
                                data object FacebookSignInSuccess: AuthenticationState()
                                data class Error(val message: String): AuthenticationState()
                            }

                            class AuthenticationViewModel : ViewModel() {

                                private val tag = "AuthenticationViewModel"

                                private val auth by lazy {
                                    Firebase.auth
                                }

                                private var _authenticationState: MutableStateFlow<AuthenticationState> = MutableStateFlow(AuthenticationState.Initial)
                                var authenticationState = _authenticationState.asStateFlow()

                                fun signInWithMail(email: String, password: String) = viewModelScope.launch {
                                    try {
                                        _authenticationState.value = AuthenticationState.Loading

                                        val user = auth.signInWithEmailAndPassword(email = email, password = password).user
                                        if (user != null) {
                                            Napier.d("Signed in user  ${auth.currentUser?.displayName}")
                                            _authenticationState.value = AuthenticationState.EmailSignInSuccess
                                        } else {
                                            Napier.e("Sign-in failed unexpectedly")
                                            _authenticationState.value = AuthenticationState.Error(message = "Sign-in failed unexpectedly")
                                        }
                                    } catch (e: FirebaseAuthInvalidCredentialsException) {
                                        Napier.e("Sign-in failed: Invalid credentials", e)
                                        _authenticationState.value = AuthenticationState.Error(message = e.message ?: e.toString())
                                    } catch (e: FirebaseAuthInvalidUserException) {
                                        Napier.e("Sign-in failed: Invalid user", e)
                                        _authenticationState.value = AuthenticationState.Error(message = e.message ?: e.toString())
                                    } catch (e: FirebaseAuthException) {
                                        // on iOS FirebaseAuthException is directly thrown
                                        Napier.e("Sign-in failed", e)
                                        _authenticationState.value = AuthenticationState.Error(message = e.message ?: e.toString())
                                    }
                                }

                                fun signUpWithMail(email: String, password: String, name: String) = viewModelScope.launch {
                                    try {
                                        _authenticationState.value = AuthenticationState.Loading

                                        val user = auth.createUserWithEmailAndPassword(email = email, password = password).user
                                        if (user != null) {
                                            user.updateProfile(displayName = name)

                                            _authenticationState.value = AuthenticationState.EmailSignInSuccess
                                        } else {
                                            Napier.e("Sign-up failed unexpectedly")
                                            _authenticationState.value = AuthenticationState.Error(message = "Sign-in failed unexpectedly")
                                        }
                                    } catch (e: FirebaseException) {
                                        Napier.e("Sign-up failed", e)
                                        _authenticationState.value = AuthenticationState.Error(message = e.message ?: e.toString())
                                    } catch (e: FirebaseAuthException) {
                                        // on iOS FirebaseAuthException is directly thrown
                                        Napier.e("Sign-up failed", e)
                                        _authenticationState.value = AuthenticationState.Error(message = e.message ?: e.toString())
                                    }
                                }

                                fun handleGoogleSignIn(googleAuthResponse: GoogleAuthResponse, cancelMessage: String) {
                                    viewModelScope.launch {
                                        when(googleAuthResponse) {
                                            GoogleAuthResponse.Cancelled -> {
                                                _authenticationState.value = AuthenticationState.Error(message = cancelMessage)
                                            }
                                            is GoogleAuthResponse.Error -> {
                                                _authenticationState.value = AuthenticationState.Error(message = googleAuthResponse.message)
                                            }
                                            is GoogleAuthResponse.Success -> {
                                                Firebase.auth.signInWithCredential(
                                                    GoogleAuthProvider.credential(
                                                        idToken = googleAuthResponse.account.idToken,
                                                        accessToken = googleAuthResponse.account.accessToken,
                                                    )
                                                )
                                                _authenticationState.value = AuthenticationState.GoogleSignInSuccess
                                            }
                                        }
                                    }
                                }

                                fun handleFacebookSignIn(facebookAuthResponse: FacebookAuthResponse, cancelMessage: String) {
                                    viewModelScope.launch {
                                        when(facebookAuthResponse) {
                                            FacebookAuthResponse.Cancelled -> {
                                                _authenticationState.value = AuthenticationState.Error(message = cancelMessage)
                                            }
                                            is FacebookAuthResponse.Error -> {
                                                _authenticationState.value = AuthenticationState.Error(message = facebookAuthResponse.message)
                                            }
                                            is FacebookAuthResponse.Success -> {
                                                try {
                                                    Firebase.auth.signInWithCredential(
                                                        FacebookAuthProvider.credential(
                                                            accessToken = facebookAuthResponse.accessToken,
                                                        )
                                                    )
                                                    _authenticationState.value = AuthenticationState.FacebookSignInSuccess
                                                } catch (e: FirebaseAuthInvalidCredentialsException) {
                                                    Napier.e(message = "Firebase Auth Error: FirebaseAuthInvalidCredentialsException", throwable = e)
                                                    _authenticationState.value = AuthenticationState.Error(message = " ${e.message}")
                                                } catch (e: FirebaseAuthException) {
                                                    Napier.e("Sign-in failed", e)
                                                    _authenticationState.value = AuthenticationState.Error(message = e.message ?: e.toString())
                                                }
                                            }
                                        }
                                    }
                                }

                                fun resetState() {
                                    _authenticationState.value = AuthenticationState.Initial
                                }
                            }
                    """.trimIndent()
            )

            SpanText(
                text = "CMP Google Map",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    data class GoogleMapViewEntries(
                        val title: String,
                        val snippet: String,
                        val lat: Double,
                        val lng: Double,
                    )

                    @Composable
                    expect fun GoogleMapView(
                        modifier: Modifier = Modifier,
                        vararg googleMapViewEntries: GoogleMapViewEntries,
                        zoom: Float = 10f
                    )

                    GoogleMapView(
                        modifier = modifier.fillMaxSize(),
                        googleMapViewEntries = arrayOf(
                            GoogleMapViewEntries(
                                title = "Anfield Stadium",
                                snippet = "",
                                lat = 53.431870,
                                lng = -2.960970
                            )
                        ),
                        zoom = 15f
                    )

                    //iosMain
                    import androidx.compose.runtime.Composable
                    import androidx.compose.ui.Modifier
                    import androidx.compose.ui.interop.UIKitView
                    import cocoapods.GoogleMaps.GMSAdvancedMarker.Companion.markerImageWithColor
                    import cocoapods.GoogleMaps.GMSCameraPosition
                    import cocoapods.GoogleMaps.GMSMapView
                    import cocoapods.GoogleMaps.GMSMapView.Companion.mapWithFrame
                    import cocoapods.GoogleMaps.GMSMarker
                    import cocoapods.GoogleMaps.animateToZoom
                    import kotlinx.cinterop.ExperimentalForeignApi
                    import platform.CoreLocation.CLLocationCoordinate2DMake
                    import platform.UIKit.UIColor


                    @OptIn(ExperimentalForeignApi::class)
                    @Composable
                    actual fun GoogleMapView(
                        modifier: Modifier,
                        vararg googleMapViewEntries: GoogleMapViewEntries,
                        zoom: Float
                    ) {
                        UIKitView(
                            factory = {
                                val camera: GMSCameraPosition? = if (googleMapViewEntries.isNotEmpty()){
                                    GMSCameraPosition.cameraWithLatitude(
                                        latitude = googleMapViewEntries.first().lat,
                                        longitude = googleMapViewEntries.first().lng,
                                        zoom = zoom
                                    )
                                }else{
                                    null
                                }

                                val mapView =  GMSMapView()
                                camera?.let { mapWithFrame(frame = mapView.frame, camera = it) }
                                mapView.settings.zoomGestures = true
                                mapView.settings.consumesGesturesInView = true
                                googleMapViewEntries.forEach { entry ->
                                    GMSMarker().apply {
                                        this.position = CLLocationCoordinate2DMake(
                                            latitude = entry.lat,
                                            longitude = entry.lng
                                        )
                                        this.title = entry.title
                                        this.snippet = entry.snippet
                                        markerImageWithColor(UIColor.redColor)
                    //                    this.markerImageWithColor(color = UIColor.redColor)
                                    }.map = mapView
                                }
                                mapView
                            },
                            modifier = modifier,
                            onRelease = {
                                it.removeFromSuperview()
                            }
                        )
                    }

                    //androidMain
                    import androidx.compose.foundation.layout.fillMaxSize
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.getValue
                    import androidx.compose.runtime.mutableStateOf
                    import androidx.compose.runtime.remember
                    import androidx.compose.ui.Modifier
                    import com.google.android.gms.maps.model.CameraPosition
                    import com.google.android.gms.maps.model.LatLng
                    import com.google.maps.android.compose.GoogleMap
                    import com.google.maps.android.compose.MapUiSettings
                    import com.google.maps.android.compose.Marker
                    import com.google.maps.android.compose.MarkerState
                    import com.google.maps.android.compose.rememberCameraPositionState

                    @Composable
                    actual fun GoogleMapView(
                        modifier: Modifier,
                        vararg googleMapViewEntries: GoogleMapViewEntries,
                        zoom: Float
                    ) {

                        val latLng: LatLng? = if (googleMapViewEntries.isNotEmpty()){
                            LatLng(googleMapViewEntries.first().lat, googleMapViewEntries.first().lng)
                        }else{
                            null
                        }

                        val cameraPositionState = rememberCameraPositionState {
                            latLng?.let { position = CameraPosition.fromLatLngZoom(it, zoom) }
                        }

                        val mapUiSettings by remember {
                            mutableStateOf(
                                MapUiSettings(
                                    zoomGesturesEnabled = true
                                )
                            )
                        }

                        GoogleMap(
                            modifier = Modifier.fillMaxSize(),
                            cameraPositionState = cameraPositionState,
                            uiSettings = mapUiSettings
                        ) {
                            googleMapViewEntries.forEach { entry ->
                                Marker(
                                    state = MarkerState(
                                        position = LatLng(entry.lat, entry.lng)
                                    ),
                                    title = entry.title,
                                    snippet = entry.snippet
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //https://github.com/areebmomin/Google-Maps-CMP

                    @Composable
                    expect fun MapComponent()

                    //androidMain
                    @Composable
                    actual fun MapComponent() {
                        Box(
                            modifier = Modifier.fillMaxSize(),
                        ) {
                            val coordinates = LatLng(19.068857, 72.833)
                            val markerState = rememberMarkerState(position = coordinates)
                            val cameraPositionState = rememberCameraPositionState {
                                position = CameraPosition.fromLatLngZoom(coordinates, 10f)
                            }
                            GoogleMap(
                                modifier = Modifier.fillMaxSize(),
                                cameraPositionState = cameraPositionState
                            ) {
                                Marker(
                                    state = markerState,
                                    title = "Bandra West",
                                    snippet = "Mumbai"
                                )
                            }
                        }
                    }

                    //iosMain
                    import androidx.compose.foundation.layout.fillMaxSize
                    import androidx.compose.runtime.Composable
                    import androidx.compose.ui.Modifier
                    import androidx.compose.ui.interop.UIKitViewController
                    import kotlinx.cinterop.ExperimentalForeignApi

                    @OptIn(ExperimentalForeignApi::class)
                    @Composable
                    actual fun MapComponent() {
                        UIKitViewController(
                            factory = mapViewController,
                            modifier = Modifier.fillMaxSize(),
                        )
                    }

                    fun MainViewController(
                        mapUIViewController: () -> UIViewController
                    ) = ComposeUIViewController {
                        mapViewController = mapUIViewController
                        App()
                    }

                    lateinit var mapViewController: () -> UIViewController

                    //iosApp
                    import UIKit
                    import SwiftUI
                    import ComposeApp
                    import GoogleMaps

                    struct GoogleMapView: UIViewRepresentable {
                        func makeUIView(context: Context) -> GMSMapView {
                            let options = GMSMapViewOptions()
                            options.camera = GMSCameraPosition.camera(withLatitude: 12.952636, longitude: 77.653059, zoom: 10.0)

                            let mapView = GMSMapView(options: options)

                            // Creates a marker in the center of the map.
                            let marker = GMSMarker()
                            marker.position = CLLocationCoordinate2D(latitude: 12.952636, longitude: 77.653059)
                            marker.title = "Indiranagar"
                            marker.snippet = "Bengaluru"
                            marker.map = mapView

                            return mapView
                        }

                        func updateUIView(_ uiView: GMSMapView, context: Context) {}
                    }

                    struct ComposeView: UIViewControllerRepresentable {
                        func makeUIViewController(context: Context) -> UIViewController {
                            MainViewControllerKt.MainViewController(
                                mapUIViewController: { () -> UIViewController in
                                    return UIHostingController(rootView: GoogleMapView())
                                }
                            )
                        }

                        func updateUIViewController(_ uiViewController: UIViewController, context: Context) {}
                    }

                    struct ContentView: View {
                        var body: some View {
                            ComposeView()
                                    .ignoresSafeArea(.keyboard) // Compose has own keyboard handler
                        }
                    }

                    import SwiftUI
                    import GoogleMaps

                    @main
                    struct iOSApp: App {
                        init() {
                            GMSServices.provideAPIKey("YOUR_API_KEY")
                        }

                        var body: some Scene {
                            WindowGroup {
                                ContentView()
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //https://github.com/mikaere66/DiscoverMultiplatform.git

                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.MutableState
                    import androidx.compose.ui.Modifier
                    import com.michaelrmossman.multiplatform.discover.features.geometry.Coordinates

                    @Composable
                    expect fun LocationVisualizer(
                        coords: Coordinates,
                        modifier: Modifier,
                        parentScrollEnableState: MutableState<Boolean>,
                        title: String?
                    )

                    //iosMain
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.MutableState
                    import androidx.compose.runtime.remember
                    import androidx.compose.ui.Modifier
                    import androidx.compose.ui.interop.UIKitView
                    import com.michaelrmossman.multiplatform.discover.features.geometry.Coordinates
                    import kotlinx.cinterop.ExperimentalForeignApi
                    import platform.CoreLocation.CLLocationCoordinate2DMake
                    import platform.MapKit.MKCoordinateRegionMakeWithDistance
                    import platform.MapKit.MKMapView
                    import platform.MapKit.MKPointAnnotation

                    @OptIn(ExperimentalForeignApi::class)
                    @Composable
                    actual fun LocationVisualizer(
                        coords: Coordinates,
                        modifier: Modifier,
                        parentScrollEnableState: MutableState<Boolean>,
                        title: String?
                    ) {
                        val location = CLLocationCoordinate2DMake(coords.latitude, coords.longitude)
                        val annotation = remember {
                            MKPointAnnotation(
                                location,
                                title = null,
                                subtitle = null
                            )
                        }
                        val mkMapView = remember { MKMapView().apply { addAnnotation(annotation) } }
                        annotation.setTitle(title)
                        UIKitView(
                            modifier = modifier,
                            factory = {
                                mkMapView
                            },
                            update = {
                                mkMapView.setRegion(
                                    MKCoordinateRegionMakeWithDistance(
                                        centerCoordinate = location,
                                        10_000.0, 10_000.0
                                    ),
                                    animated = true
                                )
                            }
                        )
                    }

                    //androidMain
                    import android.view.MotionEvent
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.LaunchedEffect
                    import androidx.compose.runtime.MutableState
                    import androidx.compose.ui.ExperimentalComposeUiApi
                    import androidx.compose.ui.Modifier
                    import androidx.compose.ui.input.pointer.pointerInteropFilter
                    import co.touchlab.kermit.Logger
                    import com.google.android.gms.maps.model.CameraPosition
                    import com.google.android.gms.maps.model.LatLng
                    import com.google.maps.android.compose.GoogleMap
                    import com.google.maps.android.compose.rememberCameraPositionState
                    import com.michaelrmossman.multiplatform.discover.features.geometry.Coordinates

                    @OptIn(ExperimentalComposeUiApi::class)
                    @Composable
                    actual fun LocationVisualizer(
                        coords: Coordinates,
                        modifier: Modifier,
                        parentScrollEnableState: MutableState<Boolean>,
                        title: String?
                    ) {
                        val currentLocation = LatLng(coords.latitude, coords.longitude)
                        // Logger.i("HEY") { currentLocation.toString() }
                        val cameraPositionState = rememberCameraPositionState {
                            position = CameraPosition.fromLatLngZoom(currentLocation, 10f)
                        }

                        LaunchedEffect(cameraPositionState.isMoving) {
                            /* This code helps when implementing a Compose
                               GoogleMap inside a scrollable container */
                            if (!cameraPositionState.isMoving) {
                                parentScrollEnableState.value = true
                            }
                        }

                        GoogleMap(
                            modifier = modifier.pointerInteropFilter(
                                onTouchEvent = {
                                    when (it.action) {
                                        MotionEvent.ACTION_DOWN -> {
                                            parentScrollEnableState.value = false
                                            false
                                        }
                                        else -> true
                                    }
                                }
                            ),
                            cameraPositionState = cameraPositionState
                        )
                    }

                    //UI
                    @Composable
                    fun RouteTab(
                        onEvent: (MainListEvent) -> Unit,
                        state: MainScreenState
                    ) {
                        val locationShape = RoundedCornerShape(10.dp)
                        val modifier = Modifier
                            .padding(horizontal = 12.dp)
                            .clip(locationShape)
                            .border(1.dp, Color.Gray, locationShape)
                            .fillMaxWidth()
                            .height(200.dp)

                        val scrollState = rememberScrollState()
                        val verticalScrollEnableState = remember { mutableStateOf(true) }

                        state.currentRoute?.let { route ->
                            val coords = Coordinates(
                                route.lati, route.long
                            )

                            Box {
                                Column(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .verticalScroll(
                                            scrollState,
                                            enabled = verticalScrollEnableState.value
                                        ),
                                    verticalArrangement = Arrangement.Center,
                                    horizontalAlignment = Alignment.CenterHorizontally
                                ) {
                                    Spacer(Modifier.height(50.dp))
                                    LocationVisualizer(
                                        coords = coords,
                                        modifier = modifier,
                                        parentScrollEnableState = verticalScrollEnableState,
                                        title = route.name
                                    )
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //https://github.com/virendersran01/cmp-delivery-application

                    @Composable
                    expect fun MapView(
                        vendors: List<Vendor>,
                        onItemClick: (Vendor) -> Unit,
                        modifier: Modifier = Modifier
                    )

                    //iosMain
                    import platform.UIKit.UIViewController

                    interface NativeViewFactory {
                        fun createMapView(
                            vendors: List<Vendor>,
                            onItemClick: (Vendor) -> Unit
                        ): UIViewController
                    }

                    import LocalNativeViewFactory
                    import androidx.compose.foundation.border
                    import androidx.compose.foundation.layout.fillMaxSize
                    import androidx.compose.runtime.Composable
                    import androidx.compose.ui.Modifier
                    import androidx.compose.ui.graphics.Color
                    import androidx.compose.ui.interop.UIKitViewController
                    import androidx.compose.ui.unit.dp
                    import kotlinx.cinterop.ExperimentalForeignApi

                    @OptIn(ExperimentalForeignApi::class)
                    @Composable
                    actual fun MapView(
                        vendors: List<Vendor>,
                        onItemClick: (Vendor) -> Unit,
                        modifier: Modifier
                    ) {

                        val nativeViewFactory = LocalNativeViewFactory.current
                        UIKitViewController(
                            modifier = Modifier
                                .fillMaxSize()
                                .border(1.dp, Color.Black),
                            factory = { nativeViewFactory.createMapView(vendors, onItemClick) },
                            update = {}
                        )
                    }

                    import androidx.compose.runtime.CompositionLocalProvider
                    import androidx.compose.runtime.staticCompositionLocalOf
                    import androidx.compose.ui.window.ComposeUIViewController

                    val LocalNativeViewFactory = staticCompositionLocalOf<NativeViewFactory> {
                        error("LocalNativeViewFactory not provided")
                    }

                    fun MainViewController(
                        nativeViewFactory: NativeViewFactory
                    ) = ComposeUIViewController {
                        CompositionLocalProvider(LocalNativeViewFactory provides nativeViewFactory) {
                            App()
                        }
                    }

                    import SwiftUI
                    import MapKit
                    import ComposeApp

                    struct NativeMapView: View {

                        var places = [Vendor]()
                        var onTap:(Vendor)->Void = {_ in}

                        @State private var region = MKCoordinateRegion(
                            center: CLLocationCoordinate2D(latitude: 23.0225, longitude: 72.5714),
                            span: MKCoordinateSpan(latitudeDelta: 0.05, longitudeDelta: 0.05)
                        )
                        var body: some View {
                            Map(coordinateRegion: $region, showsUserLocation: true, annotationItems: places, annotationContent: { item in
                                MapAnnotation(coordinate: CLLocationCoordinate2D(latitude: item.location.lat, longitude: item.location.lng)){
                                    Image(systemName: "house.circle.fill")
                                        .font(/*@START_MENU_TOKEN@*/.title/*@END_MENU_TOKEN@*/)
                                        .foregroundColor(.red).onTapGesture {
                                            onTap(item)
                                        }
                                }
                            })
                        }
                    }

                    extension Vendor:Identifiable {}

                    import UIKit
                    import SwiftUI
                    import ComposeApp

                    struct ComposeView: UIViewControllerRepresentable {
                        func makeUIViewController(context: Context) -> UIViewController {
                            MainViewControllerKt.MainViewController(nativeViewFactory: IOSNativeView.shared)
                        }

                        func updateUIViewController(_ uiViewController: UIViewController, context: Context) {}
                    }

                    class IOSNativeView : NativeViewFactory{
                        static let shared = IOSNativeView()

                        func createMapView(vendors: [Vendor], onItemClick: @escaping (Vendor) -> Void) -> UIViewController {
                            let map = NativeMapView(places: vendors, onTap: onItemClick)
                            return UIHostingController(rootView: map)
                        }
                    }

                    struct ContentView: View {
                        var body: some View {
                            ComposeView()
                        }
                    }

                    import SwiftUI
                    @main
                    struct iOSApp: App {
                    	var body: some Scene {
                    		WindowGroup {
                    			ContentView()
                    		}
                    	}
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Date Format",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    import kotlinx.datetime.Instant

                    @Suppress("NO_ACTUAL_FOR_EXPECT")
                    expect class DateHandler {
                        val formatOptions: List<String>

                        fun getDateFormat(format: String): (Instant) -> String

                        val dateTimeFormat: (Instant) -> String
                    }

                    //iosMain
                    import androidx.compose.ui.text.intl.Locale
                    import ca.gosyer.jui.core.lang.toPlatform
                    import kotlinx.datetime.Instant
                    import kotlinx.datetime.toNSDate
                    import me.tatarka.inject.annotations.Inject
                    import platform.Foundation.NSDateFormatter
                    import platform.Foundation.NSDateFormatterNoStyle
                    import platform.Foundation.NSDateFormatterShortStyle

                    actual class DateHandler
                        @Inject
                        constructor() {
                            actual val formatOptions by lazy {
                                listOf(
                                    "",
                                    "MM/dd/yy",
                                    "dd/MM/yy",
                                    "yyyy-MM-dd",
                                )
                            }

                            actual fun getDateFormat(format: String): (Instant) -> String =
                                when (format) {
                                    "" -> NSDateFormatter()
                                        .apply {
                                            setDateStyle(NSDateFormatterShortStyle)
                                            setTimeStyle(NSDateFormatterNoStyle)
                                            setLocale(Locale.current.toPlatform())
                                        }

                                    else -> NSDateFormatter()
                                        .apply {
                                            setDateFormat(format)
                                        }
                                }.let { formatter ->
                                    {
                                        formatter.stringFromDate(it.toNSDate())
                                    }
                                }

                            actual val dateTimeFormat: (Instant) -> String by lazy {
                                NSDateFormatter()
                                    .apply {
                                        setDateStyle(NSDateFormatterShortStyle)
                                        setTimeStyle(NSDateFormatterShortStyle)
                                        setLocale(Locale.current.toPlatform())
                                    }
                                    .let { formatter ->
                                        {
                                            formatter.stringFromDate(it.toNSDate())
                                        }
                                    }
                            }
                        }

                        //androidMain

                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import kotlinx.serialization.Serializable

                    @Serializable
                    expect object DateTime {
                        fun getFormattedDate(
                            timestamp: String,
                            inputFormat: String,
                            outputFormat: String
                        ): String

                        fun formatTimeStamp(
                            timeStamp: Long,
                            outputFormat: String = "yyyy-MM-dd"
                        ): String

                        fun getDateInMilliSeconds(timeStamp: String, inputFormat: String): Long

                        fun getCurrentTimeInMilliSeconds(): Long

                        fun getForwardedDate(
                            forwardedDaya: Int = 0,
                            forwardedMonth: Int = 0,
                            outputFormat: String = "yyyy_MM_dd_T_HH_mm_ss"
                        ): String

                        fun getTimestampInMillisFromString(
                            timestamp: String,
                            inputFormat: String
                        ): Long
                    }

                    //iosMain
                    import kotlinx.serialization.Serializable
                    import platform.Foundation.*

                    @Serializable
                    actual object DateTime {
                        actual fun getFormattedDate(
                            timestamp: String,
                            inputFormat: String,
                            outputFormat: String
                        ): String {
                            val df = NSDateFormatter().apply {
                                dateFormat = inputFormat
                                timeZone = NSTimeZone.timeZoneWithAbbreviation("GMT")!!
                            }

                            val date = df.dateFromString(timestamp)
                            df.timeZone = NSTimeZone.localTimeZone
                            df.dateFormat = outputFormat

                            return df.stringFromDate(date!!)
                        }

                        actual fun formatTimeStamp(timeStamp: Long, outputFormat: String): String {
                            val formatter = NSDateFormatter().apply {
                                dateFormat = outputFormat
                                timeZone = NSTimeZone.localTimeZone
                            }
                            val date = NSDate(timeStamp.toDouble() / 1000)
                            return formatter.stringFromDate(date)
                        }

                        actual fun getDateInMilliSeconds(timeStamp: String, inputFormat: String): Long {
                            if (timeStamp.trim().isEmpty()) return getCurrentTimeInMilliSeconds()

                            val df = NSDateFormatter().apply {
                                dateFormat = inputFormat
                            }
                            val date = df.dateFromString(timeStamp)
                            return (date!!.timeIntervalSince1970 * 1000).toLong()
                        }

                        actual fun getCurrentTimeInMilliSeconds(): Long {
                            return (NSDate().timeIntervalSince1970 * 1000).toLong()
                        }

                        actual fun getForwardedDate(
                            forwardedDaya: Int,
                            forwardedMonth: Int,
                            outputFormat: String
                        ): String {
                            val calendar = NSCalendar.currentCalendar
                            val currentDate = NSDate()
                            val components = NSDateComponents().apply {
                                day = forwardedDaya.toLong()
                                month = forwardedMonth.toLong()
                            }

                            val forwardDate = calendar.dateByAddingComponents(components, currentDate, NSCalendarUnitDay)
                            val dateFormatter = NSDateFormatter().apply {
                                dateFormat = outputFormat
                            }

                            return dateFormatter.stringFromDate(forwardDate ?: currentDate)
                        }

                        actual fun getTimestampInMillisFromString(
                            timestamp: String,
                            inputFormat: String
                        ): Long {
                            TODO("Not yet implemented")
                        }
                    }

                    //androidMain
                    import kotlinx.serialization.Serializable
                    import java.text.SimpleDateFormat
                    import java.util.*

                    @Serializable
                    actual object DateTime {
                        @JvmStatic
                        actual fun getFormattedDate(
                            timestamp: String,
                            inputFormat: String,
                            outputFormat: String
                        ): String {
                            val dateFormatter = SimpleDateFormat(outputFormat, Locale.getDefault())
                            dateFormatter.timeZone = TimeZone.getTimeZone("GMT")

                            val parser = SimpleDateFormat(inputFormat, Locale.getDefault())
                            parser.timeZone = TimeZone.getTimeZone("GMT")

                            return try {
                                val date = parser.parse(timestamp)
                                if (date != null) {
                                    dateFormatter.timeZone = TimeZone.getDefault()
                                    dateFormatter.format(date)
                                } else {
                                    timestamp
                                }
                            } catch (e: Exception) {
                                e.printStackTrace()
                                timestamp
                            }
                        }

                        actual fun formatTimeStamp(timeStamp: Long, outputFormat: String): String {
                            val sdf = SimpleDateFormat(outputFormat, Locale.getDefault())
                            return sdf.format(Date(timeStamp * 1000))
                        }

                        actual fun getDateInMilliSeconds(timeStamp: String, inputFormat: String): Long {
                            if (timeStamp.trim().isEmpty()) return getCurrentTimeInMilliSeconds()

                            val parser = SimpleDateFormat(inputFormat, Locale.getDefault())
                            parser.timeZone = TimeZone.getTimeZone("GMT")
                            return parser.parse(timeStamp)?.time ?: 0
                        }

                        actual fun getCurrentTimeInMilliSeconds(): Long {
                            return System.currentTimeMillis()
                        }

                        actual fun getForwardedDate(
                            forwardedDaya: Int,
                            forwardedMonth: Int,
                            outputFormat: String
                        ): String {
                            val calendar = Calendar.getInstance().apply {
                                add(Calendar.DAY_OF_MONTH, forwardedDaya)
                                add(Calendar.MONTH, forwardedMonth)
                            }

                            val dateFormat = SimpleDateFormat(outputFormat, Locale.ENGLISH)
                            return dateFormat.format(Date(calendar.timeInMillis))
                        }

                        actual fun getTimestampInMillisFromString(timestamp: String, inputFormat: String): Long {
                            if (timestamp.trim().isEmpty()) return 0L // Or handle empty string differently

                            val parser = SimpleDateFormat(inputFormat, Locale.getDefault())
                            parser.timeZone = TimeZone.getTimeZone("GMT")
                            return try {
                                parser.parse(timestamp)?.time ?: 0L
                            } catch (e: Exception) {
                                e.printStackTrace()
                                0L // Or handle parsing errors differently
                            }
                        }

                        fun convertLongToCalendar(timestamp: Long): Calendar {
                            val calendar = Calendar.getInstance()
                            calendar.timeInMillis = timestamp
                            return calendar
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    expect fun LocalDateTime.format(): String

                    //androidMain
                    import kotlinx.datetime.LocalDateTime
                    import kotlinx.datetime.TimeZone
                    import kotlinx.datetime.toInstant
                    import java.text.DateFormat
                    import java.util.Calendar
                    import java.util.Locale

                    actual fun LocalDateTime.format(): String {
                        val dateFormat = DateFormat.getDateTimeInstance(
                            DateFormat.LONG,
                            DateFormat.SHORT,
                            Locale.getDefault(),
                        )
                        val calendar = Calendar.getInstance().apply {
                            timeInMillis = toInstant(TimeZone.currentSystemDefault()).toEpochMilliseconds()
                        }
                        return dateFormat.format(calendar.time)
                    }

                    //iosMain
                    import kotlinx.datetime.LocalDateTime
                    import kotlinx.datetime.TimeZone
                    import kotlinx.datetime.toInstant
                    import kotlinx.datetime.toNSDate
                    import platform.Foundation.NSDateFormatter
                    import platform.Foundation.NSDateFormatterLongStyle
                    import platform.Foundation.NSDateFormatterShortStyle

                    actual fun LocalDateTime.format(): String {
                        val dateFormatter = NSDateFormatter()
                        dateFormatter.dateStyle = NSDateFormatterLongStyle
                        dateFormatter.timeStyle = NSDateFormatterShortStyle
                        val nsDate = toInstant(TimeZone.currentSystemDefault()).toNSDate()
                        return dateFormatter.stringFromDate(nsDate)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Now DateTime

                    interface DateHandler {
                        fun getToday(): String
                    }

                    expect fun getDateHandler(): DateHandler

                    import java.text.SimpleDateFormat
                    import java.util.Date
                    import java.util.Locale

                    class AndroidDateHandler: DateHandler {
                        override fun getToday(): String {
                            val sdf = SimpleDateFormat("yyyy-MM-dd HH:mm", Locale.US)

                            return sdf.format(Date())
                        }
                    }

                    actual fun getDateHandler(): DateHandler = AndroidDateHandler()

                    import platform.Foundation.*

                    class IOSDateHandler: DateHandler {
                        override fun getToday(): String {
                            val dateFormatter = NSDateFormatter()
                            dateFormatter.dateFormat = "yyyy-MM-dd HH:mm"
                            val now = NSDate()
                            return dateFormatter.stringFromDate(now)
                        }
                    }

                    actual fun getDateHandler(): DateHandler = IOSDateHandler()
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    expect fun formatDate(dateString: String): String

                    import java.text.SimpleDateFormat
                    import java.util.Locale

                    actual fun formatDate(dateString: String): String {
                        // Update the input format to match the provided date string
                        val inputFormat = SimpleDateFormat("yyyy-MM-dd'T'HH:mm:ss", Locale.getDefault())
                        val outputFormat = SimpleDateFormat("dd MMM yy", Locale.getDefault())
                        return try {
                            val date = inputFormat.parse(dateString)
                            date?.let { outputFormat.format(it) } ?: ""
                        } catch (e: Exception) {
                            e.printStackTrace()
                            ""
                        }
                    }

                    import platform.Foundation.NSDateFormatter
                    import platform.Foundation.NSLocale
                    import platform.Foundation.NSTimeZone
                    import platform.Foundation.currentLocale
                    import platform.Foundation.localTimeZone

                    actual fun formatDate(dateString: String): String {
                        val possibleFormats = listOf(
                            "yyyy-MM-dd'T'HH:mm:ss.SSSSSS",
                            "yyyy-MM-dd'T'HH:mm:ss.SSS",
                            "yyyy-MM-dd'T'HH:mm:ss",
                            "yyyy-MM-dd HH:mm:ss.SSSSSS",
                            "yyyy-MM-dd HH:mm:ss.SSS",
                            "yyyy-MM-dd HH:mm:ss"
                        )

                        val outputFormatter = NSDateFormatter().apply {
                            dateFormat = "dd MMM yy"
                            locale = NSLocale.currentLocale
                            timeZone = NSTimeZone.localTimeZone
                        }

                        for (format in possibleFormats) {
                            val inputFormatter = NSDateFormatter().apply {
                                dateFormat = format
                                locale = NSLocale.currentLocale
                                timeZone = NSTimeZone.localTimeZone
                            }

                            val date = inputFormatter.dateFromString(dateString)
                            if (date != null) {
                                return outputFormatter.stringFromDate(date)
                            }
                        }

                        return ""
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Get LocalTime

                    expect fun getLocalTime(): String

                    //iosMain
                    import platform.Foundation.NSDate
                    import platform.Foundation.NSDateFormatter
                    import platform.Foundation.NSTimeZone
                    import platform.Foundation.localTimeZone
                    import platform.Foundation.timeIntervalSince1970

                    actual fun getLocalTime(): String {
                        // Returns e.g. 20:37:00.866
                        val timeStamp = (NSDate().timeIntervalSince1970 * 1000).toLong()
                        val outputFormat = "H:m:s.S"
                        val formatter = NSDateFormatter().apply {
                            dateFormat = outputFormat
                            timeZone = NSTimeZone.localTimeZone
                        }
                        val date = NSDate(timeStamp.toDouble() / 1000)
                        return formatter.stringFromDate(date)
                    }

                    //androidMain
                    actual fun getLocalTime(): String {
                        // Returns e.g. 20:37:00.866
                        val sdf = SimpleDateFormat("H:m:s.S", Locale.getDefault())
                        return sdf.format(Date()).toString()
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    expect object DateUtils {
                        fun getComingSoonDates(
                            monthPeriod: Int = Constants.MONTH_PERIOD_COMING_SOON,
                        ): Pair<String, String>

                        fun getCurrentTimeMillis(): Long
                    }

                    object Constants {
                        const val MONTH_PERIOD_COMING_SOON = 1
                    }

                    //iosMain
                    import platform.Foundation.NSCalendar
                    import platform.Foundation.NSCalendarUnitMonth
                    import platform.Foundation.NSDate
                    import platform.Foundation.NSDateFormatter
                    import platform.Foundation.NSLocale
                    import platform.Foundation.currentLocale
                    import platform.Foundation.timeIntervalSince1970

                    actual object DateUtils {
                        actual fun getComingSoonDates(
                            monthPeriod: Int,
                        ): Pair<String, String> {
                            val calendar = NSCalendar.currentCalendar

                            val dateFormatter = NSDateFormatter().apply {
                                dateFormat = "yyyy-MM-dd"
                                locale = NSLocale.currentLocale
                            }
                            val releaseDateGte = dateFormatter.stringFromDate(NSDate())
                            val releaseDateLte = calendar.dateByAddingUnit(
                                NSCalendarUnitMonth, monthPeriod.toLong(), NSDate(), 0u,
                            )?.let {
                                dateFormatter.stringFromDate(it)
                            } ?: releaseDateGte
                            return Pair(releaseDateGte, releaseDateLte)
                        }

                        actual fun getCurrentTimeMillis(): Long = (NSDate().timeIntervalSince1970 * 1000).toLong()
                    }

                    //androidMain
                    import java.text.SimpleDateFormat
                    import java.util.Calendar
                    import java.util.Locale

                    actual object DateUtils {
                        actual fun getComingSoonDates(
                            monthPeriod: Int,
                        ): Pair<String, String> {
                            val calendar = Calendar.getInstance()

                            val dateFormat = SimpleDateFormat("yyyy-MM-dd", Locale.getDefault())
                            val releaseDateGte = dateFormat.format(calendar.time)
                            calendar.add(Calendar.MONTH, monthPeriod)
                            val releaseDateLte = dateFormat.format(calendar.time)
                            return Pair(releaseDateGte, releaseDateLte)
                        }

                        actual fun getCurrentTimeMillis(): Long = System.currentTimeMillis()
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import kotlinx.datetime.Clock
                    import kotlinx.datetime.Instant

                    expect object DateFormatHelper {
                        fun getFormattedDate(
                            iso8601Timestamp: Long,
                            format: String
                        ): String
                    }

                    //iosMain
                    import platform.Foundation.NSDate
                    import platform.Foundation.NSDateFormatter
                    import platform.Foundation.NSLocale
                    import platform.Foundation.dateWithTimeIntervalSince1970

                    actual object DateFormatHelper {
                        actual fun getFormattedDate(
                            iso8601Timestamp: Long,
                            format: String
                        ): String {
                            val date = NSDate.dateWithTimeIntervalSince1970(iso8601Timestamp / 1000.0)
                            val dateFormatter = NSDateFormatter()
                            dateFormatter.dateFormat = format
                            dateFormatter.locale = NSLocale(localeIdentifier = "en_US")
                            return dateFormatter.stringFromDate(date)
                        }
                    }

                    //androidMain
                    import java.text.SimpleDateFormat
                    import java.util.Locale

                    actual object DateFormatHelper {
                        actual fun getFormattedDate(
                            iso8601Timestamp: Long,
                            format: String
                        ): String {
                            val dateFormat = SimpleDateFormat(format, Locale.US)
                            return dateFormat.format(iso8601Timestamp)
                        }
                    }

                    fun getCurrentTimeAsLong(): Long {
                        val currentInstant: Instant = Clock.System.now()
                        return currentInstant.toEpochMilliseconds()
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    expect object DateTime {
                        fun getFormattedDate(
                            timestamp: String,
                        ): String
                    }

                    //iOSMain
                    import platform.Foundation.NSDate
                    import platform.Foundation.NSDateFormatter
                    import platform.Foundation.NSTimeZone

                    actual object DateTime {
                        actual fun getFormattedDate(timestamp: String): String {
                            val df = NSDateFormatter()
                            val timestampFormat = "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"
                            val outputFormat = "MMM dd, yyyy HH:mm:ss"

                            df.dateFormat = timestampFormat

                            // Set the time zone to GMT (or UTC)
                            df.timeZone = NSTimeZone.timeZoneWithAbbreviation("GMT")

                            // Parse the GMT timestamp into an NSDate
                            val date = df.dateFromString(timestamp)

                            // Create a date formatter for the local time zone
                            df.timeZone = NSTimeZone.localTimeZone
                            df.dateFormat = outputFormat

                            // Format the NSDate into a string in the local time zone
                            return df.stringFromDate(date)
                        }
                    }

                    //androidMain
                    import java.text.SimpleDateFormat
                    import java.util.Locale
                    import java.util.TimeZone

                    actual object DateTime {
                        @JvmStatic
                        actual fun getFormattedDate(
                            timestamp: String,
                        ): String {
                            val timestampFormat = "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"
                            val outputFormat = "MMM dd, yyyy HH:mm:ss"

                            val dateFormatter = SimpleDateFormat(outputFormat, Locale.getDefault())
                            dateFormatter.timeZone = TimeZone.getTimeZone("GMT")

                            val parser = SimpleDateFormat(timestampFormat, Locale.getDefault())
                            parser.timeZone = TimeZone.getTimeZone("GMT")

                            try {
                                val date = parser.parse(timestamp)
                                if (date != null) {
                                    dateFormatter.timeZone = TimeZone.getDefault()
                                    return dateFormatter.format(date)
                                }
                            } catch (e: Exception) {
                                // Handle parsing error
                                e.printStackTrace()
                            }

                            // If parsing fails, return the original timestamp
                            return timestamp
                        }
                    }

                    //UI
                    val timestamp = "2023-09-22T10:18:15.311Z"
                    val formattedDate = DateTime.getFormattedDate(timestamp)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //NumberFormat
                    expect fun formatNumber(number: Long): String

                    actual fun formatNumber(number: Long): String {
                        return try {
                                val numberFormat = NumberFormat.getNumberInstance(Locale.getDefault())
                                numberFormat.format (number)
                            }
                            catch(e: Exception) { "" }
                        }
                    }

                    actual fun formatNumber(number: Long): String {
                        val nsNumber = NSNumber.numberWithLong(number)

                        return try {
                            val numberFormatter = NSNumberFormatter().apply {
                                locale = NSLocale.currentLocale
                                numberStyle = NSNumberFormatterDecimalStyle
                            }

                            numberFormatter.stringFromNumber(nsNumber) ?: ""
                        } catch(e: Exception) { "" }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "CountDownTimer",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em).fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    expect class CountDownTimer(
                        initialMillis: Long,
                        intervalMillis: Long,
                        onTick: (days: Long, hours: Long, minutes: Long, seconds: Long) -> Unit,
                        onFinish: () -> Unit
                    ) {
                        fun start()
                        fun cancel()
                    }

                    //Android
                    actual class CountDownTimer actual constructor(
                        private val initialMillis: Long,
                        private val intervalMillis: Long,
                        private val onTick: (days: Long, hours: Long, minutes: Long, seconds: Long) -> Unit,
                        private val onFinish: () -> Unit
                    ) {
                        private var androidCountDownTimer: android.os.CountDownTimer? = null

                        actual fun start() {
                            androidCountDownTimer = object : android.os.CountDownTimer(initialMillis, intervalMillis) {
                                override fun onTick(millisUntilFinished: Long) {
                                    val days = millisUntilFinished / (24 * 60 * 60 * 1000)
                                    val hours = (millisUntilFinished % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000)
                                    val minutes = (millisUntilFinished % (60 * 60 * 1000)) / (60 * 1000)
                                    val seconds = (millisUntilFinished % (60 * 1000)) / 1000
                                    onTick(days, hours, minutes, seconds)
                                }

                                override fun onFinish() {
                                    onFinish()
                                }
                            }.start()
                        }

                        actual fun cancel() {
                            androidCountDownTimer?.cancel()
                        }
                    }

                    //iOS
                    actual class CountDownTimer actual constructor(
                        private val initialMillis: Long,
                        private val intervalMillis: Long,
                        private val onTick: (days: Long, hours: Long, minutes: Long, seconds: Long) -> Unit,
                        private val onFinish: () -> Unit
                    ) {
                        private var iosTimer: NSTimer? = null
                        private var endTimeMillis: Long = initialMillis

                        @ObjCAction
                        fun onTimerTick(timer: NSTimer) {
                            val currentTimeMillis = (NSDate().timeIntervalSince1970 * 1000).toLong()
                            val millisUntilFinished = endTimeMillis - currentTimeMillis

                            if (millisUntilFinished <= 0) {
                                onTimerFinish(timer)
                            } else {
                                val days = millisUntilFinished / (24 * 60 * 60 * 1000)
                                val hours = (millisUntilFinished % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000)
                                val minutes = (millisUntilFinished % (60 * 60 * 1000)) / (60 * 1000)
                                val seconds = (millisUntilFinished % (60 * 1000)) / 1000
                                onTick(days, hours, minutes, seconds)
                            }
                        }

                        @ObjCAction
                        fun onTimerFinish(timer: NSTimer) {
                            iosTimer?.invalidate()
                            iosTimer = null
                            onFinish()
                        }

                        actual fun start() {
                            endTimeMillis = (NSDate().timeIntervalSince1970 * 1000).toLong() + initialMillis
                            iosTimer = NSTimer.scheduledTimerWithTimeInterval(
                                interval = intervalMillis / 1000.0,
                                repeats = true
                            ) { timer ->
                                onTimerTick(timer)
                            }
                        }

                        actual fun cancel() {
                            iosTimer?.invalidate()
                            iosTimer = null
                        }
                    }

                    //UI
                    val countDownTimer = CountDownTimer(
                        initialMillis = 3600000, // 1 hour
                        intervalMillis = 1000, // 1 second
                        onTick = { days, hours, minutes, seconds ->

                        },
                        onFinish = {
                            println("Countdown finished!")
                        }
                    )

                    countDownTimer.start()
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //WindowSize
                    import androidx.compose.runtime.Composable

                    data class WindowSize(val width: Int, val height: Int)

                    @Composable
                    expect fun getWindowSize(): WindowSize

                    //desktopMain
                    import androidx.compose.runtime.Composable
                    import androidx.compose.ui.platform.LocalDensity
                    import java.awt.Toolkit

                    @Composable
                    actual fun getWindowSize(): WindowSize {
                        val screenSize = Toolkit.getDefaultToolkit().screenSize
                        val density = LocalDensity.current.density
                        val width = screenSize.width * density
                        val height = screenSize.height * density
                        return WindowSize(width.toInt(), height.toInt())
                    }

                    //iosMain
                    import androidx.compose.runtime.Composable
                    import androidx.compose.ui.platform.LocalDensity
                    import kotlinx.cinterop.ExperimentalForeignApi
                    import kotlinx.cinterop.useContents
                    import platform.UIKit.UIScreen

                    @OptIn(ExperimentalForeignApi::class)
                    @Composable
                    actual fun getWindowSize(): WindowSize {
                        val screenBounds = UIScreen.mainScreen.bounds
                        val density = LocalDensity.current.density
                        val width = screenBounds.useContents { size.width } * density
                        val height = screenBounds.useContents { size.height } * density
                        return WindowSize(width.toInt(), height.toInt())
                    }

                    //androidMain
                    import androidx.compose.runtime.Composable
                    import androidx.compose.ui.platform.LocalConfiguration
                    import androidx.compose.ui.platform.LocalDensity

                    @Composable
                    actual fun getWindowSize(): WindowSize {
                        val configuration = LocalConfiguration.current
                        val screenWidthDp = configuration.screenWidthDp
                        val screenHeightDp = configuration.screenHeightDp
                        val density = LocalDensity.current.density
                        val width = screenWidthDp * density
                        val height = screenHeightDp * density
                        return WindowSize(width.toInt(), height.toInt())
                    }

                    //wasmJsMain
                    import androidx.compose.runtime.Composable
                    import kotlinx.browser.window

                    @Composable
                    actual fun getWindowSize(): WindowSize {
                        return WindowSize(window.innerWidth, window.innerHeight)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //BackHandler

                    import androidx.compose.runtime.Composable

                    @Composable
                    expect fun BackHandler(isEnabled: Boolean, onBack: ()-> Unit)

                    //androidMain
                    import androidx.compose.runtime.Composable
                    import androidx.activity.compose.BackHandler as AndroidBackHandler

                    @Composable
                    actual fun BackHandler(isEnabled: Boolean, onBack: () -> Unit) {
                        AndroidBackHandler(enabled = isEnabled) {
                            onBack()
                        }
                    }

                    //iosMain
                    import androidx.compose.runtime.Composable
                    import platform.UIKit.UIApplication
                    import platform.UIKit.UIViewController
                    import platform.UIKit.navigationController

                    @Composable
                    actual fun BackHandler(isEnabled: Boolean, onBack: () -> Unit) {
                        if (!isEnabled) {
                            val currentViewController = findCurrentViewController()
                            val navigationController = currentViewController?.navigationController

                            navigationController?.popViewControllerAnimated(animated = true)

                            onBack()
                        }
                    }

                    private fun findCurrentViewController(): UIViewController? {
                        val keyWindow = UIApplication.sharedApplication.keyWindow
                        return keyWindow?.rootViewController
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Toast
                    expect fun showToast(message: String)

                    class MyApplication : Application() {
                        companion object {
                            var instance: MyApplication? = null
                                private set
                        }

                        override fun onCreate() {
                            super.onCreate()
                            instance = this
                        }
                    }

                    import android.widget.Toast

                    actual fun showToast(message: String) {
                        val context = MyApplication.instance
                        context?.let {
                            Toast.makeText(it, message, Toast.LENGTH_SHORT).show()
                        }
                    }

                    import platform.UIKit.UIAlertAction
                    import platform.UIKit.UIAlertActionStyleDefault
                    import platform.UIKit.UIAlertController
                    import platform.UIKit.UIAlertControllerStyleAlert
                    import platform.UIKit.UIApplication


                    actual fun showToast(message: String) {
                        val controller = UIAlertController.alertControllerWithTitle(
                            title = null,
                            message = message,
                            preferredStyle = UIAlertControllerStyleAlert
                        )
                        controller.addAction(
                            UIAlertAction.actionWithTitle(
                                title = "OK",
                                style = UIAlertActionStyleDefault,
                                handler = null
                            )
                        )

                        UIApplication.sharedApplication.keyWindow?.rootViewController?.presentViewController(controller, animated = true, completion = null)
                    }
                    """.trimIndent()
            )

            //https://github.com/narendraanjana09/CMPRazorpayDemo.git
            //https://github.com/meradeveloper/kmp-razorpay-integration
            KotlinCode(
                code = $$"""
                    //RazorPay
                    
                    plugins {
                        alias(libs.plugins.kotlinMultiplatform)
                        alias(libs.plugins.androidApplication)
                        alias(libs.plugins.composeMultiplatform)
                        alias(libs.plugins.composeCompiler)
                        alias(libs.plugins.kotlinCocoapods)
                    }

                    kotlin {
                        androidTarget {
                            compilerOptions {
                                jvmTarget.set(JvmTarget.JVM_11)
                            }
                        }

                        listOf(
                            iosX64(),
                            iosArm64(),
                            iosSimulatorArm64()
                        ).forEach { iosTarget ->
                            iosTarget.binaries.framework {
                                baseName = "ComposeApp"
                                isStatic = true
                                freeCompilerArgs += listOf("-Xadd-light-debug=enable")
                            }
                        }

                        cocoapods {
                            version = "1.0"
                            summary = "Shared module for VirtualLibrary"
                            homepage = "https://github.com/narendraanjana09/virtuallibrary" // Replace with your project's homepage
                            ios.deploymentTarget = "13.0" // Match your project's deployment target
                            podfile = project.file("../iosApp/Podfile") // Point to your iosApp's Podfile

                            pod("razorpay-pod") {
                                version = "~> 1.4.3"
                                moduleName = "Razorpay"
                                extraOpts += listOf("-compiler-option", "-fmodules")
                            }

                            framework {
                                baseName = "ComposeApp"
                                isStatic = true
                            }
                        }


                        sourceSets {
                            androidMain.dependencies {
                                implementation(compose.preview)
                                implementation(libs.androidx.activity.compose)
                                implementation("com.razorpay:checkout:1.6.36")
                            }
                            commonMain.dependencies {
                                implementation(compose.runtime)
                                implementation(compose.foundation)
                                implementation(compose.material3)
                                implementation(compose.ui)
                                implementation(compose.components.resources)
                                implementation(compose.components.uiToolingPreview)
                                implementation(libs.androidx.lifecycle.viewmodelCompose)
                                implementation(libs.androidx.lifecycle.runtimeCompose)
                            }
                            commonTest.dependencies {
                                implementation(libs.kotlin.test)
                            }
                        }
                    }

                    val localProperties = Properties()
                    val localPropertiesFile = project.rootProject.file("local.properties")
                    if (localPropertiesFile.exists()) {
                        localProperties.load(localPropertiesFile.inputStream())
                    }


                    android {
                        namespace = "in.demo.razorpay.razorpaydemo"
                        compileSdk = libs.versions.android.compileSdk.get().toInt()

                        defaultConfig {
                            applicationId = "in.demo.razorpay.razorpaydemo"
                            minSdk = libs.versions.android.minSdk.get().toInt()
                            targetSdk = libs.versions.android.targetSdk.get().toInt()
                            versionCode = 1
                            versionName = "1.0"

                            buildConfigField(
                                "String",
                                "RAZORPAY_KEY_ID",
                                "\"${localProperties.getProperty("RAZORPAY_KEY_ID") ?: ""}\""
                            )
                        }
                        buildFeatures {
                            buildConfig = true
                        }

                        packaging {
                            resources {
                                excludes += "/META-INF/{AL2.0,LGPL2.1}"
                            }
                        }
                        buildTypes {
                            getByName("release") {
                                isMinifyEnabled = false
                            }
                        }
                        compileOptions {
                            sourceCompatibility = JavaVersion.VERSION_11
                            targetCompatibility = JavaVersion.VERSION_11
                        }
                    }

                    dependencies {
                        debugImplementation(compose.uiTooling)
                    }
                    
                    #Kotlin
                    kotlin.code.style=official
                    kotlin.daemon.jvmargs=-Xmx3072M
                    #Gradle
                    org.gradle.jvmargs=-Xmx4096M -Dfile.encoding=UTF-8
                    org.gradle.configuration-cache=true
                    org.gradle.caching=true
                    #Android
                    android.nonTransitiveRClass=true
                    android.useAndroidX=true
                    kotlin.native.cacheKind=none
                    kotlin.apple.xcodeCompatibility.nowarn=true
                    kotlin.apple.deprecated.allowUsingEmbedAndSignWithCocoaPodsDependencies=true
                    
                    //RazorpayLauncher.kt
                    sealed class PaymentResult {
                        data class Success(val paymentId: String, val razorpaySignature: String) : PaymentResult()
                        data class Error(val code: Int, val description: String) : PaymentResult()
                    }

                    expect class RazorpayLauncher {
                        suspend fun startPayment(
                            orderId: String,
                            amount: Int,
                            userName: String,
                            userEmail: String,
                            description: String
                        ): PaymentResult
                    }
                    
                    @Composable
                    @Preview
                    fun App(
                        razorpayLauncher: RazorpayLauncher
                    ) {
                        val coroutineScope = rememberCoroutineScope()
                        MaterialTheme {
                            Column(
                                modifier = Modifier
                                    .background(MaterialTheme.colorScheme.primaryContainer)
                                    .safeContentPadding()
                                    .fillMaxSize(),
                                horizontalAlignment = Alignment.CenterHorizontally,
                            ) {
                                Button(
                                    onClick = {
                                        coroutineScope.launch {
                                            val result = razorpayLauncher.startPayment(
                                                orderId = "pass_your_order_id_here",
                                                amount = 100,
                                                userName = "Narendra",
                                                userEmail = "narendraanjana09@gmail.com",
                                                description = "Debug Description"
                                            )
                                            when (result) {
                                                is PaymentResult.Success -> {

                                                }

                                                is PaymentResult.Error -> {
                                                    val message = when (result.code) {
                                                        0 -> "Payment Cancelled"
                                                        else -> result.description
                                                    }

                                                }
                                            }
                                        }
                                    }
                                ) {
                                    Text("Launch RazorPay")
                                }
                            }
                        }
                    }
                    
                    //android
                    class MainActivity : ComponentActivity(), PaymentResultWithDataListener {
                        private lateinit var razorpayLauncher: RazorpayLauncher
                        override fun onCreate(savedInstanceState: Bundle?) {
                            enableEdgeToEdge()
                            super.onCreate(savedInstanceState)
                            razorpayLauncher = RazorpayLauncher(this)
                            setContent {
                                App(
                                    razorpayLauncher = razorpayLauncher
                                )
                            }
                        }

                        override fun onPaymentSuccess(razorpayPaymentID: String?, paymentData: PaymentData?) {
                            PaymentHandler.paymentCallback?.invoke(
                                PaymentResult.Success(
                                    razorpayPaymentID ?: "",
                                    paymentData?.signature ?: ""
                                )
                            )
                        }

                        override fun onPaymentError(code: Int, description: String?, paymentData: PaymentData?) {
                            PaymentHandler.paymentCallback?.invoke(PaymentResult.Error(code, description ?: "Unknown error"))
                        }
                    }
                    
                    object PaymentHandler {
                        var paymentCallback: ((PaymentResult) -> Unit)? = null
                    }
                    
                    import android.app.Activity
                    import com.razorpay.Checkout
                    import com.razorpay.PayloadHelper
                    import kotlinx.coroutines.suspendCancellableCoroutine
                    import kotlin.coroutines.resume

                    actual class RazorpayLauncher(
                        private val activity: Activity
                    ) {
                        actual suspend fun startPayment(
                            orderId: String,
                            amount: Int,
                            userName: String,
                            userEmail: String,
                            description: String
                        ): PaymentResult {
                            return processPayment(activity, orderId, amount, userName, userEmail, description)
                        }
                    }

                    private suspend fun processPayment(
                        activity: Activity,
                        orderId: String,
                        amount: Int,
                        userName: String,
                        userEmail: String,
                        descriptionStr: String
                    ): PaymentResult {
                        return suspendCancellableCoroutine { continuation ->
                            val checkout = Checkout()
                            checkout.setKeyID(BuildConfig.RAZORPAY_KEY_ID)
                            checkout.setImage(R.mipmap.ic_launcher_round)

                            // Set up the listener
                            PaymentHandler.paymentCallback = { paymentResult ->
                                if (continuation.isActive) {
                                    continuation.resume(paymentResult)
                                }
                            }

                            continuation.invokeOnCancellation {
                                PaymentHandler.paymentCallback = null
                            }


                            activity.runOnUiThread {
                                try {
                                    val payloadHelper = PayloadHelper("INR", amount, orderId)
                                        .apply {
                                            name = activity.getString(R.string.app_name)
                                            description = descriptionStr
                                            color = "#407BFF"
                                            retryMaxCount = 4
                                            retryEnabled = true
                                            hideTopBar = true
                                            prefillEmail = userEmail
                                            prefillName = userName
                                        }
                                    checkout.open(activity, payloadHelper.getJson())
                                    // The result will be delivered via the listener
                                } catch (e: Exception) {
                                    if (continuation.isActive) {
                                        continuation.resume(PaymentResult.Error(0, e.message ?: "SDK Error"))
                                    }
                                }
                            }
                        }
                    }
                    
                    //iosMain
                    import androidx.compose.ui.window.ComposeUIViewController

                    fun MainViewController() = ComposeUIViewController {
                        App(
                            razorpayLauncher = RazorpayLauncher()
                        )
                    }
                    
                    import cocoapods.razorpay_pod.RazorpayCheckout
                    import cocoapods.razorpay_pod.RazorpayPaymentCompletionProtocolWithDataProtocol
                    import kotlinx.cinterop.ExperimentalForeignApi
                    import kotlinx.coroutines.suspendCancellableCoroutine
                    import platform.UIKit.*
                    import platform.darwin.NSObject
                    import platform.posix.int32_t
                    import kotlin.coroutines.resume
                    import objcnames.classes.UIViewController as RazorpayUIViewController

                    @OptIn(ExperimentalForeignApi::class)
                    private var paymentDelegate: RazorpayPaymentCompletionProtocolWithDataProtocol? = null

                    actual class RazorpayLauncher {
                        actual suspend fun startPayment(
                            orderId: String,
                            amount: Int,
                            userName: String,
                            userEmail: String,
                            description: String
                        ): PaymentResult {
                            return processPayment(orderId, amount, userName, userEmail, description)
                        }
                    }

                    @OptIn(ExperimentalForeignApi::class)
                    private suspend fun processPayment(
                        orderId: String,
                        amount: Int,
                        userName: String,
                        userEmail: String,
                        description2: String
                    ): PaymentResult = suspendCancellableCoroutine { continuation ->
                        try {
                            val razorpayKey = getRazorpayKeyId()
                            if (razorpayKey.isBlank()) {
                                continuation.resume(
                                    PaymentResult.Error(-2, "Razorpay API Key not found.")
                                )
                                return@suspendCancellableCoroutine
                            }

                            val topController = getTopViewController()
                            if (topController == null) {
                                continuation.resume(
                                    PaymentResult.Error(-3, "Could not find a ViewController to display the payment sheet.")
                                )
                                return@suspendCancellableCoroutine
                            }

                            // 2. Create an instance of our new delegate class
                            val delegate = object : NSObject(), RazorpayPaymentCompletionProtocolWithDataProtocol {
                                override fun onPaymentSuccess(payment_id: String, andData: Map<Any?, *>?) {
                                    val signature = andData?.get("razorpay_signature") as? String

                                    // Ensure we only resume once
                                    if (continuation.isActive) {
                                        // Use signature ?: "" to avoid passing "null" if the signature is missing
                                        continuation.resume(
                                            PaymentResult.Success(
                                                paymentId = payment_id,
                                                razorpaySignature = signature ?: ""
                                            )
                                        )
                                    }
                                    paymentDelegate = null
                                }

                                override fun onPaymentError(
                                    code: int32_t,
                                    description: String,
                                    andData: Map<Any?, *>?
                                ) {
                                    // Ensure we only resume once
                                    if (continuation.isActive) {
                                        continuation.resume(PaymentResult.Error(code, description))
                                    }
                                    paymentDelegate = null
                                }

                            }

                            paymentDelegate = delegate

                            // 3. Initialize Razorpay with the key AND the delegate.
                            val razorpay = RazorpayCheckout.initWithKey(razorpayKey, andDelegateWithData = delegate)
                            val options: Map<Any?, *> = mapOf(
                                "amount" to amount,
                                "currency" to "INR",
                                "order_id" to orderId,
                                "description" to description2,
                                "name" to "Virtual Library",
                                "image" to "https://firebasestorage.googleapis.com/v0/b/virtual-library-4b732.firebasestorage.app/o/extras%2Fapp_icon.jpeg?alt=media&token=a443cf16-4768-4adf-b725-3b2d8ee8dd93",
                                "prefill" to mapOf(
                                    "email" to userEmail,
                                    "contact" to "", // You can add user's phone number here
                                    "name" to userName
                                ),
                                "theme" to mapOf(
                                    "color" to "#407BFF" // A sample theme color
                                )
                            )

                            // 4. Open the Razorpay payment sheet.
                            razorpay.open(options, displayController = topController as RazorpayUIViewController)

                        } catch (e: Exception) {
                            println(e)
                            if (continuation.isActive) {
                                continuation.resume(PaymentResult.Error(1, "Exception: ${e.message}"))
                            }
                        }

                        continuation.invokeOnCancellation {
                            // Cancellation logic if needed
                        }
                    }

                    // Helper to get the key from the iOS app's Info.plist
                    fun getRazorpayKeyId(): String {
                        return platform.Foundation.NSBundle.mainBundle.objectForInfoDictionaryKey("RAZORPAY_KEY_ID") as? String ?: ""
                    }

                    @OptIn(ExperimentalForeignApi::class)
                    private fun getTopViewController(): UIViewController? {
                        val window = UIApplication.sharedApplication.windows.firstOrNull {
                            (it as? UIWindow)?.isKeyWindow() == true
                        } as? UIWindow
                        var topController = window?.rootViewController
                        while (topController?.presentedViewController != null) {
                            topController = topController.presentedViewController
                        }
                        when (topController) {
                            is UINavigationController -> {
                                topController = topController.topViewController
                            }

                            is UITabBarController -> {
                                topController = topController.selectedViewController
                            }
                        }
                        return topController
                    }
                    """.trimIndent()
            )

            //https://github.com/dalafiarisamuel/Unsplash-KMP
            KotlinCode(
                code = $$"""
                    //Download Image
                    sealed class ImageDownloadState {
                        data object Idle : ImageDownloadState()
                        data object Loading : ImageDownloadState()
                        data object Success : ImageDownloadState()
                        data class Failure(val exception: Throwable) : ImageDownloadState()
                    }

                    expect class PlatformDownloadImage {
                        suspend fun downloadImage(imageLink: String): ImageDownloadState
                    }

                    //Permission
                    import com.mohamedrejeb.calf.permissions.ExperimentalPermissionsApi
                    import com.mohamedrejeb.calf.permissions.PermissionState
                    import com.mohamedrejeb.calf.permissions.PermissionStatus

                    @OptIn(ExperimentalPermissionsApi::class, ExperimentalPermissionsApi::class)
                    fun runWithPermission(permissionState: PermissionState, ifGranted: () -> Unit) {
                        when (val status = permissionState.status) {
                            is PermissionStatus.Denied -> {
                                if (status.shouldShowRationale) {
                                    permissionState.launchPermissionRequest()
                                } else {
                                    permissionState.openAppSettings()
                                }
                            }

                            is PermissionStatus.Granted -> {
                                ifGranted()
                            }
                        }
                    }

                    //UI
                    val writeStorage = rememberPermissionState(Permission.WriteStorage)
                    val gallery = rememberPermissionState(Permission.Gallery)
                    val currentPlatform = remember { getPlatform() }

                     when (currentPlatform) {
                        Platform.Android -> {
                            runWithPermission(writeStorage) {
                                if (it != null) {

                                }
                            }
                        }

                        Platform.Apple -> {
                            runWithPermission(gallery) {
                                if (it != null) {

                                }
                            }
                        }

                        Platform.Desktop -> {
                            if (it != null) {

                            }
                        }
                    }

                    //viewmodel
                    fun startDownload(photoUrl: String) {
                        viewModelScope.launch {
                            imageDownloadState = ImageDownloadState.Loading
                            imageDownloadState = platformDownloadImage.downloadImage(photoUrl)
                        }
                    }

                    //iosMain
                    <key>NSPhotoLibraryUsageDescription</key>
                    <string>We need access to your photo library to save images.</string>

                    import kotlinx.cinterop.ExperimentalForeignApi
                    import kotlinx.coroutines.Dispatchers
                    import kotlinx.coroutines.IO
                    import kotlinx.coroutines.suspendCancellableCoroutine
                    import kotlinx.coroutines.withContext
                    import okio.IOException
                    import platform.Foundation.NSData
                    import platform.Foundation.NSError
                    import platform.Foundation.NSURL
                    import platform.Foundation.NSURLErrorDomain
                    import platform.Foundation.NSURLResponse
                    import platform.Foundation.NSURLSession
                    import platform.Foundation.NSURLSessionConfiguration
                    import platform.Foundation.dataTaskWithURL
                    import platform.UIKit.UIImage
                    import platform.UIKit.UIImageWriteToSavedPhotosAlbum
                    import ui.state.ImageDownloadState
                    import kotlin.coroutines.resume
                    import kotlin.coroutines.resumeWithException

                    @OptIn(ExperimentalForeignApi::class)
                    actual class PlatformDownloadImage {

                        actual suspend fun downloadImage(imageLink: String): ImageDownloadState {
                            return withContext(Dispatchers.IO) {
                                try {
                                    val nsUrl = NSURL(string = imageLink)

                                    val (data, _) = fetchData(nsUrl)

                                    if (data == null) {
                                        println("No data received")
                                        return@withContext ImageDownloadState.Failure(Exception("No data was received"))
                                    }

                                    val image = UIImage(data = data)

                                    saveImageToPhotos(image)
                                    println("Image saved successfully")
                                    ImageDownloadState.Success
                                } catch (e: Exception) {
                                    ImageDownloadState.Failure(e)
                                }
                            }
                        }

                        private suspend fun fetchData(url: NSURL): Pair<NSData?, NSURLResponse?> =
                            suspendCancellableCoroutine { continuation ->
                                val configuration = NSURLSessionConfiguration.defaultSessionConfiguration
                                configuration.timeoutIntervalForRequest = 30.0
                                val session = NSURLSession.sharedSession
                                val task = session.dataTaskWithURL(url) { data, response, error ->
                                    if (error != null) {
                                        continuation.resumeWithException(error.toKotlinException())
                                    } else {
                                        continuation.resume(Pair(data, response))
                                    }
                                }
                                task.resume()
                            }

                        private suspend fun saveImageToPhotos(image: UIImage) =
                            suspendCancellableCoroutine<Unit> { continuation ->
                                UIImageWriteToSavedPhotosAlbum(image, null, null, null)
                                continuation.resume(Unit)
                            }

                        private fun NSError.toKotlinException(): Exception {
                            return when (domain) {
                                NSURLErrorDomain -> IOException(localizedDescription)
                                else -> Exception(localizedDescription)
                            }
                        }
                    }

                    //desktopMain
                    import io.ktor.client.*
                    import io.ktor.client.plugins.*
                    import io.ktor.client.request.*
                    import io.ktor.client.statement.*
                    import io.ktor.http.*
                    import io.ktor.util.*
                    import kotlinx.coroutines.Dispatchers
                    import kotlinx.coroutines.withContext
                    import java.io.File
                    import java.lang.Exception
                    import java.nio.file.Paths
                    import ui.state.ImageDownloadState

                    actual class PlatformDownloadImage(private val client: HttpClient) {
                        actual suspend fun downloadImage(imageLink: String): ImageDownloadState {
                            return try {
                                val fileName = "${Url(imageLink).pathSegments[1]}.jpeg"

                                val response = client.get(imageLink) {
                                    onDownload { bytesSentTotal, contentLength ->
                                    }
                                }
                                val body = response.bodyAsChannel()
                                val result = body.toByteArray()
                                saveImageToFile(result, fileName)
                                ImageDownloadState.Success
                            } catch (e: Exception) {
                                ImageDownloadState.Failure(e)
                            }
                        }

                        private suspend fun saveImageToFile(imageBytes: ByteArray, fileName: String) {
                            withContext(Dispatchers.IO) {
                                val userHome = System.getProperty("user.home")
                                val desktopPath = Paths.get(userHome, "Downloads", fileName).toString()
                                val file = File(desktopPath)
                                file.writeBytes(imageBytes)
                            }
                        }
                    }

                    //androidMain
                    <uses-permission android:name="android.permission.INTERNET"/>
                    <uses-permission android:name="android.permission.WRITE_EXTERNAL_STORAGE" tools:ignore="ScopedStorage"/>

                    import android.app.DownloadManager
                    import android.content.Context
                    import android.net.Uri
                    import android.os.Environment
                    import kotlinx.coroutines.Dispatchers
                    import kotlinx.coroutines.delay
                    import kotlinx.coroutines.withContext
                    import ui.state.ImageDownloadState
                    import kotlin.time.Duration.Companion.seconds

                    actual class PlatformDownloadImage(private val context: Context) {
                        actual suspend fun downloadImage(imageLink: String): ImageDownloadState {
                            withContext(Dispatchers.Default) {
                                delay(3.seconds)
                            }
                            return withContext(Dispatchers.Main) {
                                try {
                                    val uri = Uri.parse(imageLink)
                                    val fileName = "${uri.pathSegments[0]}.jpeg"

                                    val request = DownloadManager.Request(uri)
                                        .setNotificationVisibility(DownloadManager.Request.VISIBILITY_VISIBLE_NOTIFY_COMPLETED)
                                        .setDestinationInExternalPublicDir(Environment.DIRECTORY_DOWNLOADS, fileName)

                                    (context.getSystemService(Context.DOWNLOAD_SERVICE)
                                            as DownloadManager).enqueue(request)
                                    ImageDownloadState.Success
                                } catch (e: Exception) {
                                    ImageDownloadState.Failure(e)
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //https://github.com/D10NGYANG/DLBluetoothUtil

                    //ios bluetooth
                    private val centralManager by lazy { CBCentralManager(delegate = centralDelegate, queue = null) }

                    private val centralDelegate = object : NSObject(), CBCentralManagerDelegateProtocol {
                    }

                    //android
                    private val ble by lazy { BluetoothLe(ctx) }
                    private val bluetoothManager by lazy { ctx.getSystemService(Context.BLUETOOTH_SERVICE) as BluetoothManager? }
                    private val bluetoothAdapter by lazy { bluetoothManager?.adapter }
                    private val scope by lazy { CoroutineScope(Dispatchers.IO) }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import androidx.compose.runtime.Composable
                    import androidx.compose.ui.Modifier

                    @Composable
                    expect fun BlurHashView(blurhash: String, width: Int, height: Int, modifier: Modifier)

                    //iosMain
                    import platform.UIKit.UIImage
                    import platform.UIKit.UIViewController

                    object ComposeGlobals {
                        var controllerFactory: ((UIImage) -> UIViewController)? = null
                    }

                    import androidx.compose.foundation.layout.fillMaxSize
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.LaunchedEffect
                    import androidx.compose.runtime.getValue
                    import androidx.compose.runtime.mutableStateOf
                    import androidx.compose.runtime.remember
                    import androidx.compose.runtime.setValue
                    import androidx.compose.ui.Modifier
                    import androidx.compose.ui.interop.UIKitViewController
                    import com.vanniktech.blurhash.BlurHash
                    import platform.UIKit.UIImage

                    @Composable
                    actual fun BlurHashView(blurhash: String, width: Int, height: Int, modifier: Modifier) {

                        var uiImage: UIImage? by remember { mutableStateOf(null) }

                        LaunchedEffect(blurhash) {

                            val decoded: UIImage? = BlurHash.decode(
                                blurHash = blurhash,
                                width = (width / IMAGE_SIDE_DIVIDER).toDouble(),
                                height = (height / IMAGE_SIDE_DIVIDER).toDouble(),
                                punch = 1.0f,
                                useCache = true,
                            )

                            uiImage = decoded
                        }

                        uiImage?.let {
                            UIKitViewController(
                                factory = { ComposeGlobals.controllerFactory!!(it) },
                                modifier = modifier.fillMaxSize(),
                            )
                        }
                    }

                    private const val IMAGE_SIDE_DIVIDER = 4

                    //iosApp
                    import SwiftUI
                    import ComposeApp

                    @main
                    struct iOSApp: App {
                        init() {
                            ViewControllerFactoryCompanion().shared = TheViewControllerFactory()
                        }

                    	var body: some Scene {
                    		WindowGroup {
                    			ContentView()
                    		}
                    	}
                    }

                    import Foundation
                    import UIKit
                    import SwiftUI
                    import ComposeApp

                    class TheViewControllerFactory : ViewControllerFactory {
                        func makeController(view: UIImage) -> UIViewController {
                            let image = Image(uiImage: view)
                            return UIHostingController(rootView: image)
                        }
                    }

                    import UIKit
                    import SwiftUI
                    import ComposeApp

                    struct ComposeView: UIViewControllerRepresentable {
                        func makeUIViewController(context: Context) -> UIViewController {
                            MainViewControllerKt.MainViewController()
                        }

                        func updateUIViewController(_ uiViewController: UIViewController, context: Context) {}
                    }

                    struct ContentView: View {
                        var body: some View {
                            ComposeView()
                                    .ignoresSafeArea(.keyboard) // Compose has own keyboard handler
                        }
                    }

                    //androidMain
                    import android.graphics.Bitmap
                    import androidx.compose.foundation.Image
                    import androidx.compose.foundation.layout.fillMaxSize
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.LaunchedEffect
                    import androidx.compose.runtime.getValue
                    import androidx.compose.runtime.mutableStateOf
                    import androidx.compose.runtime.remember
                    import androidx.compose.runtime.setValue
                    import androidx.compose.ui.Modifier
                    import androidx.compose.ui.graphics.ImageBitmap
                    import androidx.compose.ui.graphics.asImageBitmap
                    import androidx.compose.ui.layout.ContentScale

                    @Composable
                    actual fun BlurHashView(blurhash: String, width: Int, height: Int, modifier: Modifier) {

                        var bitmap: ImageBitmap? by remember { mutableStateOf(null) }

                        LaunchedEffect(blurhash) {
                            val decoded: Bitmap? = BlurHash.decode(
                                blurHash = blurhash,
                                width = width / IMAGE_SIDE_DIVIDER,
                                height = height / IMAGE_SIDE_DIVIDER,
                            )

                            bitmap = decoded?.asImageBitmap()
                        }

                        bitmap?.let {
                            Image(
                                bitmap = it,
                                contentDescription = null,
                                contentScale = ContentScale.Fit,
                                modifier = modifier.fillMaxSize(),
                            )
                        }
                    }

                    private const val IMAGE_SIDE_DIVIDER = 4
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Open App Rating

                    expect class PlatformContext
                    expect val appStoreId: String
                    expect fun openAppRatingPage(platformContext: PlatformContext)
                    expect fun PlatformContext.openUrl(url: String)

                    //di
                    actual val targetModule = module {
                        single { PlatformContext(androidContext()) }
                    }

                    //androidMain
                    single { PlatformContext(androidContext()) }

                    import android.content.Context
                    import android.content.Intent
                    import android.net.Uri

                    actual class PlatformContext(val context: Context)

                    actual val appStoreId: String
                        get() = "YOUR_APP_ID"

                    actual fun openAppRatingPage(platformContext: PlatformContext) {
                        val context = platformContext.context // Access the Android Context

                        val uri = Uri.parse("market://details?id=packageName")
                        val goToMarket = Intent(Intent.ACTION_VIEW, uri)
                        goToMarket.addFlags(Intent.FLAG_ACTIVITY_NEW_TASK or Intent.FLAG_ACTIVITY_NO_HISTORY or Intent.FLAG_ACTIVITY_NEW_DOCUMENT or Intent.FLAG_ACTIVITY_MULTIPLE_TASK)
                        try {
                            context.startActivity(goToMarket)
                        } catch (e: Exception) {
                            context.startActivity(Intent(Intent.ACTION_VIEW, Uri.parse("https://play.google.com/store/apps/details?id=packageName")).apply {
                                addFlags(Intent.FLAG_ACTIVITY_NEW_TASK)
                            })
                        }
                    }

                    actual fun PlatformContext.openUrl(url: String) {
                        val intent = Intent(Intent.ACTION_VIEW, Uri.parse(url))
                        context.startActivity(intent.apply {
                            addFlags(Intent.FLAG_ACTIVITY_NEW_TASK)
                        })
                    }


                    //iosMain
                    import platform.UIKit.UIApplication
                    import platform.Foundation.NSURL

                    actual fun openAppRatingPage(platformContext: PlatformContext) {
                        val appStoreUrl = "itms-apps://itunes.apple.com/app/idYOUR_APP_ID?action=write-review"
                        val nsUrl = NSURL.URLWithString(appStoreUrl)

                        if (nsUrl != null && UIApplication.sharedApplication.canOpenURL(nsUrl)) {
                            UIApplication.sharedApplication.openURL(nsUrl)
                        }
                    }

                    actual val appStoreId: String
                        get() = "YOUR_APP_ID"


                    actual class PlatformContext

                    actual fun PlatformContext.openUrl(url: String) {
                        val nsUrl = NSURL.URLWithString(url)
                        if (nsUrl != null && UIApplication.sharedApplication.canOpenURL(nsUrl)) {
                            UIApplication.sharedApplication.openURL(nsUrl)
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //Chat UI with Platform UI TextField, only iOS
                    https://github.com/kinto-technologies/kmp-swiftui.git

                    fun ComposeEntryPoint(): UIViewController =
                        ComposeUIViewController {
                            Column(
                                Modifier
                                    .fillMaxSize()
                                    .windowInsetsPadding(WindowInsets.systemBars),
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                ChatApp(displayTextField = false)
                            }
                        }

                    fun sendMessage(text: String) {
                        viewModel.send(Message(user = myUser, timeMs = timestampMs(), text = text))
                    }

                    actual fun timestampMs(): Long {
                        return (NSDate().timeIntervalSince1970() * 1000).toLong()
                    }

                    import UIKit
                    import SwiftUI
                    import ComposeApp

                    struct ComposeViewControllerRepresentable : UIViewControllerRepresentable {

                        func updateUIViewController(_ uiViewController: UIViewControllerType, context: Context) {}

                        func makeUIViewController(context: Context) -> some UIViewController {
                            return MainViewControllerKt.ComposeEntryPoint()
                        }
                    }

                    import UIKit
                    import SwiftUI
                    import ComposeApp

                    struct ContentView: View {

                        @State private var inputText = ""

                        var body: some View {
                            ZStack {
                                Color("TopGradient")
                                    .ignoresSafeArea()
                                ComposeViewControllerRepresentable()
                                TextInputView(inputText: $\inputText)
                            }
                            .onTapGesture {
                                // Hide keyboard on tap outside of TextField
                                UIApplication.shared.sendAction(#selector(UIResponder.resignFirstResponder), to: nil, from: nil, for: nil)
                            }
                        }
                    }

                    import SwiftUI
                    import ComposeApp

                    public func sendMessage(_ text: String) {
                        MainViewControllerKt.sendMessage(text: text)
                    }

                    struct TextInputView: View {

                        @Binding var inputText: String
                        @FocusState private var isFocused: Bool

                        var body: some View {
                            VStack {
                                Spacer()
                                HStack {
                                    TextField("Type message...", text: $\inputText, axis: .vertical)
                                        .focused() //isFocused
                                        .lineLimit(3)
                                    if (!inputText.isEmpty) {
                                        Button {
                                            sendMessage(inputText)
                                            isFocused = false
                                            inputText = ""
                                        } label: {
                                            Image(systemName: "arrow.up.circle.fill")
                                                .tint(Color(red: 0.671, green: 0.365, blue: 0.792))
                                        }
                                    }
                                }
                                .padding(15)
                                .background(RoundedRectangle(cornerRadius: 200).fill(.white).opacity(0.95))
                                .padding(15)
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                //Open google map

                //iosMain
                import androidx.compose.runtime.Composable
                import platform.Foundation.NSURL
                import platform.UIKit.UIApplication

                @Composable
                actual fun OpenGoogleMaps() {
                    val mapsUrl = "http://maps.google.com/?q=-23.46511,-47.47381&mode=d"
                    val nsUrl = mapsUrl.let { NSURL.URLWithString(it) } ?: return
                    UIApplication.sharedApplication.openURL(nsUrl)
                }

                //androidMain

                import android.content.Intent
                import android.net.Uri
                import androidx.compose.runtime.Composable
                import androidx.compose.ui.platform.LocalContext

                @Composable
                actual fun OpenGoogleMaps() {
                    val mapsUri = Uri.parse("google.navigation:q=-23.46511,-47.47381&mode=d&mode=d")
                    LocalContext.current.startActivity(
                        Intent(
                            Intent.ACTION_VIEW,
                            mapsUri
                        ).setPackage("com.google.android.apps.maps")
                    )
                }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    //PosPrinter, bluetooth, excel, barcode
                    //https://github.com/youvita/PosComposeMultiplatform

                    class AndroidPlatform : Platform {
                        override val name: String = "Android 1"
                        override var barcode: ImageBitmap = ImageBitmap(100, 50)

                        @Composable
                        override fun Capture(key: Int, composable: @Composable () -> Unit) {
                            CaptureImage(key, composable)
                        }
                    }

                    var imageList = mutableListOf<Capture>()

                    data class Capture(
                        val key: Int,
                        val bitmap: Bitmap
                    )

                    @Composable
                    fun CaptureImage(
                        key: Int, content: @Composable () -> Unit
                    ) {
                        Box(modifier = Modifier.fillMaxWidth()) {
                            AndroidView(factory = {
                                ComposeView(it).apply {
                                    setContent {
                                        Row(
                                            modifier = Modifier.size(width = 380.dp, height = Dp.Infinity),
                                            verticalAlignment = Alignment.CenterVertically
                                        ) {
                                            content()
                                        }
                                    }
                                }
                            }, modifier = Modifier.fillMaxWidth(), update = {
                                it.run {
                                    doOnLayout { view ->
                                        val bitmap = Bitmap.createBitmap(
                                            view.measuredWidth,
                                            view.measuredHeight,
                                            Bitmap.Config.ARGB_8888
                                        ).applyCanvas {
                                            it.draw(this)
                                        }

                                        imageList.add(Capture(key, bitmap))
                                    }
                                }
                            })
                        }
                    }

                    fun clearAll() {
                        imageList.clear()
                    }

                    fun printOut() {
                        val printer = EscPosPrinter(BluetoothPrintersConnections.selectFirstPaired(),
                            203,
                            48f,
                            32, EscPosCharsetEncoding("UTF-8", 24))

                        var receipt = ""
                        for(image in imageList.sortedBy { it.key }) {
                            receipt = receipt.plus("[C]<img>" + PrinterTextParserImg.bitmapToHexadecimalString(
                                printer, image.bitmap
                            ) + "</img>\n")
                        }

                        printer.printFormattedText(receipt)
                    }
                """.trimIndent()
            )

            //https://github.com/yusufaltunoymak/MovieAppComposeMultiplatform.git
            KotlinCode(
                code = """
                    //PullToRefresh

                    @Composable
                    expect fun PlatformPullToRefresh(
                        isRefreshing: Boolean,
                        onRefresh: () -> Unit,
                        modifier: Modifier = Modifier,
                        content: @Composable () -> Unit
                    )

                    //androidMain
                    import androidx.compose.material3.ExperimentalMaterial3Api
                    import androidx.compose.material3.pulltorefresh.PullToRefreshBox
                    import androidx.compose.runtime.Composable
                    import androidx.compose.ui.Modifier

                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    actual fun PlatformPullToRefresh(
                        isRefreshing: Boolean,
                        onRefresh: () -> Unit,
                        modifier: Modifier,
                        content: @Composable () -> Unit
                    ) {
                        PullToRefreshBox(
                            isRefreshing = isRefreshing,
                            onRefresh = onRefresh,
                            modifier = modifier
                        ) {
                            content()
                        }
                    }

                    //iosMain
                    import androidx.compose.foundation.layout.Box
                    import androidx.compose.foundation.layout.fillMaxSize
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.DisposableEffect
                    import androidx.compose.runtime.LaunchedEffect
                    import androidx.compose.runtime.getValue
                    import androidx.compose.runtime.remember
                    import androidx.compose.runtime.rememberUpdatedState
                    import androidx.compose.ui.Modifier
                    import androidx.compose.ui.interop.UIKitView
                    import kotlinx.cinterop.BetaInteropApi
                    import kotlinx.cinterop.ExperimentalForeignApi
                    import kotlinx.cinterop.ObjCAction
                    import kotlinx.cinterop.readValue
                    import platform.CoreGraphics.CGRectZero
                    import platform.UIKit.UIControlEventValueChanged
                    import platform.UIKit.UIRefreshControl
                    import platform.darwin.NSObject
                    import platform.darwin.NSObjectMeta
                    import platform.objc.sel_registerName
                    import platform.UIKit.UIScrollView
                    private class RefreshControlTarget(private val onRefresh: () -> Unit) : NSObject() {
                        @OptIn(BetaInteropApi::class)
                        @ObjCAction
                        fun valueChanged() {
                            onRefresh()
                        }

                        companion object : NSObjectMeta()
                    }

                    @OptIn(ExperimentalForeignApi::class)
                    @Composable
                    actual fun PlatformPullToRefresh(
                        isRefreshing: Boolean,
                        onRefresh: () -> Unit,
                        modifier: Modifier,
                        content: @Composable () -> Unit
                    ) {
                        val latestOnRefresh by rememberUpdatedState(onRefresh)

                        val refreshControl = remember { UIRefreshControl() }

                        val scrollView = remember {
                            UIScrollView(frame = CGRectZero.readValue()).apply {
                                setRefreshControl(refreshControl)
                            }
                        }

                        DisposableEffect(refreshControl) {
                            val target = RefreshControlTarget { latestOnRefresh() }
                            refreshControl.addTarget(
                                target = target,
                                action = sel_registerName("valueChanged"),
                                forControlEvents = UIControlEventValueChanged
                            )
                            onDispose {
                                refreshControl.removeTarget(
                                    target = target,
                                    action = sel_registerName("valueChanged"),
                                    forControlEvents = UIControlEventValueChanged
                                )
                            }
                        }

                        LaunchedEffect(isRefreshing) {
                            if (isRefreshing) {
                                if (!refreshControl.isRefreshing()) {
                                    refreshControl.beginRefreshing()
                                }
                            } else {
                                if (refreshControl.isRefreshing()) {
                                    refreshControl.endRefreshing()
                                }
                            }
                        }

                        Box(modifier = modifier) {
                            UIKitView(
                                factory = { scrollView },
                                modifier = Modifier.fillMaxSize(),
                                update = {

                                }
                            )
                            content()
                        }
                    }
                    """.trimIndent()
            )
        }
    }
}

/*
Swift
func __getCurrentLocation() async throws -> Any? {
    return nil
}
 */

//https://github.com/diegoamribeiro/Compose_Multiplatform_MapNative_Track
//https://medium.com/@adman.shadman/sharing-application-context-in-kotlin-multiplatform-with-expect-actual-mechanism-88feb9649c14
//https://abhidharmik.medium.com/
//https://github.com/TaalayDev/DoodleVerse-ComposeMultiplatform (paint canvas - Mobile - Desktop - Web)
//https://github.com/euri16/cmp-google-loginƒ
//https://github.com/the-best-is-best/compose-google-sigin-in
//https://github.com/the-best-is-best/compose_sign_in_with_facebook.git
//https://github.com/ismai117/KScan
//https://github.com/TM-Apps/konnection.git
//https://github.com/D10NGYANG/DLBluetoothUtil
//https://github.com/youvita/PosComposeMultiplatform
//https://github.com/k1rak1ra/KotlinMultiplatformPaypal
//https://github.com/k1rak1ra/KotlinMultiplatformStripe
//https://github.com/Sherlock-Blue/CMPBLE
//https://github.com/Tweener/passage (Apple and Google Login)
//https://github.com/asterixorobelix/afrikaburn_app.git (From Asset Json and CSV Reader)
//https://github.com/Chaintech-Network/CMP-Bluetooth-Manager
//https://github.com/Chaintech-Network/QRKitComposeMultiplatform
//https://medium.com/@houssembababendermel/implementing-google-authentication-in-kotlin-multiplatform-using-cocoapods-2f7a2f568c9a (Google Login)
//https://github.com/bymaskeli53/ShopListKMP (Text-to-Speech)
//https://github.com/Ali0092/Taskaroo_Mulitplatform.git (DateTimePicker Platform Specific)
//https://github.com/brewkits/KRelay
//https://github.com/kttipay/KPayment (google and apple login)
//https://github.com/narendraanjana09/CMPRazorpayDemo.git
//https://medium.com/@fitcoder1/integrating-paynimo-payment-gateway-in-kotlin-multiplatform-kmp-android-ios-c7d9ae9323c0
//https://github.com/Xemb0/kmp-location (Flow using location)
//https://github.com/junhyeongleeee/HeistHunt.git (Location, QRScanner, GoogleMap, Voice Channel)

//https://github.com/rbbozkurt/kmp-swiftpm-interop
//https://github.com/apolacsek/kmp-compose-mapkit
//https://github.com/sunildhiman90/KotlinMultiplatformAuth

//https://www.youtube.com/watch?v=O0BqoLcRuJI (Video)

//Bluetooth
//https://github.com/D10NGYANG/DLBluetoothUtil
//https://github.com/Reedyuk/blue-falcon

//SKIE
//https://medium.com/@spparks_/kmp-implementing-the-expected-actual-mechanism-57d51d76a2c9
//https://medium.com/@santimattius/kmp-design-for-android-ios-part-3-contracts-errors-and-generics-that-feel-native-in-swift-with-08c730eff4a0

//https://medium.com/@santimattius/kmp-design-for-android-ios-part-2-concurrency-structure-ffa7249a1f2e

//https://www.swift.org/install/macos/

//SwiftUI
//https://www.linkedin.com/in/ruchit-shah-81097826/recent-activity/all/